import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlarmClock, CalendarHeart, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp,
  ClipboardList, Copy, CreditCard, ExternalLink, HeartHandshake, Link2, PenLine, Phone,
  PhoneCall, SlidersHorizontal, StickyNote, Trash2, X, Image as ImageIcon, CheckCircle2, Circle,
  ArrowLeft, MoreHorizontal, Plus, KeyRound, AlertTriangle, Heart, Clock, Eye,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  profilesAPI, coachDailyContactsAPI, matchesAPI, datingScheduleAPI, meetingsAPI, coachKpiAPI, kpiV2API, serviceSopAPI,
  serviceReportsAPI, workspaceAPI, supervisorAPI,
} from '../../services/api';
import { parseCalDate } from '../../utils/calendarDate';
import { laInputToUTCISO, utcISOToLAInput, laShiftInput, laWeekStart, laTodayYMD } from '../../utils/laTime';
import { resolvePhotoUrl } from '../../utils/photoUtils';
import { useAuth } from '../../context/AuthContext';
import { translateOptionLabel } from '../../utils/prefValueI18n';
import { STAGES_DEF } from './ClientPipelineBoard';
import MatchmakingCard from '../MatchmakingCard';
import QuestionnaireResultsModal from '../kpi/QuestionnaireResultsModal';
import ServiceStatusLine from './ServiceStatusLine';
import CrmNotesPanel from './CrmNotesPanel';
import ComplaintsModal from './ComplaintsModal';
import ServiceReportEditor from '../service/ServiceReportEditor';
import GenerateLoginModal from './GenerateLoginModal';
import { Section } from '../staff/StaffUI';
import {
  PURPOSES, METHODS, OUTCOMES, MEETING_PURPOSES, kindOfMethod, needsScreenshot, screenshotHint, needsNextContact,
  purposeLabel, methodLabel, outcomeLabel, fmtMD, hoursSince,
  isSupervisorUser, asArray, REPORT_STATUS, reportStatusKey, monthNo,
} from '../service/sopShared';
import {
  S as WS, TabIntro, LIGHT, card as wsCard, btnPrimary as wsBtnPrimary, btnGhost as wsBtnGhost, btnLink as wsBtnLink,
  quickLabel, is404, fallbackNextActions, fallbackLight, QUICK_FIELDS,
} from './workspace/wsShared';
import { bottomLineHits, ageFromDob } from './workspace/bottomLine';
import RecommendReasonEditor, { draftRecommendReason } from './workspace/RecommendReason';
import NextStepTab from './workspace/NextStepTab';
import MemoList from './workspace/MemoList';
import StepsTab from './workspace/StepsTab';
import QuickCardPanel from './workspace/QuickCardPanel';
import RichProfileDetailModal from './RichProfileDetailModal';
import ReviewTab from './workspace/ReviewTab';
import { FavoritesPanel, FavoritesNudge, favName, favWhen } from './workspace/FavoritesSection';
import { useTour } from '../tour/tourCore';
import { QrUploadButton, appendCapped } from './workspace/QrUpload';
import { TourEntryButton } from '../tour/TourWidgets';
import UrgentFlagButton, { UrgentFlagTag } from './UrgentFlagButton';
import RecStatusTag from './RecStatusTag';
import RecPager, { pageSlice } from './workspace/RecPager';
import { TAB_EXPLAIN } from '../tour/tourSteps';
import { DEMO_CONTACT_TEXT, DEMO_MEMO_TEXT, DEMO_REPORT_ID, DEMO_SHOT } from '../tour/tourFixtures';

// 客户详情 (CRM) — the design mockup's page 3: basic info on the left, the
// client's merged timeline on the right, and the five coach actions along the
// bottom. This is what a row on 客户管道看板 and a 今日待办 item open.
//
// Deliberately NOT RichProfileDetailModal: that one is the 档案详情 (the client's
// profile record — photos, custom fields, contract). Opening it from the board
// answered "what is this person like" when the coach asked "what has happened
// with this client and what do I do next".
//
// All five actions complete INSIDE this modal — 推荐给他人 opens the candidate
// picker (design page 5: sticky 已选 bar, selection survives searching),
// 安排约会 books off the client's existing matches, 写约会反馈 writes back to a
// past date. Nothing navigates away: leaving the modal to a list page loses the
// client you came for, which is what made the old buttons useless.
//
// Props: profileId (required), seed (a board row already in hand — renders the
// header instantly while the fetches land), isZh, onClose, onChanged (fires
// after an action writes, so the caller can refresh its list).

// Luxe tokens (staff shell). Key names kept so every reference keeps working:
// `burgundy` is now the ink accent, `alt` the recessed panel.
const APP = { bg: WS.card, alt: WS.soft, line: WS.line, ink: WS.ink, muted: WS.sub, burgundy: WS.ink, gold: WS.gold, paper: WS.paper, serif: WS.serif };
// Placeholder tones behind an initial when a candidate has no photo — muted Luxe inks, ivory letter on top.
const AV_PALETTE = ['#3A3835', '#8A6D3B', '#5E5446', '#3F7D5A', '#6E685F', '#7A5C3E'];
const avColor = (n) => AV_PALETTE[(Math.abs(Number(n) || 0)) % AV_PALETTE.length];
// Initials avatar: recessed ivory square with a gold serif letter (the prototype's member portrait tile).
const av = (bg, size = 24) => ({ width: size, height: size, borderRadius: WS.radius, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: WS.serif, fontSize: Math.round(size * 0.5), fontWeight: 500, color: WS.gold, background: WS.soft, flex: '0 0 auto', overflow: 'hidden' });

// Photo when there is one, initials otherwise — same rule as the board rows.
// Date time: type it (19:30, 1930, 7:30pm, 下午3点半 …) or pick from a 15-min
// list (06:00–23:45). Replaces <input type="time">, which collapses to an
// untappable sliver in Safari / iOS when empty. Stored as HH:mm (24 h); shown
// as "7:30 PM" in English and "下午 7:30" in Chinese. Commits on blur / Enter;
// anything unparseable reverts to the last good value.
const TIME_SLOTS = Array.from({ length: (24 - 6) * 4 }, (_, i) => {
  const m = 6 * 60 + i * 15;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
});
const parseTime = (raw) => {
  const x = String(raw || '').trim().toLowerCase().replace(/\s+/g, '').replace(/[：.]/g, ':')
    .replace(/点半$/, ':30').replace(/点$/, ':00').replace(/点/, ':').replace(/分$/, '');
  if (!x) return '';
  const m = x.match(/^(上午|早上|凌晨|下午|晚上|中午)?(\d{1,2})(?::?(\d{2}))?(am|pm|a|p)?$/);
  if (!m) return null;
  let h = Number(m[2]);
  const min = Number(m[3] || 0);
  const pm = (m[4] && m[4][0] === 'p') || ['下午', '晚上'].includes(m[1]) || (m[1] === '中午' && h < 11);
  const am = (m[4] && m[4][0] === 'a') || ['上午', '早上', '凌晨'].includes(m[1]);
  if (pm && h < 12) h += 12;
  if (am && h === 12) h = 0;
  if (h > 23 || min > 59) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
};
const fmtTime12 = (hhmm, isZh) => {
  const [h, mm] = String(hhmm || '').split(':').map(Number);
  if (!(h >= 0) || !(mm >= 0)) return hhmm || '';
  const h12 = h % 12 || 12;
  const m2 = String(mm).padStart(2, '0');
  return isZh ? `${h < 12 ? '上午' : '下午'} ${h12}:${m2}` : `${h12}:${m2} ${h < 12 ? 'AM' : 'PM'}`;
};
function TimeSelect({ value, onChange, style, placeholder, isZh }) {
  const v = String(value || '').slice(0, 5);
  const shown = (x) => (x ? fmtTime12(x, isZh) : '');
  const [draft, setDraft] = useState(shown(v));
  const [open, setOpen] = useState(false);
  const listRef = React.useRef(null);
  useEffect(() => { setDraft(v ? fmtTime12(v, isZh) : ''); }, [v, isZh]);
  // scroll the list to the current (or nearest later) slot when it opens
  useEffect(() => {
    if (!open || !listRef.current) return;
    const target = TIME_SLOTS.find((x) => x >= (v || '19:00')) || TIME_SLOTS[TIME_SLOTS.length - 1];
    const el = listRef.current.querySelector(`[data-slot="${target}"]`);
    if (el) listRef.current.scrollTop = el.offsetTop - 4;
  }, [open, v]);
  const commit = () => {
    const t2 = parseTime(draft);
    if (t2 === null) setDraft(shown(v));
    else { setDraft(shown(t2)); if (t2 !== v) onChange(t2); }
  };
  return (
    <div style={{ position: 'relative', display: 'inline-flex', minWidth: 128, flex: '0 0 auto' }}>
      <input value={draft} placeholder={placeholder} inputMode="text" autoComplete="off"
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onBlur={() => { commit(); setTimeout(() => setOpen(false), 150); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); setOpen(false); }
          else if (e.key === 'Escape') setOpen(false);
        }}
        style={{ ...style, width: 128, minWidth: 128, paddingRight: 28, boxSizing: 'border-box' }} />
      <Clock size={14} color={APP.muted} style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
      {open && (
        <div ref={listRef} role="listbox"
          style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50, width: 128, maxHeight: 220, overflowY: 'auto', background: '#FFFDF9', border: `1px solid ${APP.line}`, borderRadius: 2, boxShadow: 'none', padding: 4 }}>
          {TIME_SLOTS.map((x) => (
            <div key={x} data-slot={x} role="option" aria-selected={x === v}
              onMouseDown={(e) => { e.preventDefault(); setDraft(shown(x)); onChange(x); setOpen(false); }}
              style={{ padding: '6px 10px', borderRadius: 2, fontSize: 13, cursor: 'pointer', background: x === v ? '#EFEAE2' : 'transparent', color: x === v ? APP.burgundy : APP.ink, fontWeight: x === v ? 500 : 400 }}>
              {shown(x)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Avatar({ photo, initials, color, size = 24 }) {
  const src = resolvePhotoUrl(photo);
  return (
    <div style={av(color, size)}>
      {src
        ? <img src={src} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        : initials}
    </div>
  );
}

const TL_META = {
  contact: { Icon: Phone,          color: '#8A6D3B', bg: '#EFEAE2' },
  match:   { Icon: HeartHandshake, color: '#111111', bg: '#EFEAE2' },
  date:    { Icon: CalendarHeart,  color: '#3A3835', bg: '#EFEAE2' },
  note:    { Icon: StickyNote,     color: '#3F7D5A', bg: '#EFEAE2' },
};

// Brand that prefixes the coach's own name in the invite, so the client sees
// 「美国FINDU中高端婚恋白老师 invites you…」 rather than a bare given name.
const INVITE_BRAND = '美国FINDU中高端婚恋';
// The title the client sees as the sender. Kept separate from the coach's own
// account name so every outgoing message signs the same way.
const INVITE_TITLE = '恋爱导师';
const INVITE_TZ = 'America/Los_Angeles';
const INVITE_MINUTES = 60;

// The VooV-Meeting-shaped invitation the coach pastes to the client. Times are
// printed in Pacific — head office time — with the offset resolved for that
// actual date, so PDT/PST is never wrong by an hour.
// What the client is being invited TO. The wording follows the type so the
// message says what will actually happen when they open the link.
const INVITE_KIND = {
  audio:         { label: 'Audio Meeting', article: 'an' },
  video:         { label: 'Video Meeting', article: 'a' },
  questionnaire: { label: 'Questionnaire Meeting', article: 'a' },
};

export function meetingInvite({ host, url, kind = 'audio', start = new Date(), minutes = INVITE_MINUTES }) {
  const end = new Date(start.getTime() + minutes * 60000);
  const parts = (d) => {
    const f = new Intl.DateTimeFormat('en-CA', {
      timeZone: INVITE_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d).reduce((o, x) => { o[x.type] = x.value; return o; }, {});
    return { date: `${f.year}/${f.month}/${f.day}`, time: `${f.hour === '24' ? '00' : f.hour}:${f.minute}` };
  };
  let offset = 'GMT-07:00';
  try {
    offset = new Intl.DateTimeFormat('en-US', { timeZone: INVITE_TZ, timeZoneName: 'longOffset' })
      .formatToParts(start).find(x => x.type === 'timeZoneName')?.value || offset;
  } catch (e) { /* older engines: keep the default */ }
  const a = parts(start); const b = parts(end);
  const k = INVITE_KIND[kind] || INVITE_KIND.audio;
  return [
    `${host} invites you to ${k.article} ${k.label}`,
    `Time: ${a.date} ${a.time}-${b.time} (${offset}) Pacific Time - Los Angeles`,
    '',
    `Click the link to join the meeting: ${url}`,
  ].join('\n');
}

// Clipboard with a fallback: the API is unavailable on plain http and inside
// some in-app browsers, where prompt() at least lets the coach copy by hand.
const copyText = (text, okMsg, promptMsg) => {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text)
      .then(() => toast.success(okMsg))
      .catch(() => window.prompt(promptMsg, text));
  } else {
    window.prompt(promptMsg, text);
  }
};

// coach_daily_contacts.kind — shown as a label on the 打卡 rows of 跟进记录.
// dating_schedules.status → label, for 约见记录. Mirrors the board's own map.
const DATE_STATUS_LABEL = {
  pending: { zh: '待约会', en: 'Pending', c: '#6E685F' }, confirmed: { zh: '已确认', en: 'Confirmed', c: '#3A3835' },
  accepted: { zh: '已接受', en: 'Accepted', c: '#3F7D5A' }, meeting: { zh: '见面了', en: 'Met', c: '#8A6D3B' },
  chatting: { zh: '聊天中', en: 'Chatting', c: '#3F7D5A' }, in_relationship: { zh: '交往中', en: 'Together', c: '#8A6D3B' },
  engaged: { zh: '已订婚', en: 'Engaged', c: '#8A6D3B' }, completed: { zh: '已完成', en: 'Done', c: '#6E685F' },
  cancelled: { zh: '已取消', en: 'Cancelled', c: '#6E685F' }, no_show: { zh: '未到场', en: 'No-show', c: '#9E2B25' },
  rejected: { zh: '已拒绝', en: 'Rejected', c: '#9E2B25' },
};

// The filter fields on the candidate picker, each backed by a global custom
// field so the dropdown lists the SAME options the profile form offers rather
// than whatever happens to be in the loaded page of candidates.
//   key  = the column matchingController returns on a candidate row
//   fid  = global_custom_fields.id
const CAND_FILTER_FIELDS = [
  { key: 'education',       fid: 107, zh: '学历', en: 'Education' },
  { key: 'marital_status',  fid: 80,  zh: '婚况', en: 'Marital' },
  { key: 'children_status', fid: 90,  zh: '子女', en: 'Children' },
  { key: 'citizenship',     fid: 149, zh: '国籍', en: 'Citizenship' },
  { key: 'religion',        fid: 83,  zh: '宗教', en: 'Religion' },
  { key: 'job_industry',    fid: 5,   zh: '职业', en: 'Job' },
  { key: 'monthly_income',  fid: 109, zh: '收入', en: 'Income' },
  { key: 'real_estate',     fid: 6,   zh: '房产', en: 'Property' },
];

// How many candidates to pull (the whole pool — /matches/potential caps at
// 2000) and how many to show per page in the picker grid (4 columns x 4 rows
// on a desktop panel).
const CAND_FETCH_LIMIT = 2000;
const CAND_PAGE_SIZE = 48;

// matches.status — the enum the 推荐记录 detail can move a recommendation through.
// 'succeeded' is shown as 已约见 (stored value unchanged); 'in_contact' 接触中 and 'in_relationship' 已交往 are new (2026-10).
const MATCH_STATUS = [
  ['pending', '待确认', 'Pending'], ['approved', '已确认', 'Approved'],
  ['in_contact', '接触中', 'In contact'],
  ['succeeded', '已约见', 'Met'], ['in_relationship', '已交往', 'Dating'], ['rejected', '已拒绝', 'Rejected'],
  ['failed', '未成功', 'Failed'], ['cancelled', '已取消', 'Cancelled'],
  ['completed', '已完成', 'Completed'],
];

// Readable label for any matches.status value (the list above plus older values the API still returns).
const MATCH_STATUS_EXTRA = {
  accepted: ['已接受', 'Accepted'], in_progress: ['进行中', 'In progress'], successful: ['已约见', 'Met'],
  declined: ['已拒绝', 'Declined'], unsuccessful: ['未成功', 'Not a match'],
};
const matchStatusLabel = (st, isZh) => {
  const hit = MATCH_STATUS.find((x) => x[0] === st);
  if (hit) return isZh ? hit[1] : hit[2];
  const ex = MATCH_STATUS_EXTRA[st];
  return ex ? ex[isZh ? 0 : 1] : st;
};

const CONTACT_KIND = {
  call:     { zh: '电话', en: 'Call' },
  message:  { zh: '消息', en: 'Message' },
  meet:     { zh: '见面', en: 'Met' },
  followup: { zh: '回访', en: 'Follow-up' },
  other:    { zh: '联系', en: 'Contact' },
};

// The small "美西时间 / PT" tag beside a time input: what a coach types there
// is US Pacific time, whichever zone the browser is in.
const ptHint = { fontSize: 11, fontWeight: 400, letterSpacing: '.18em', color: '#6E685F', alignSelf: 'center', whiteSpace: 'nowrap' };

const fmtWhen = (at, isZh) => {
  if (!at) return '';
  const d = new Date(at);
  if (isNaN(d.getTime())) return '';
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return isZh ? '今天' : 'Today';
  if (days === 1) return isZh ? '昨天' : 'Yesterday';
  if (days < 30) return isZh ? `${days} 天前` : `${days}d ago`;
  return d.toLocaleDateString();
};

// Monday-start week on the Pacific clock, matching the 本周联系 aggregate on
// the board (coach_daily_contacts >= the Pacific Monday) in any browser zone.
const startOfWeek = () => laWeekStart();

const ageOf = (dob) => {
  const d = parseCalDate(dob);
  if (!d) return null;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1;
  return a > 0 && a < 120 ? a : null;
};

// Candidate meta shown on the 推荐给他人 picker. GET /matches/potential/:id
// returns p.* plus the custom-field values below, so a coach can judge the
// match from this row instead of opening each profile.
//
// Custom-field values are stored in English ("Unmarried", "$15K - $30K",
// "Accounting Industry"), so every one goes through translateOptionLabel — the
// same EN→中文 dictionary RichProfileDetailModal falls back to.
const candidateMeta = (c, isZh) => {
  const t = (zh, en) => (isZh ? zh : en);
  const zh = (v) => {
    const s = String(v ?? '').trim();
    if (!s) return '';
    return isZh ? translateOptionLabel(s, 'zh') : s;
  };
  const g = String(c.gender || '').toLowerCase();
  const gender = g === 'male' ? t('男', 'M') : g === 'female' ? t('女', 'F') : '';
  const age = ageOf(c.date_of_birth || c.birth_date);
  const city = [c.location_city, c.location_state, c.location_country].filter(Boolean).map(zh).join(' ');
  const h = Number(c.height);
  // 身高 is stored as a raw custom value and carries junk rows (-2 / 0).
  const height = h > 100 && h < 250 ? `${h}cm` : '';

  const facts = [
    gender,
    age ? t(`${age}岁`, `${age}y`) : '',
    height,
    city,
  ].filter(Boolean);

  const detail = [
    [t('婚况', 'Marital'), zh(c.marital_status)],
    [t('子女', 'Children'), zh(c.children_status)],
    [t('学历', 'Education'), zh(c.education)],
    [t('宗教', 'Religion'), zh(c.religion)],
    [t('国籍', 'Citizenship'), zh(c.citizenship)],
    [t('职业', 'Job'), zh(c.job_industry || c.occupation)],
    [t('收入', 'Income'), zh(c.monthly_income)],
    [t('房产', 'Property'), zh(c.real_estate)],
  ].filter(([, v]) => v && v !== '-' && !/^prefer not/i.test(v));

  return { facts: facts.join(' · '), detail, bio: String(c.bio || '').trim() };
};

// A stored preference value can be:
//   '{"start":"50","end":"64"}'  ← range fields
//   'Owns Home, Able to Buy'     ← checkbox fields (gcv joins with ', ')
//   'Do Not Want Children'       ← select fields
// Rendering it means resolving each piece against the field's OWN option list;
// the shared dictionary only knows common words, which is why half the 择偶
// rows used to stay in English.
const UNIT_BY_FIELD = {
  mate_age: { zh: '岁', en: 'y' }, mate_heigh: { zh: 'cm', en: 'cm' }, mate_height: { zh: 'cm', en: 'cm' },
  // 接受体重 is stored as a plain range too; without the unit it read as a bare
  // 「50 – 70」 next to the age range and looked like a second age.
  mate_weigh: { zh: 'kg', en: 'kg' }, mate_weight: { zh: 'kg', en: 'kg' },
};

// Some rows in profile_global_custom_values literally hold the string
// "[object Object]" — written by sync/import paths that did String(value) on a
// range object. The original numbers are gone, so the only honest thing to show
// is nothing at all.
const JUNK_VALUES = new Set(['[object object]', 'null', 'undefined', '{}', '[]', '-']);

export function formatFieldValue(field, raw, isZh) {
  const t = (zh, en) => (isZh ? zh : en);
  let v = raw;
  if (v == null) return '';
  if (typeof v === 'string') {
    const str = v.trim();
    if (!str || JUNK_VALUES.has(str.toLowerCase())) return '';
    if (str.startsWith('{') || str.startsWith('[')) {
      try { v = JSON.parse(str); } catch (e) { v = str; }
    } else {
      v = str;
    }
  }

  // range → "50 – 64 岁" / "165cm 以上" / "64 岁以下"
  if (v && typeof v === 'object' && !Array.isArray(v)
      && ('start' in v || 'end' in v || 'min' in v || 'max' in v)) {
    const u = UNIT_BY_FIELD[field?.name] || { zh: '', en: '' };
    const unit = isZh ? u.zh : u.en;
    const a = String(v.start ?? v.min ?? '').trim();
    const b = String(v.end ?? v.max ?? '').trim();
    if (a && b) return `${a} – ${b}${unit ? ` ${unit}` : ''}`;
    if (a) return t(`${a}${unit} 以上`, `${a}${unit}+`);
    if (b) return t(`${b}${unit} 以下`, `up to ${b}${unit}`);
    return '';
  }

  // 人生合伙人画像 tiers: { ideal, acceptable, bottom_line }, each tier itself a stored value
  // (often a JSON string). One line when the tiers agree, else 「理想 … ；可接受 … ；底线 …」.
  if (v && typeof v === 'object' && !Array.isArray(v)
      && ('ideal' in v || 'acceptable' in v || 'bottom_line' in v)) {
    const tiers = [
      [t('理想', 'Ideal'), formatFieldValue(field, v.ideal, isZh)],
      [t('可接受', 'Acceptable'), formatFieldValue(field, v.acceptable, isZh)],
      [t('底线', 'Bottom line'), formatFieldValue(field, v.bottom_line, isZh)],
    ].filter(([, x]) => x);
    if (!tiers.length) return '';
    // same choices in another order count as the same; tiers that agree share one entry
    const keyOf = (x) => x.split('、').sort().join('、');
    const groups = [];
    tiers.forEach(([k, x]) => {
      const g = groups.find(gr => gr.key === keyOf(x));
      if (g) g.names.push(k); else groups.push({ key: keyOf(x), names: [k], text: x });
    });
    if (groups.length === 1) return groups[0].text;
    return groups.map(g => `${g.names.join(t('、', '/'))} ${g.text}`).join(t('；', '; '));
  }

  if (v && typeof v === 'object' && !Array.isArray(v)) {
    // unknown object shape — show its values rather than "[object Object]"
    const inner = Object.values(v).map(x => String(x ?? '').trim()).filter(Boolean);
    if (!inner.length) return '';
    v = inner;
  }
  const opts = (field && field.opts) || [];
  const isOpt = (x) => opts.some(o => String(o.en).toLowerCase() === x.toLowerCase() || String(o.zh) === x);
  // Array items can be nested JSON (old saves wrapped arrays in strings) or a comma-joined
  // list of options; a whole item that is an option wins (some labels contain commas).
  const flat = (x) => {
    if (x == null) return [];
    if (typeof x === 'object') return formatFieldValue(field, x, isZh).split('、');
    const s = String(x).trim();
    if (!s) return [];
    if (s.startsWith('{') || s.startsWith('[')) {
      try { return flat(JSON.parse(s)); } catch (e) { /* plain text */ }
    }
    if (isOpt(s)) return [s];
    return s.split(/[,、]/).map(y => y.trim());
  };
  const pieces = (Array.isArray(v) ? v.flatMap(flat) : flat(v))
    .filter(x => x && !JUNK_VALUES.has(x.toLowerCase()));

  const label = (piece) => {
    if (!piece) return '';
    const hit = opts.find(o =>
      String(o.en).toLowerCase() === piece.toLowerCase() || String(o.zh) === piece);
    if (hit) return String(isZh ? (hit.zh || hit.en) : (hit.en || hit.zh)).trim();
    // not one of the field's options (free text / legacy value) — fall back to
    // the shared dictionary, then to the value itself
    return isZh ? translateOptionLabel(piece, 'zh') : piece;
  };

  return [...new Set(pieces.map(label).filter(Boolean))].join('、');
}

const photoOf = (c) => resolvePhotoUrl(c && (c.profile_photo || c.photo));

const nameOfProfile = (r, prefix = '') => {
  const f = r[`${prefix}first_name`] || r.first_name || '';
  const l = r[`${prefix}last_name`] || r.last_name || '';
  return [f, l].filter(Boolean).join(' ').trim();
};

// initialAction (optional): 'contact' opens 打卡 straight away, 'notes' lands on
// 备注记录, 'meets' on 约见记录 — used by the 今日跟进 rows.
//
// 操作引导 (components/tour): `demo` renders the 示例会员 for the guided tour — TourProvider
// mounts it with the sample profile id while its demo data source answers every request from
// tourFixtures.js and refuses writes. `demoView` ({ tab, composer, report, stepFocus, rect, memo, n })
// is the screen the current step wants; a new `n` re-applies it. A real (non-demo) workspace stays
// mounted but hidden while a tour runs, and is back untouched when it ends.
export default function ClientCrmModal({ profileId, seed = null, isZh = true, onClose, onChanged, initialAction = null, demo = false, demoView = null }) {
  const { user } = useAuth();
  const tour = useTour();
  const hiddenByTour = !demo && tour.active;
  const hiddenRef = React.useRef(hiddenByTour);
  hiddenRef.current = hiddenByTour;
  const maybeInvite = tour.maybeInvite;
  // First real 客户工作台 of this user → invite to the guided tour (unless finished / dismissed).
  useEffect(() => { if (!demo) maybeInvite(); }, [demo, maybeInvite]);
  const meId = user?.id;
  const [profile, setProfile] = useState(seed);
  const [activity, setActivity] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  // 客户工作台 tabs: next | steps | records | profile | review (主管 only).
  const [tab, setTab] = useState('next');
  // Inline composer for the two actions that complete without leaving: the
  // check-in and the client note. `kind` is null when closed.
  const [composer, setComposer] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  // 下次联系 on the check-in composer. Defaults to +7 days — the weekly-contact
  // cadence the coaching KPI is built around — so the common case is one tap.
  const [nextAt, setNextAt] = useState('');
  // 推荐给他人 cards: a call link per candidate, and the 相亲卡 generator.
  const [candCall, setCandCall] = useState({});      // candidateId -> url
  // The questionnaire generated from 打卡.
  const [qList, setQList] = useState(null);          // null = not loaded yet
  const [qOpen, setQOpen] = useState(false);
  const [qBusy, setQBusy] = useState(false);
  const [qLink, setQLink] = useState(null);          // { title, url }
  const [candBusy, setCandBusy] = useState(null);    // candidateId being worked
  const [cardProfile, setCardProfile] = useState(null);
  // Candidate filter + sort, shared by 推荐给他人 and 安排约会's 全部异性 tab.
  const EMPTY_FILTERS = { ageMin: '', ageMax: '', country: '', state: '', city: '', ...Object.fromEntries(CAND_FILTER_FIELDS.map(f => [f.key, ''])) };
  // fid -> [{en, zh}] from /profiles/global-custom-fields, fetched once.
  const [fieldOptions, setFieldOptions] = useState({});
  // Every global custom field (id / name / label), for the 档案资料 + 择偶 panel.
  const [allFields, setAllFields] = useState([]);
  // 档案资料 starts collapsed — the timeline is what the coach opens this modal
  // for, and the field list pushed it below the fold.
  const [showProfilePanel, setShowProfilePanel] = useState(false);
  const [showPrefPanel, setShowPrefPanel] = useState(false);
  // A record opened for detail: { kind, data }. Every list and every Gantt bar
  // feeds this one panel, so "点开看详情" behaves the same everywhere.
  const [record, setRecord] = useState(null);
  const [recordStatus, setRecordStatus] = useState('');
  const [recordSaving, setRecordSaving] = useState(false);
  // 推荐确认链接 for the match being viewed: { url, count }.
  const [reviewLink, setReviewLink] = useState(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  // Profiles of the people on the other side of a 推荐 / 约见, so their detail
  // can show a real photo and the same facts a candidate card carries.
  const [peers, setPeers] = useState({});
  const [candFilters, setCandFilters] = useState(EMPTY_FILTERS);
  const [candSort, setCandSort] = useState('match');
  // 底线提醒: hide candidates who miss either side's 底线.
  const [hideBottom, setHideBottom] = useState(false);
  // 速记卡对照 — the candidate whose card is open next to this client's.
  const [qcCompare, setQcCompare] = useState(null);
  // This client's own 速记卡 fields, for 速记卡对照 and the 推荐理由 draft.
  const [ownQuick, setOwnQuick] = useState(null);
  // 会员档案详情 of a candidate, opened from the picker card (same modal as 查看 on /profiles).
  const [detailId, setDetailId] = useState(null);
  // 详情 quick view in the candidate grid: { c, mode }.
  const [quickView, setQuickView] = useState(null);
  // 推荐理由 being written in 推荐给他人, candidate id -> { text, confirmed }.
  // A pick without an entry shows (and saves) the system draft, unconfirmed.
  const [reasons, setReasons] = useState({});
  // 推荐理由 of the open 推荐 record: { text, confirmed, saved, draft, loading }.
  const [recReason, setRecReason] = useState(null);
  const [recReasonSaving, setRecReasonSaving] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // Candidate grid paging. The whole opposite-sex pool is fetched (hundreds of
  // rows), so it is paged rather than dumped into one endless scroll.
  const [candPage, setCandPage] = useState(1);
  // 测评报告 — 人生合伙人画像 / 性格测评 results this client has returned. They
  // used to be reachable only from the ticket that sent them.
  const [quizRows, setQuizRows] = useState([]);
  const [quizOpen, setQuizOpen] = useState(null);   // submission id being viewed
  // 付款记录 — the client's invoices + the invoice-installment aggregate, the
  // same data the coach KPI 客户付款 view renders. Coaches who don't own the
  // client get a 403 there, which lands here as an empty tab.
  const [payData, setPayData] = useState(null);     // { invoices, paymentSummary }
  // Candidate cards run 4-up on a normal desktop panel so a page shows more of
  // the pool; narrower viewports step down instead of squeezing 4 columns into
  // a phone. Tracked in state because the grid is styled inline.
  const [vw, setVw] = useState(() => (typeof window === 'undefined' ? 1440 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // 简约网格: small square portraits, so many to a row.
  const candCols = vw >= 1500 ? 8 : vw >= 1100 ? 6 : vw >= 820 ? 4 : vw >= 560 ? 3 : 2;
  // 安排约会: pick from the client's signed lines, or from the whole
  // opposite-sex pool.
  const [scheduleSource, setScheduleSource] = useState('lines');
  // Filters and sorting both work on the loaded pool — the fetch already pulls
  // every opposite-sex candidate, so neither changes what is requested.
  const filtersOn = !!(candFilters.ageMin || candFilters.ageMax
    || candFilters.country || candFilters.state || candFilters.city
    || CAND_FILTER_FIELDS.some(f => candFilters[f.key]));
  // Timeline row being edited: { type, id, text, next_at, sdate, stime, location }
  const [editing, setEditing] = useState(null);
  // Full-panel action views that replace the timeline area: 'recommend'
  // (candidate picker), 'schedule' (book a date), 'feedback' (write one up).
  const [view, setView] = useState(null);
  // 推荐给他人
  const [candidates, setCandidates] = useState([]);
  const [candLoading, setCandLoading] = useState(false);
  const [candSearch, setCandSearch] = useState('');
  // Map id → row, so a pick survives re-searching (the design's 「翻页保留」).
  const [picked, setPicked] = useState({});
  // 安排约会 / 写约会反馈
  const [matchRows, setMatchRows] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [form, setForm] = useState({ partnerId: '', date: '', time: '', location: '', remindAt: '' });
  const [fbTarget, setFbTarget] = useState(null);
  const [fbForm, setFbForm] = useState({ status: 'completed', text: '', rating: '' });
  // VIP 服务流程 5.0 — service status line, meeting checks and the 5.0 打卡
  // fields. All optional: a 404 from an older API leaves them null and the
  // modal looks the way it did before.
  const [svc, setSvc] = useState(null);
  const [meetChecks, setMeetChecks] = useState({});   // dating_schedule_id -> meeting-checks row
  const [reportOpen, setReportOpen] = useState(null);  // service report id
  const [complaintsOpen, setComplaintsOpen] = useState(false);
  const [notesFocus, setNotesFocus] = useState(0);
  const [cPurpose, setCPurpose] = useState('followup_48h');
  const [cMethod, setCMethod] = useState('phone');
  const [cOutcome, setCOutcome] = useState('');
  const [cImages, setCImages] = useState([]);
  const [cUploading, setCUploading] = useState(false);
  const [cSchedule, setCSchedule] = useState('');
  // 交叉电话 to the partner member records on THEIR profile.
  const [cTarget, setCTarget] = useState(null);       // { id, name } | null
  const cFileRef = React.useRef(null);

  const t = (zh, en) => (isZh ? zh : en);

  // ── 客户工作台 data (round 2) ─────────────────────────────────────────────
  // /profiles/:id/workspace drives the header light, 下一步 and 服务进度. On an
  // API without it (404) the tabs fall back to the phase-1 service-status.
  const [ws, setWs] = useState(null);
  const [wsMissing, setWsMissing] = useState(false);
  // Not this teacher's member (403 on /workspace): 服务进度 is still shown, read-only (/sop-progress).
  const [wsRo, setWsRo] = useState(null);
  const [wsRound, setWsRound] = useState(null);       // null = current round
  const [complaints, setComplaints] = useState([]);
  const [quickHist, setQuickHist] = useState([]);
  const [reports, setReports] = useState([]);
  const [recFilter, setRecFilter] = useState('all');
  const [recPage, setRecPage] = useState(1);
  useEffect(() => { setRecPage(1); }, [recFilter]);
  const [showGantt, setShowGantt] = useState(false);
  const [stepFocus, setStepFocus] = useState(null);
  const [memoFocus, setMemoFocus] = useState(0);
  const [nextBusy, setNextBusy] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const moreRef = React.useRef(null);
  const [presetRect, setPresetRect] = useState(null);   // { n, title } → 审核 opens 新建整改单
  const isSup = isSupervisorUser(user) || !!(ws && ws.is_supervisor);

  const reloadWs = useCallback(() => {
    const params = wsRound ? { round: wsRound } : undefined;
    workspaceAPI.get(profileId, params)
      .then(r => {
        const d = r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : null;
        setWs(d); setWsMissing(false); setWsRo(null);
        // workspace.status is the same object as /service-status
        if (d && d.status && typeof d.status === 'object') setSvc(prev => prev || d.status);
      })
      .catch(e => {
        if (is404(e)) { setWs(null); setWsMissing(true); return; }
        if (e?.response?.status === 403) {
          workspaceAPI.progress(profileId, params)
            .then(r => setWsRo(r.data && Array.isArray(r.data.steps) ? r.data : null))
            .catch(() => setWsRo(null));
        }
      });
  }, [profileId, wsRound]);
  useEffect(() => { reloadWs(); }, [reloadWs]);

  const loadQuickHist = useCallback(() => {
    workspaceAPI.getQuickCard(profileId)
      .then(r => { setQuickHist(asArray(r.data && r.data.history)); setOwnQuick((r.data && r.data.fields) || null); })
      .catch(() => { setQuickHist([]); setOwnQuick(null); });
  }, [profileId]);
  // 会员心动 — null while unknown / when the endpoint isn't deployed (everything about it then hides).
  const [favs, setFavs] = useState(null);
  const loadSide = useCallback(() => {
    workspaceAPI.memberFavorites(profileId)
      .then(r => setFavs(r.data && Array.isArray(r.data.current) ? r.data : null))
      .catch(() => setFavs(null));
    profilesAPI.getComplaints(profileId).then(r => setComplaints(Array.isArray(r.data?.complaints) ? r.data.complaints : [])).catch(() => setComplaints([]));
    serviceReportsAPI.forProfile(profileId)
      .then(r => setReports(Array.isArray(r.data?.reports) ? r.data.reports : Array.isArray(r.data) ? r.data : []))
      .catch(() => setReports([]));
    loadQuickHist();
  }, [profileId, loadQuickHist]);
  useEffect(() => { loadSide(); }, [loadSide]);

  // 核实老师 / 整改负责老师 options: the staff directory where this viewer may
  // read it, plus everyone who has worked on this member.
  useEffect(() => {
    let alive = true;
    workspaceAPI.staff()
      .catch(() => supervisorAPI.getStaff())
      .then(r => { if (alive) setStaffList(Array.isArray(r.data?.staff) ? r.data.staff : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // The overlay covers exactly the main content area: right of the app
  // sidebar, full height. The sidebar can collapse while it is open, so the
  // offset is re-measured on resize of .main-content too.
  const [frameLeft, setFrameLeft] = useState(0);
  useEffect(() => {
    const el = document.querySelector('.main-content');
    const measure = () => {
      const node = document.querySelector('.main-content');
      setFrameLeft(node ? Math.max(0, Math.round(node.getBoundingClientRect().left)) : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    let ro = null;
    if (el && typeof window.ResizeObserver === 'function') { ro = new window.ResizeObserver(measure); ro.observe(el); }
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, []);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onDown = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [moreOpen]);


  const load = useCallback(async () => {
    setLoading(true);
    const [p, a, f, sc, qz, pay, st, mc] = await Promise.all([
      // GET /profiles/:id spreads the profile at the top level (no wrapper key).
      profilesAPI.getById(profileId).then(r => r.data).catch(() => null),
      // The endpoint answers { activity: [...] } — reading `items` here silently
      // rendered an empty timeline even though the check-ins and notes had been
      // written correctly, which made every action look like it did nothing.
      profilesAPI.getActivity(profileId).then(r => r.data?.activity || r.data?.items || []).catch(() => []),
      profilesAPI.getFollowups(profileId).then(r => r.data?.followups || []).catch(() => []),
      // 约见记录 + 「见了几次」 need the real schedule rows (partner ids, status,
      // feedback), not the name-only date entries on the timeline.
      datingScheduleAPI.getAll({ profile_id: profileId }).then(r => r.data?.schedules || []).catch(() => []),
      kpiV2API.listProfileQuestionnaires(profileId).then(r => r.data?.submissions || []).catch(() => []),
      coachKpiAPI.getClientPayments(profileId).then(r => r.data).catch(() => null),
      serviceSopAPI.status(profileId).then(r => r.data).catch(() => null),
      serviceSopAPI.meetingChecks(profileId).then(r => r.data?.meetings || null).catch(() => null),
    ]);
    setSvc(st && typeof st === 'object' && !Array.isArray(st) ? st : null);
    const mcMap = {};
    (Array.isArray(mc) ? mc : []).forEach(m => { if (m && m.dating_schedule_id != null) mcMap[m.dating_schedule_id] = m; });
    setMeetChecks(mcMap);
    if (p) setProfile(prev => ({ ...(prev || {}), ...p }));
    setActivity(Array.isArray(a) ? a : []);
    setFollowups(Array.isArray(f) ? f : []);
    setSchedules(Array.isArray(sc) ? sc : []);
    setQuizRows(Array.isArray(qz) ? qz : []);
    setPayData(pay && Array.isArray(pay.invoices) ? pay : null);
    setLoading(false);
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  // Status-line only refresh (after a frequency request / a note / a complaint).
  const reloadStatus = useCallback(() => {
    serviceSopAPI.status(profileId)
      .then(r => setSvc(r.data && typeof r.data === 'object' && !Array.isArray(r.data) ? r.data : null))
      .catch(() => {});
  }, [profileId]);

  // Esc closes, and the body doesn't scroll behind the overlay.
  const dialogOpenRef = React.useRef(false);
  useEffect(() => {
    // A dialog over the workspace (写联系记录 / 编辑) swallows Esc, so a
    // half-written record is never thrown away by the workspace closing.
    const onKey = (e) => { if (e.key === 'Escape' && !dialogOpenRef.current && !hiddenRef.current) onClose && onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [onClose]);

  const p = profile || {};
  // WeChat name (field 138) is the display name everywhere else in the app. The
  // board row carries it as `gcv`, GET /profiles/:id as `custom_values`.
  const name = (p.gcv && p.gcv['138']) || (p.custom_values && p.custom_values['138']) || p.first_name || `#${profileId}`;
  const initials = `${(p.first_name || '')[0] || ''}${(p.last_name || '')[0] || ''}`.toUpperCase() || '?';

  // Tell the docked assistant which client is on screen, so a question asked
  // while this modal is open is answered about THIS person. A window event
  // keeps the two components decoupled — the modal is opened from four places.
  useEffect(() => {
    const fire = (detail) => {
      try { window.dispatchEvent(new CustomEvent('kpi-crm-client', { detail })); } catch (e) { /* older browsers */ }
    };
    if (demo) return undefined;
    fire({ id: profileId, name });
    return () => fire(null);
  }, [profileId, name, demo]);
  const stage = STAGES_DEF(isZh).find(s => s.key === String(p.pipeline_stage || 'new').toLowerCase())
    || STAGES_DEF(isZh)[0];
  const coachName = [p.coach_first_name, p.coach_last_name].filter(Boolean).join(' ');
  // 提供身份证件 (provide_photo_id, answered on the Photo ID registration step)
  // — 'No' is worth a warning wherever staff look the client up.
  const noPhotoId = (() => {
    const f = allFields.find(x => x.name === 'provide_photo_id');
    if (!f) return false;
    const gcv = (p.gcv || p.custom_values) || {};
    const v = gcv[f.id] != null ? gcv[f.id] : gcv[String(f.id)];
    const sv = String(v ?? '').trim().toLowerCase();
    return sv === 'no' || sv === '未提供';
  })();
  const expiry = (() => {
    const d = parseCalDate(p.membership_end_date);
    if (!d) return null;
    const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
    // counted from today's Pacific date, not the browser's
    const days = Math.round((startOf(d) - startOf(parseCalDate(laTodayYMD()))) / 86400000);
    return days < 0 ? t(`已过期 ${-days} 天`, `${-days}d overdue`) : t(`还剩 ${days} 天`, `${days}d left`);
  })();
  // Derived rather than taken from the board aggregate, so the panel is right
  // whether it was opened from the board or from 今日待办.
  const contactedThisWeek = activity.some(x => x.type === 'contact' && new Date(x.at) >= startOfWeek());

  // Every action funnels through here: write → toast → reload the timeline so the
  // result is visible immediately → drop back to it → tell the caller to refresh.
  const runAction = async (fn, okMsg) => {
    setSaving(true);
    try {
      await fn();
      toast.success(okMsg);
      // Only a SUCCESSFUL write closes the editor — on failure the panel stays
      // open with the text still in it, so nothing typed is lost.
      setComposer(null); setDraft(''); setView(null); setEditing(null);
      await load();
      reloadWs(); loadSide();
      onChanged && onChanged();
    } catch (e) {
      const err = e?.response?.data;
      toast.error(err?.error || err?.message || t('操作失败', 'Failed'));
    } finally { setSaving(false); }
  };

  // ── 推荐给他人 ───────────────────────────────────────────────────────────
  const loadCandidates = useCallback(async (q) => {
    setCandLoading(true);
    try {
      // The FULL opposite-sex pool, always scored. This used to fetch 25 rows
      // by default (and 300 unscored ones while searching/filtering) because
      // the backend ran a scoring query per candidate; scoring is batched now,
      // so the whole pool comes back scored in one go and the grid pages it.
      // 2000 is the server-side cap on /matches/potential.
      // include_matched — people already on a line with this client come back
      // ANNOTATED instead of filtered out. Dropping them made a search for such
      // a member return nothing at all («搜索不出来»), which is indistinguishable
      // from the person not existing; the card now says 已推荐 instead.
      const params = { limit: CAND_FETCH_LIMIT, include_matched: 1, with_prefs: 1, with_quick_card: 1, ...(q ? { search: q } : {}) };
      // 推荐的必须是客户的异性 — the endpoint takes a gender filter but applies
      // none by default, so the opposite of THIS client's gender is passed in.
      // Unknown gender on the client → no filter, rather than an empty pool.
      const g = String(profile?.gender || '').toLowerCase();
      if (g === 'male') params.gender = 'female';
      else if (g === 'female') params.gender = 'male';
      const r = await matchesAPI.getPotential(profileId, params);
      const rows = Array.isArray(r.data) ? r.data : [];
      setCandidates(rows);
    } catch { setCandidates([]); } finally { setCandLoading(false); }
  }, [profileId, profile?.gender]);

  // Applied over the fetched pool. Age comes from date_of_birth, 地区 matches
  // city/state/country as a substring, and 学历 / 婚况 match the raw custom
  // value or its 中文 label, so 「大学」 finds rows stored as "College".
  // 底线提醒, per candidate, both directions:
  //   member  — this client's 择偶要求 vs the candidate's 档案
  //   partner — the candidate's 择偶要求 vs this client's 档案
  // level 'bottom' = a 画像 底线 missed; 'pref' = an older plain 择偶要求 missed.
  const bottomHits = useMemo(() => {
    const gcv = (profile && (profile.gcv || profile.custom_values)) || {};
    const g = (fid) => gcv[fid] != null ? gcv[fid] : gcv[String(fid)];
    const me = {
      age: ageFromDob(profile && profile.date_of_birth), height: g(9), love: g(80), child: g(90),
      pay: g(109), edu: g(107), house: g(6), religion: g(83),
    };
    const out = {};
    (candidates || []).forEach(c => {
      const them = {
        age: ageFromDob(c.date_of_birth || c.birth_date), height: c.height, love: c.marital_status, child: c.children_status,
        pay: c.monthly_income, edu: c.education, house: c.real_estate, religion: c.religion,
      };
      const member = bottomLineHits(gcv, me, them);
      const partner = bottomLineHits(c.prefs || {}, them, me);
      out[c.id] = { member, partner, bottom: member.concat(partner).some(h => h.level === 'bottom') };
    });
    return out;
  }, [candidates, profile]);

  const visibleCandidates = useMemo(() => {
    const norm = (v) => String(v ?? '').trim().toLowerCase();
    const min = Number(candFilters.ageMin) || 0;
    const max = Number(candFilters.ageMax) || 0;
    // A candidate stores the raw option (usually the English one); the filter
    // may hold either side of the {en, zh} pair, so both are compared.
    const matches = (raw, want) => {
      if (!want) return true;
      const w = norm(want);
      const a = norm(raw);
      const b = norm(translateOptionLabel(String(raw ?? '').trim(), 'zh'));
      return a.includes(w) || b.includes(w) || (!!a && w.includes(a));
    };
    const out = (candidates || []).filter(c => {
      const age = ageOf(c.date_of_birth || c.birth_date);
      if (min && (!age || age < min)) return false;
      if (max && (!age || age > max)) return false;
      // 国家 / 省·州 / 城市 each match their own column, so "加州" no longer
      // needs to compete with a city of the same name in a free-text box.
      if (!matches(c.location_country, candFilters.country)) return false;
      if (!matches(c.location_state, candFilters.state)) return false;
      if (!matches(c.location_city, candFilters.city)) return false;
      for (const f of CAND_FILTER_FIELDS) {
        if (!matches(c[f.key], candFilters[f.key])) return false;
      }
      if (hideBottom && bottomHits[c.id] && bottomHits[c.id].bottom) return false;
      return true;
    });
    const age = (c) => ageOf(c.date_of_birth || c.birth_date) || 0;
    if (candSort === 'ageAsc') out.sort((a, b) => (age(a) || 999) - (age(b) || 999));
    else if (candSort === 'ageDesc') out.sort((a, b) => age(b) - age(a));
    else if (candSort === 'newest') out.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    else out.sort((a, b) => (Number(b.matchPercentage) || 0) - (Number(a.matchPercentage) || 0));
    return out;
  }, [candidates, candFilters, candSort, hideBottom, bottomHits]);

  // 国家 / 省·州 / 城市 option lists, derived from the pool that is actually
  // loaded — so every option can really match something. They cascade: picking
  // a country narrows the states, picking a state narrows the cities.
  const locationOptions = useMemo(() => {
    const norm = (v) => String(v ?? '').trim().toLowerCase();
    const eq = (raw, want) => {
      if (!want) return true;
      const w = norm(want);
      const a = norm(raw);
      const b = norm(translateOptionLabel(String(raw ?? '').trim(), 'zh'));
      return a.includes(w) || b.includes(w) || (!!a && w.includes(a));
    };
    const uniq = (rows, key) => [...new Set(
      rows.map(c => String(c[key] ?? '').trim()).filter(Boolean)
    )].sort((a, b) => a.localeCompare(b));

    const all = candidates || [];
    const inCountry = all.filter(c => eq(c.location_country, candFilters.country));
    const inState = inCountry.filter(c => eq(c.location_state, candFilters.state));
    return {
      countries: uniq(all, 'location_country'),
      states: uniq(inCountry, 'location_state'),
      cities: uniq(inState, 'location_city'),
    };
  }, [candidates, candFilters.country, candFilters.state]);

  // Any change to what's listed puts the grid back on page 1, so a filter never
  // lands the coach on an out-of-range page showing nothing.
  useEffect(() => { setCandPage(1); }, [candidates, candFilters, candSort, hideBottom, view, scheduleSource]);

  // Options come from the field definitions, so a filter can offer a value that
  // no loaded candidate happens to have yet.
  const loadFieldOptions = useCallback(() => {
    if (Object.keys(fieldOptions).length) return;
    profilesAPI.getGlobalCustomFields()
      .then(r => {
        const wanted = new Set(CAND_FILTER_FIELDS.map(f => f.fid));
        const map = {};
        const all = [];
        (Array.isArray(r.data) ? r.data : []).forEach(f => {
          const raw = Array.isArray(f.field_options) ? f.field_options : [];
          const opts = raw
            .map(o => (typeof o === 'string' ? { en: o, zh: o } : { en: o.en || o.value || o.label || '', zh: o.zh || o.label_zh || o.en || '' }))
            .filter(o => o.en || o.zh);
          all.push({
            id: Number(f.id), name: String(f.field_name || ''), type: String(f.field_type || ''),
            label: f.field_label || '', label_zh: f.field_label_zh || '', opts,
          });
          if (wanted.has(Number(f.id))) map[Number(f.id)] = opts;
        });
        setFieldOptions(map);
        setAllFields(all);
      })
      .catch(() => { /* filters fall back to free text */ });
  }, [fieldOptions]);

  // The left column renders 档案资料 + 择偶 from the field catalogue, so it is
  // fetched with the modal rather than only when a candidate panel opens.
  useEffect(() => { loadFieldOptions(); }, [loadFieldOptions]);

  const openRecommend = () => {
    setView('recommend'); setCandSearch(''); setCandCall({});
    setCandFilters(EMPTY_FILTERS); setCandSort('match'); setShowFilters(false); setHideBottom(false);
    loadFieldOptions();
    loadCandidates('');
  };

  // 互相心动 nudge → 推荐给他人 with that person already in 已选.
  const recommendFav = (tg) => {
    setComposer(null); setEditing(null);
    openRecommend();
    if (tg && tg.id) {
      setPicked(prev => ({ ...prev, [tg.id]: { id: tg.id, first_name: tg.first_name || favName(tg), last_name: tg.last_name || '', profile_photo: tg.photo || null, gender: tg.gender || null } }));
    }
  };

  // 把客户推荐给他人打电话 — same public /audio-call room as the client call,
  // booked against the CANDIDATE so the record lands on their profile, and
  // copied out with the same invitation block.
  const callCandidate = async (c) => {
    if (candBusy) return;
    setCandBusy(c.id);
    try {
      const nm = nameOfProfile(c) || `#${c.id}`;
      const res = await meetingsAPI.create({
        title: t(`语音通话 · ${nm}`, `Voice call · ${nm}`),
        client_profile_id: c.id,
        client_name: nm,
        call_type: 'audio',
        record_video: false,
      });
      const mtg = res.data?.meeting || res.data || {};
      const path = mtg.meeting_link || (mtg.channel_name ? `/audio-call/${mtg.channel_name}` : '');
      if (!path) throw new Error('no link in response');
      const url = `${window.location.origin}${path}`;
      setCandCall(prev => ({ ...prev, [c.id]: url }));
      copyText(shareBlock(url), t('邀请内容已复制', 'Invitation copied'), t('复制邀请内容', 'Copy the invitation'));
    } catch (e) {
      toast.error(e?.response?.data?.error || t('创建通话失败', 'Could not create the call'));
    } finally { setCandBusy(null); }
  };

  // 签线 — put this client and the candidate on one line (a matches row). It is
  // the single-candidate form of the bulk 创建匹配 button at the bottom.
  const signLine = async (c) => {
    if (candBusy) return;
    setCandBusy(c.id);
    try {
      await matchesAPI.create({ profile_a_id: Number(profileId), profile_b_id: Number(c.id) });
      toast.success(t(`已签线：${nameOfProfile(c) || c.id}`, 'Line signed'));
      setPicked(prev => { const n = { ...prev }; delete n[c.id]; return n; });
      loadCandidates(candSearch.trim());
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || t('签线失败', 'Failed'));
    } finally { setCandBusy(null); }
  };

  // 约见 — sign the line first when it doesn't exist yet (a date needs a match
  // behind it), then drop into 安排约会 with this person preselected.
  const meetCandidate = async (c) => {
    if (candBusy) return;
    setCandBusy(c.id);
    try {
      try {
        await matchesAPI.create({ profile_a_id: Number(profileId), profile_b_id: Number(c.id) });
      } catch (e) { /* already on a line — fine, go straight to scheduling */ }
      await openSchedule(c.id);
      onChanged && onChanged();
    } finally { setCandBusy(null); }
  };

  // 相亲卡 — the row lacks custom_values, so fetch the full profile first or the
  // card renders with empty 性格/兴趣 sections.
  const openCard = async (c) => {
    if (candBusy) return;
    setCandBusy(c.id);
    try {
      let full = c;
      try { const r = await profilesAPI.getById(c.id); full = { ...c, ...(r?.data || {}) }; } catch (e) { /* row data */ }
      setCardProfile(full);
    } finally { setCandBusy(null); }
  };

  // 心动 list「查看档案」→ the same 相亲卡 the candidate cards open (demo: no fetch).
  const viewFav = (tg) => {
    const row = { id: tg.id, first_name: tg.first_name || '', last_name: tg.last_name || '', profile_photo: tg.photo || null,
      gender: tg.gender || null, location_city: tg.city || null, offline_id: tg.offline_id || null };
    if (demo) { setCardProfile(row); return; }
    openCard(row);
  };

  const togglePick = (row) => {
    // Already on a line — createMatch would reject the duplicate pair, so the
    // card is shown (so a search finds them) but cannot be picked again.
    if (row.existing_match_id) {
      toast.info(t(`${nameOfProfile(row) || `#${row.id}`} 与 ${name} 已匹配过`,
        `${nameOfProfile(row) || `#${row.id}`} is already matched with ${name}`));
      return;
    }
    setPicked(prev => {
      const next = { ...prev };
      if (next[row.id]) delete next[row.id]; else next[row.id] = row;
      return next;
    });
  };

  // The system draft for one candidate, from both 速记卡 (member-safe fields only).
  const reasonDraft = (id) => {
    const c = (candidates || []).find(x => String(x.id) === String(id)) || picked[id] || {};
    return draftRecommendReason({ theirName: nameOfProfile(c) || 'TA', mine: ownQuick || {}, theirs: c.quick_card || {} });
  };
  const reasonFor = (id) => reasons[id] || { text: reasonDraft(id), confirmed: false };

  const submitRecommend = () => {
    const ids = Object.keys(picked);
    if (!ids.length) { toast.error(t('请先选择候选人', 'Pick someone first')); return; }
    runAction(async () => {
      // Sequential, not Promise.all: createMatch computes a match score per pair
      // and firing 10 at once just queues them on the DB anyway.
      for (const id of ids) {
        const rv = reasonFor(id);
        await matchesAPI.create({
          profile_a_id: Number(profileId), profile_b_id: Number(id),
          ...(rv.text.trim() ? { recommend_reason: rv.text, recommend_reason_confirmed: !!rv.confirmed } : {}),
        });
      }
      setPicked({}); setReasons({});
    }, t(`已推荐 ${ids.length} 人`, `Recommended ${ids.length}`));
  };

  // ── 安排约会 ────────────────────────────────────────────────────────────
  const openSchedule = async (preselectId = null) => {
    setView('schedule');
    setScheduleSource('lines'); setCandFilters(EMPTY_FILTERS); setCandSort('match'); setShowFilters(false);
    loadFieldOptions();
    setForm({ partnerId: preselectId ? String(preselectId) : '', date: '', time: '', location: '', remindAt: '' });
    setCandLoading(true);
    try {
      const r = await matchesAPI.getAll({ profile_id: profileId, limit: 50 });
      setMatchRows(r.data?.matches || []);
    } catch { setMatchRows([]); } finally { setCandLoading(false); }
  };

  // The other half of each match — whichever side isn't this client.
  const partners = matchRows.map(m => {
    const aIsMe = Number(m.profile_a_id) === Number(profileId);
    return {
      id: aIsMe ? m.profile_b_id : m.profile_a_id,
      name: (aIsMe ? m.profile2_name : m.profile1_name) || `#${aIsMe ? m.profile_b_id : m.profile_a_id}`,
      offline_id: aIsMe ? m.profile2_offline_id : m.profile1_offline_id,
      pct: m.match_percentage,
    };
  }).filter((x, i, arr) => x.id && arr.findIndex(y => y.id === x.id) === i);

  // 删除签线 — drops the matches row behind a partner. The date history stays
  // (dating_schedules rows are not touched); only the line goes.
  const removeLine = async (x) => {
    const row = matchRows.find(m => Number(m.profile_a_id) === Number(x.id) || Number(m.profile_b_id) === Number(x.id));
    if (!row) return;
    if (!window.confirm(t(`确定删除与 ${x.name} 的签线？`, `Delete the line with ${x.name}?`))) return;
    try {
      await matchesAPI.delete(row.id);
      setMatchRows(list => list.filter(m => m.id !== row.id));
      if (String(form.partnerId) === String(x.id)) setForm(f => ({ ...f, partnerId: '' }));
      toast.success(t('已删除签线', 'Line deleted'));
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.error || t('删除失败', 'Failed'));
    }
  };

  // `anchor` = key of the 全部记录 row that was clicked: the detail opens right under that row
  // (clicking it again closes it). Without one (e.g. from the timeline chart) it shows above the list.
  // 推荐理由 of a 推荐 record: the saved text, plus a fresh draft from the
  // other person's 速记卡 (fetched through the candidate endpoint, which is
  // the one place another coach's member's card can be read).
  const loadRecReason = async (data) => {
    setRecReason({ loading: true, text: '', confirmed: false, saved: null, draft: '' });
    let text = ''; let confirmed = false;
    try {
      const r = await matchesAPI.getById(data.id);
      text = (r.data && r.data.recommend_reason) || '';
      confirmed = !!(r.data && r.data.recommend_reason_confirmed_at);
    } catch (e) { /* no access / old backend → start empty */ }
    let draft = '';
    const pid = data.other_id || data.otherId;
    if (pid) {
      try {
        const r = await matchesAPI.getPotential(profileId, { search: String(pid), include_matched: 1, with_quick_card: 1, score: 0, limit: 20 });
        const c = (Array.isArray(r.data) ? r.data : []).find(x => String(x.id) === String(pid));
        draft = draftRecommendReason({ theirName: (c && nameOfProfile(c)) || data.other || 'TA', mine: ownQuick || {}, theirs: (c && c.quick_card) || {} });
      } catch (e) { /* no draft */ }
    }
    setRecReason(cur => (cur && cur.loading
      ? { loading: false, text: text || draft, confirmed, saved: { text, confirmed }, draft }
      : cur));
  };
  const saveRecReason = async () => {
    if (!record || record.kind !== 'match' || !recReason) return;
    setRecReasonSaving(true);
    try {
      await matchesAPI.update(record.data.id, { recommend_reason: recReason.text, recommend_reason_confirmed: !!recReason.confirmed });
      setRecReason(r => ({ ...r, saved: { text: r.text.trim(), confirmed: !!r.confirmed && !!r.text.trim() } }));
      toast.success(recReason.confirmed && recReason.text.trim()
        ? t('推荐理由已确认，会员在确认链接里看得到', 'Saved — the member will see it')
        : t('推荐理由已保存（未确认，会员看不到）', 'Saved — hidden until confirmed'));
    } catch (e) {
      toast.error(e?.response?.data?.message || t('保存失败', 'Save failed'));
    } finally { setRecReasonSaving(false); }
  };

  const openRecord = (kind, data, anchor) => {
    if (anchor && record && record.anchor === anchor) { setRecord(null); return; }
    setRecord({ kind, data, anchor: anchor || null });
    setRecordStatus(String(data?.status || ''));
    setReviewLink(null);
    setRecReason(null);
    if (kind === 'match' && data && data.id) loadRecReason(data);
    const pid = data && (data.other_id || data.otherId || data.partnerId);
    if (pid && !peers[pid]) {
      profilesAPI.getById(pid)
        .then(r => setPeers(prev => ({ ...prev, [pid]: r?.data || null })))
        .catch(() => setPeers(prev => ({ ...prev, [pid]: null })));
    }
  };

  // 推荐确认链接 — a public /match-review page where the client accepts or
  // declines the people they were recommended. Reuses the existing review
  // flow; the backend merges repeated requests into one open review, so a
  // client gets ONE link listing everyone rather than a link per match.
  const createReviewLink = async () => {
    if (reviewBusy || !record || record.kind !== 'match') return;
    setReviewBusy(true);
    try {
      const r = await coachKpiAPI.createMatchReview({
        client_profile_id: Number(profileId),
        match_ids: [Number(record.data.id)],
      });
      const url = r.data?.link;
      if (!url) throw new Error('no link');
      setReviewLink({ url, count: r.data?.match_count || 1 });
      copyText(
        t(`${inviteHost}，请确认我们为你推荐的人选：${url}`,
          `${inviteHost}: please confirm the people we recommended: ${url}`),
        t('确认链接已复制，可直接发给客户', 'Confirmation link copied — send it to the client'),
        t('复制确认链接', 'Copy the confirmation link'),
      );
    } catch (e) {
      toast.error(e?.response?.data?.error || t('生成确认链接失败', 'Could not create the link'));
    } finally { setReviewBusy(false); }
  };

  // 推荐/约见 records can be moved along from their detail panel.
  const saveRecordStatus = async () => {
    if (!record || !recordStatus) return;
    setRecordSaving(true);
    try {
      if (record.kind === 'match') {
        await matchesAPI.update(record.data.id, { status: recordStatus });
      } else if (record.kind === 'date') {
        await datingScheduleAPI.updateStatus(record.data.id, { status: recordStatus });
      }
      toast.success(t('状态已更新', 'Status updated'));
      setRecord(r => (r ? { ...r, data: { ...r.data, status: recordStatus } } : r));
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || t('更新失败', 'Update failed'));
    } finally { setRecordSaving(false); }
  };

  const submitSchedule = () => {
    if (!form.partnerId || !form.date || !form.time) {
      toast.error(t('请选择对象、日期和时间', 'Pick a partner, date and time')); return;
    }
    runAction(async () => {
      // Picked straight out of 全部异性 → put the pair on a line first, so the
      // date never exists without the 签线 record behind it.
      const known = partners.some(pp => String(pp.id) === String(form.partnerId));
      if (!known) {
        try { await matchesAPI.create({ profile_a_id: Number(profileId), profile_b_id: Number(form.partnerId) }); }
        catch (e) { /* already on a line */ }
      }
      await datingScheduleAPI.create({
        client1_profile_id: Number(profileId),
        client2_profile_id: Number(form.partnerId),
        scheduled_date: form.date,
        scheduled_time: form.time,
        location: form.location || null,
        title: t('约会', 'Date'),
        remind_at: form.remindAt || null,
      });
    }, t('约会已安排', 'Date scheduled'));
  };

  // ── 写约会反馈 ──────────────────────────────────────────────────────────
  const openFeedback = async () => {
    setView('feedback'); setFbTarget(null);
    setFbForm({ status: 'completed', text: '', rating: '' });
    setCandLoading(true);
    try {
      const r = await datingScheduleAPI.getAll({ profile_id: profileId });
      setSchedules(r.data?.schedules || []);
    } catch { setSchedules([]); } finally { setCandLoading(false); }
  };

  // Selecting a date prefills whatever is already on file for THIS client's
  // side, so re-opening it edits rather than silently starts over.
  const pickFbTarget = (s) => {
    const isC1 = Number(s.client1_profile_id) === Number(profileId);
    setFbTarget(s);
    setFbForm({
      status: s.status && s.status !== 'pending' ? s.status : 'completed',
      text: (isC1 ? s.client1_feedback : s.client2_feedback) || '',
      rating: (isC1 ? s.client1_rating : s.client2_rating) || '',
    });
  };

  const submitFeedback = () => {
    if (!fbTarget) { toast.error(t('请选择一场约会', 'Pick a date first')); return; }
    if (!fbForm.text.trim()) { toast.error(t('请填写反馈', 'Write the feedback')); return; }
    // The endpoint overwrites ALL FOUR feedback columns on every call, so a
    // payload carrying only this client's side would wipe the other client's
    // feedback. Send both halves: ours from the form, theirs unchanged.
    const isC1 = Number(fbTarget.client1_profile_id) === Number(profileId);
    const mine = fbForm.text.trim();
    const myRating = fbForm.rating ? Number(fbForm.rating) : null;
    const payload = {
      status: fbForm.status,
      client1_feedback: isC1 ? mine : (fbTarget.client1_feedback || null),
      client1_rating: isC1 ? myRating : (fbTarget.client1_rating || null),
      client2_feedback: isC1 ? (fbTarget.client2_feedback || null) : mine,
      client2_rating: isC1 ? (fbTarget.client2_rating || null) : myRating,
    };
    runAction(() => datingScheduleAPI.submitFeedback(fbTarget.id, payload), t('反馈已保存', 'Feedback saved'));
  };

  // 'YYYY-MM-DDTHH:mm' on the PACIFIC wall clock — what <input type="datetime-local">
  // wants. Every time a coach types here means US Pacific, wherever the browser
  // is, so values go in via utcISOToLAInput and come out via laInputToUTCISO.
  const toLocalInput = (d) => utcISOToLAInput(d.toISOString());
  // 下次联系 defaults to now + this member's contact interval (48 h unless a
  // 主管 approved another frequency).
  const contactInterval = Number(svc && svc.interval_hours) || 48;
  const defaultNextAt = () => toLocalInput(new Date(Date.now() + contactInterval * 3600000));
  // opts (打卡 only): { purpose, scheduleId, target: { id, name } } — preset by
  // the 约见记录 check links.
  const openComposer = (kind, opts = {}) => {
    setView(null); setComposer(kind); setDraft(opts.draft || ''); setEditing(null);
    setQLink(null); setQOpen(false);
    if (kind === 'contact') {
      setNextAt(defaultNextAt());
      setCPurpose(opts.purpose || (svc && !svc.last_contact_at ? 'first_contact' : 'followup_48h'));
      setCMethod('phone'); setCOutcome(''); setCImages([]);
      setCSchedule(opts.scheduleId != null ? String(opts.scheduleId) : '');
      setCTarget(opts.target || null);
    }
  };

  // initialAction — applied once, after the first load, so 打卡 picks up the
  // member's interval and first-contact default.
  const initialDone = React.useRef(false);
  useEffect(() => {
    if (initialDone.current || loading || !initialAction) return;
    initialDone.current = true;
    // 'contact' → 写联系记录 dialog · 'notes' → 下一步 (备忘录) · 'client_notes' → 全部记录 · 客户情况 · 'review' → 审核
    // (主管) · 'steps' → 服务进度 · 'info' (心动 bell: ?crm=…&tab=info) → 资料·报告 · legacy 'meets' / 'tasks' / 'matches' → 全部记录.
    if (initialAction === 'contact') openComposer('contact');
    else if (initialAction === 'notes') { setTab('next'); setMemoFocus(n => n + 1); }
    else if (initialAction === 'client_notes') { setTab('records'); setRecFilter('notes'); }
    else if (initialAction === 'review') setTab(isSup ? 'review' : 'next');
    else if (initialAction === 'steps') setTab('steps');
    else if (initialAction === 'info' || initialAction === 'favorites') setTab('profile');
    else if (['meets', 'tasks', 'matches'].includes(initialAction)) {
      setTab('records'); setRecFilter({ meets: 'meet', tasks: 'contact', matches: 'match' }[initialAction]);
    }
  }, [loading, initialAction]);

  // 操作引导: put the screen the current tour step wants on (tab / 写联系记录 / report / step / 整改单).
  const demoN = demo && demoView ? demoView.n : 0;
  useEffect(() => {
    if (!demoN) return;
    const v = demoView || {};
    setRecord(null); setView(null); setEditing(null); setMoreOpen(false);
    setTab(v.tab || 'next');
    if (v.composer === 'contact') {
      openComposer('contact', { purpose: 'followup_48h', draft: DEMO_CONTACT_TEXT });
      setCMethod('phone'); setCOutcome('two_way'); setCImages([DEMO_SHOT]);
    } else {
      setComposer(null); setDraft('');
    }
    setReportOpen(v.report ? DEMO_REPORT_ID : null);
    if (v.stepFocus) setStepFocus(v.stepFocus);
    if (v.rect) setPresetRect((x) => ({ n: ((x && x.n) || 0) + 1, title: '补齐 2 次超时的 48 小时联系，并写清会员反馈和下一步' }));
  }, [demoN]);

  // 打卡 screenshots — same /upload endpoint the KPI 打卡 and complaints use.
  const onContactFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setCUploading(true);
    try {
      const urls = [];
      for (const f of files.slice(0, Math.max(0, 9 - cImages.length))) {
        const r = await coachDailyContactsAPI.uploadImage(f);
        const u = r.data?.url || r.data?.file_url;
        if (u) urls.push(u);
      }
      setCImages(prev => [...prev, ...urls]);
    } catch (err) {
      toast.error(t('图片上传失败', 'Upload failed'));
    } finally {
      setCUploading(false);
      if (cFileRef.current) cFileRef.current.value = '';
    }
  };

  // Host line of the invite: brand + the coach's own name.
  const inviteHost = `${INVITE_BRAND}${INVITE_TITLE}`;

  // One place builds the shareable block, so every kind of link pastes the
  // same shape — only the "invites you to …" line changes.
  const shareBlock = (url, kind = 'audio') => meetingInvite({ host: inviteHost, url, kind });

  // 问卷 — list what is available, generate a client link for the chosen one.
  const openQuestionnaires = async () => {
    setQOpen(v => !v);
    if (qList !== null) return;
    try {
      const r = await coachKpiAPI.getQuestionnaires();
      const rows = r.data?.questionnaires || r.data?.data || (Array.isArray(r.data) ? r.data : []);
      setQList(rows);
    } catch (e) {
      setQList([]);
      toast.error(t('问卷列表加载失败', 'Could not load questionnaires'));
    }
  };

  const generateQuestionnaire = async (q) => {
    if (qBusy) return;
    setQBusy(true);
    try {
      const r = await coachKpiAPI.sendQuestionnaire(q.id, {
        client_name: name, sent_to_name: name,
        client_email: profile?.email || null, sent_to_email: profile?.email || null,
        profile_id: profileId,
      });
      const url = r.data?.link || r.data?.public_link;
      if (!url) throw new Error('no link');
      const title = (isZh && q.title_zh) ? q.title_zh : (q.title || t('问卷', 'Questionnaire'));
      setQLink({ title, url });
      setQOpen(false);
      setDraft(d => (d.includes(url) ? d : `${d ? `${d}\n` : ''}${t('问卷', 'Questionnaire')}「${title}」：${url}`));
      copyText(shareBlock(url, 'questionnaire'),
        t('问卷邀请已复制，可直接发给客户', 'Invitation copied — paste it to the client'), t('复制问卷邀请', 'Copy the invitation'));
    } catch (e) {
      toast.error(e?.response?.data?.error || t('生成问卷失败', 'Could not create the questionnaire'));
    } finally { setQBusy(false); }
  };

  // The visible box holds the full invitation, not the bare URL — what the
  // coach sees is what gets pasted to the client.
  const shareBox = (url, kind, accent) => {
    const text = shareBlock(url, kind);
    return (
      <>
        <textarea readOnly value={text} rows={4}
          onFocus={(e) => e.target.select()}
          style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical', border: `1px solid ${APP.line}`,
            borderRadius: 2, padding: '7px 9px', fontSize: 11.5, lineHeight: 1.6, fontFamily: 'inherit',
            background: '#FFFDF9', color: APP.ink, marginBottom: 6 }} />
        <button type="button" onClick={() => copyText(text, t('已复制', 'Copied'), t('复制邀请内容', 'Copy the invitation'))}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500, padding: '6px 11px',
            borderRadius: 2, border: 'none', background: accent, color: '#FFFDF9', cursor: 'pointer' }}>
          <Copy size={13} />{t('复制邀请内容', 'Copy invitation')}
        </button>
      </>
    );
  };

  const submitComposer = () => {
    const text = draft.trim();
    if (!text) { toast.error(t('请先填写内容', 'Write something first')); return; }
    if (composer === 'contact') {
      if (!cOutcome) { toast.error(t('请选择结果：已双向沟通，或已联系未回复', 'Pick the outcome first')); return; }
      if (text.length < 10) { toast.error(t('沟通内容至少 10 个字：沟通了什么、会员反馈、下一步', 'Write at least 10 characters')); return; }
      if (needsScreenshot(cMethod, cPurpose) && !cImages.length) {
        toast.error(t(`请至少上传 1 张${screenshotHint(cMethod, true)}`, `Add at least one ${screenshotHint(cMethod, false)}`));
        return;
      }
      if (needsNextContact(cMethod) && !nextAt) {
        toast.error(t('请选择下次联系的日期和时间', 'Pick the next contact date and time')); return;
      }
      if (MEETING_PURPOSES.includes(cPurpose) && !cSchedule) {
        toast.error(t('请选择关联哪次约会', 'Pick the date this belongs to')); return;
      }
      const next = nextAt ? laInputToUTCISO(nextAt) : null;   // typed as Pacific
      runAction(
        () => serviceSopAPI.logContact({
          profile_id: cTarget ? cTarget.id : profileId,
          kind: kindOfMethod(cMethod),
          notes: text,
          images: cImages,
          next_follow_up_at: next || null,
          purpose: cPurpose,
          method: cMethod,
          outcome: cOutcome,
          dating_schedule_id: MEETING_PURPOSES.includes(cPurpose) && cSchedule ? Number(cSchedule) : null,
          contacted_at: new Date().toISOString(),
        }),
        t('联系记录已保存', 'Contact saved'),
      );
    } else {
      runAction(() => profilesAPI.createProfileNote(profileId, { note: text }), t('已记录', 'Saved'));
    }
  };

  // ── timeline row edit / delete (own entries only) ───────────────────────
  const startEdit = (x) => {
    setComposer(null); setView(null);
    setEditing({
      type: x.type, id: x.id, text: x.text || '',
      next_at: x.next_at ? toLocalInput(new Date(x.next_at)) : '',
      sdate: x.sdate || '', stime: x.stime || '', location: x.location || '',
    });
  };
  const saveEdit = () => {
    const e = editing;
    if (!e) return;
    if (e.type === 'contact') {
      if (!e.text.trim()) { toast.error(t('请填写内容', 'Write something')); return; }
      runAction(() => coachDailyContactsAPI.patchEntry(e.id, { notes: e.text.trim(), next_follow_up_at: e.next_at ? laInputToUTCISO(e.next_at) : null }), t("已更新", "Updated"));
    } else if (e.type === 'note') {
      if (!e.text.trim()) { toast.error(t('请填写内容', 'Write something')); return; }
      runAction(() => profilesAPI.updateProfileNote(profileId, e.id, e.text.trim()), t("已更新", "Updated"));
    } else if (e.type === 'date') {
      if (!e.sdate || !e.stime) { toast.error(t('请填写日期和时间', 'Date and time are required')); return; }
      runAction(() => datingScheduleAPI.update(e.id, { scheduled_date: e.sdate, scheduled_time: e.stime, location: e.location || null }), t("已更新", "Updated"));
    }
  };
  const removeItem = (x) => {
    const label = { contact: t('这条打卡', 'this check-in'), note: t('这条客户情况', 'this note'),
      date: t('这场约会', 'this date'), match: t('这条推荐', 'this recommendation') }[x.type];
    // eslint-disable-next-line no-alert
    if (!window.confirm(t(`确定删除${label}？删除后无法恢复。`, `Delete ${label}? This cannot be undone.`))) return;
    const fn = {
      contact: () => coachDailyContactsAPI.removeEntry(x.id),
      note: () => profilesAPI.deleteProfileNote(profileId, x.id),
      date: () => datingScheduleAPI.delete(x.id),
      match: () => matchesAPI.delete(x.id),
    }[x.type];
    if (!fn) return;
    runAction(fn, t('已删除', 'Deleted'));
  };

  // ── 5.0 打卡 form pieces (prototype Contact) ──────────────────────────────
  // Themed select: no OS chrome, burgundy chevron, same height/radius as the chips below.
  const fieldSel = {
    appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
    border: `1px solid ${APP.line}`, borderRadius: 2, height: 38, padding: '0 34px 0 12px', fontSize: 13.5,
    fontFamily: 'inherit', color: APP.ink, width: '100%', boxSizing: 'border-box', cursor: 'pointer',
    backgroundColor: '#FFFDF9',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23111111' stroke-width='2.4' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
  };
  // Pill choice used for 联系目的 / 方式 (theme colours instead of a native <select>).
  const choiceChip = (on) => ({
    height: 32, padding: '0 12px', borderRadius: 2, fontFamily: 'inherit', fontSize: 13, cursor: 'pointer',
    border: `1px solid ${on ? APP.burgundy : APP.line}`, background: on ? APP.burgundy : '#FFFDF9',
    color: on ? '#FFFDF9' : APP.ink, fontWeight: on ? 500 : 400, whiteSpace: 'nowrap',
  });
  const lastH = hoursSince(svc && svc.last_contact_at);
  // Red 「必填」 marker (prototype Contact).
  const reqTag = { fontSize: 11, letterSpacing: '.06em', color: '#9E2B25', border: '1px solid #9E2B25', padding: '0 5px', borderRadius: 2 };
  const renderContactHead = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 10 }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 500, color: APP.ink }}>
          {cTarget ? t(`交叉电话 · ${cTarget.name || `#${cTarget.id}`}`, `Cross call · ${cTarget.name || `#${cTarget.id}`}`) : name}
        </div>
        <div style={{ fontSize: 12, color: APP.muted, marginTop: 1 }}>
          {cTarget
            ? t('这条记录会记在对方会员名下，作为本次约会的见面前沟通。', 'Recorded on the partner’s profile as the pre-meeting contact for this date.')
            : [name, lastH != null ? t(`上次联系 ${lastH} 小时前`, `last contact ${lastH}h ago`) : (svc ? t('还没有联系记录', 'never contacted') : '')].filter(Boolean).join(' · ')}
        </div>
      </div>
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={{ fontSize: 12, color: APP.muted, padding: 0, marginBottom: 5 }}>{t('联系目的', 'Purpose')}</legend>
        <div role="radiogroup" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {PURPOSES.map(([v, zh, en]) => (
            <button key={v} type="button" role="radio" aria-checked={cPurpose === v} onClick={() => setCPurpose(v)} style={choiceChip(cPurpose === v)}>
              {isZh ? zh : en}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={{ fontSize: 12, color: APP.muted, padding: 0, marginBottom: 5 }}>{t('方式', 'Method')}</legend>
        <div role="radiogroup" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {METHODS.map(([v, zh, en]) => (
            <button key={v} type="button" role="radio" aria-checked={cMethod === v} onClick={() => setCMethod(v)} style={choiceChip(cMethod === v)}>
              {isZh ? zh : en}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend style={{ fontSize: 12, color: APP.muted, padding: 0, marginBottom: 5 }}>{t('结果', 'Outcome')}</legend>
        <div role="radiogroup" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
          {OUTCOMES.map(([v, zh, en]) => {
            const on = cOutcome === v;
            const tone = v === 'two_way' ? { c: '#3F7D5A', bg: '#E6EEE8' } : { c: '#93591A', bg: '#F3EADB' };
            return (
              <label key={v} style={{ display: 'flex', gap: 8, alignItems: 'center', border: `1px solid ${on ? tone.c : APP.line}`, background: on ? tone.bg : '#FFFDF9', borderRadius: 2, padding: '8px 10px', fontSize: 13, cursor: 'pointer', color: APP.ink }}>
                <input type="radio" name={`crm-outcome-${profileId}`} checked={on} onChange={() => setCOutcome(v)} style={{ accentColor: tone.c }} />
                {isZh ? zh : en}
              </label>
            );
          })}
        </div>
      </fieldset>
      <div style={{ fontSize: 12, color: APP.muted, marginBottom: -4 }}>{t('沟通内容', 'What was discussed')}</div>
    </div>
  );
  const renderContactTail = () => {
    const shotRequired = needsScreenshot(cMethod, cPurpose);
    const linkDate = MEETING_PURPOSES.includes(cPurpose);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
        <div style={{ fontSize: 11.5, color: '#6E685F' }}>
          {t('沟通了什么 · 会员反馈 · 下一步（至少 10 个字）。简单问候不算有效服务。', 'What was discussed · how the member responded · next step (10+ characters). A greeting is not a service contact.')}
          {draft.trim().length > 0 && draft.trim().length < 10 && <span style={{ color: '#9E2B25' }}> {draft.trim().length}/10</span>}
        </div>
        <div data-tour="ws-contact-shots">
          <div style={{ fontSize: 12, color: APP.muted, marginBottom: 5 }}>
            {t('上传照片', 'Photos')}{' '}
            {shotRequired
              ? <><span style={reqTag}>{t('必填', 'Required')}</span> <span>{screenshotHint(cMethod, isZh)}</span></>
              : <span>{t('（选填）', '(optional)')}</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {cImages.map((u, i) => (
              <div key={u + i} style={{ position: 'relative', width: 60, height: 80, borderRadius: 2, overflow: 'hidden', border: `1px solid ${APP.line}` }}>
                <img src={resolvePhotoUrl(u) || u} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button type="button" aria-label={t('移除截图', 'Remove')} onClick={() => setCImages(prev => prev.filter((_, j) => j !== i))}
                  style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,.6)', color: '#FFFDF9', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={11} /></button>
              </div>
            ))}
            <input ref={cFileRef} type="file" accept="image/*" multiple onChange={onContactFiles} style={{ display: 'none' }} />
            {cImages.length < 9 && (
              <button type="button" aria-label={t('添加截图', 'Add screenshot')} disabled={cUploading} onClick={() => cFileRef.current && cFileRef.current.click()}
                style={{ width: 60, height: 80, borderRadius: 2, border: `1px dashed ${shotRequired && !cImages.length ? '#9E2B25' : 'rgba(17,17,17,.25)'}`, background: '#FFFDF9', color: APP.muted, cursor: cUploading ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {cUploading ? '…' : <ImageIcon size={18} />}
              </button>
            )}
            {cImages.length < 9 && !demo && (
              <QrUploadButton profileId={profileId} target={{ kind: 'contact' }} isZh={isZh} variant="tile" tileW={60} tileH={80}
                where={`${name} · ${t('写联系记录', 'Contact log')} · ${purposeLabel(cPurpose, isZh) || t('联系记录照片', 'Photos')}`}
                onFiles={(urls) => appendCapped(setCImages, urls, 9, isZh)} />
            )}
          </div>
        </div>
        {linkDate && (
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: APP.muted }}>
            {t('关联哪次约会', 'Which date')}
            <select value={cSchedule} onChange={(e) => setCSchedule(e.target.value)} style={fieldSel}>
              <option value="">{t('请选择', 'Choose…')}</option>
              {meetRows.map(r => (
                <option key={r.id} value={String(r.id)}>{[r.partner, r.at, r.location].filter(Boolean).join(' · ')}</option>
              ))}
            </select>
          </label>
        )}
      </div>
    );
  };


  // Label above value: these rows carry long multi-option answers, and the
  // side-by-side kv() used for the short header facts squeezes them unreadably.
  const stackedKv = (label, value, key) => (
    <div key={key} style={{ padding: '7px 0', borderTop: `1px solid ${APP.line}` }}>
      <div style={{ fontSize: 11, color: APP.muted, letterSpacing: '.18em', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13.5, fontWeight: 400, color: APP.ink, lineHeight: 1.6, wordBreak: 'break-word' }}>{value || '—'}</div>
    </div>
  );

  const kv = (label, value, color) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, padding: '6px 0', borderTop: `1px solid ${APP.line}`, color: APP.muted }}>
      <span style={{ letterSpacing: '.12em' }}>{label}</span><b style={{ color: color || APP.ink, fontWeight: 500, textAlign: 'right' }}>{value || '—'}</b>
    </div>
  );

  // 档案资料: a curated set of fields in the order a coach reads them.
  // 择偶: everything the profile form files under the mate_* / want* family.
  const PROFILE_FIELD_IDS = [9, 107, 80, 90, 83, 149, 5, 109, 6];
  const { profileFacts, prefFacts } = useMemo(() => {
    const gcv = (profile && (profile.gcv || profile.custom_values)) || {};
    const byId = new Map(allFields.map(f => [f.id, f]));
    const val = (fid) => {
      const raw = gcv[fid] != null ? gcv[fid] : gcv[String(fid)];
      if (raw == null || raw === '' || raw === '-' || raw === 'null') return '';
      const out = formatFieldValue(byId.get(Number(fid)), raw, isZh);
      return /^prefer not/i.test(out) ? '' : out;
    };
    const labelOf = (f) => (isZh && f.label_zh ? f.label_zh : (f.label || f.name));

    const basics = [];
    const age = ageOf(profile?.date_of_birth);
    const g = String(profile?.gender || '').toLowerCase();
    if (g) basics.push([t('性别', 'Gender'), g === 'male' ? t('男', 'Male') : t('女', 'Female')]);
    if (age) basics.push([t('年龄', 'Age'), t(`${age} 岁`, `${age}`)]);
    const where = [profile?.location_city, profile?.location_state, profile?.location_country].filter(Boolean).join(' ');
    if (where) basics.push([t('地区', 'Location'), where]);
    PROFILE_FIELD_IDS.forEach(fid => {
      const v = val(fid);
      if (!v) return;
      const f = byId.get(fid);
      basics.push([f ? labelOf(f) : String(fid), v]);
    });

    const prefs = [];
    allFields
      .filter(f => /^(mate_|want|accept_location|lifesort)/.test(f.name))
      .forEach(f => {
        const v = val(f.id);
        if (v) prefs.push([labelOf(f), v]);
      });

    return { profileFacts: basics, prefFacts: prefs };
  }, [profile, allFields, isZh]);   // t() only reads isZh, which is a dep


  // 约见记录 — every date this client has, newest first, each tagged with which
  // meeting it was WITH THAT PERSON (第1次/第2次…). That per-partner counter is
  // the answer to 「客户有没有见第二次」: a partner stuck at 第1次 never got one.
  const meetRows = useMemo(() => {
    const rows = (schedules || []).map(sc => {
      const isC1 = Number(sc.client1_profile_id) === Number(profileId);
      const partnerId = isC1 ? sc.client2_profile_id : sc.client1_profile_id;
      const at = `${String(sc.scheduled_date || '').slice(0, 10)} ${String(sc.scheduled_time || '').slice(0, 5)}`.trim();
      return {
        id: sc.id,
        partnerId,
        partner: (isC1 ? nameOfProfile(sc, 'client2_') : nameOfProfile(sc, 'client1_')) || `#${partnerId}`,
        at,
        ts: new Date(`${String(sc.scheduled_date || '').slice(0, 10)}T${String(sc.scheduled_time || '00:00').slice(0, 5)}`).getTime() || 0,
        location: sc.location || '',
        status: sc.status,
        done: !!sc.feedback_at,
      };
    });
    // number each partner's meetings chronologically, then show newest first
    const seq = new Map();
    rows.slice().sort((a, b) => a.ts - b.ts).forEach(r => {
      const n = (seq.get(r.partnerId) || 0) + 1;
      seq.set(r.partnerId, n);
      r.nth = n;
    });
    const totals = seq;
    rows.forEach(r => { r.total = totals.get(r.partnerId) || 1; });
    return rows.sort((a, b) => b.ts - a.ts);
  }, [schedules, profileId]);

  // 约见记录 · 三个勾 (VIP 服务流程 5.0): 见面前 · 本会员 / 见面前 · 对方会员 /
  // 见面后 · 回访. A missing one is a link that opens 打卡 with the purpose and
  // the date already chosen; the partner's check is a 交叉电话 recorded on the
  // partner's profile. Nothing renders when meeting-checks is unavailable.
  const renderMeetChecks = (r) => {
    const m = meetChecks[r.id];
    if (!m || !m.checks) return null;
    const g = String(p.gender || '').toLowerCase();
    const selfWord = g === 'female' ? t('女方', 'her') : g === 'male' ? t('男方', 'him') : t('本会员', 'this member');
    const partnerWord = g === 'female' ? t('男方', 'him') : g === 'male' ? t('女方', 'her') : t('对方会员', 'the partner');
    const ended = !!m.ended;
    const partnerId = m.partner_profile_id || r.partnerId;
    const items = [
      { key: 'pre_self', label: t(`见面前 · ${selfWord}`, `Before · ${selfWord}`), c: m.checks.pre_self, can: !ended,
        open: () => openComposer('contact', { purpose: 'pre_meeting', scheduleId: r.id }) },
      { key: 'pre_partner', label: t(`见面前 · ${partnerWord}`, `Before · ${partnerWord}`), c: m.checks.pre_partner, can: !ended && !!partnerId,
        hint: t('（待交叉电话）', ' (cross call due)'),
        open: () => openComposer('contact', { purpose: 'cross_call', scheduleId: r.id, target: { id: partnerId, name: m.partner_label || r.partner } }) },
      { key: 'post_self', label: t('见面后 · 回访', 'After · follow-up'), c: m.checks.post_self, can: ended,
        open: () => openComposer('contact', { purpose: 'post_meeting', scheduleId: r.id }) },
    ];
    return (
      <div onClick={(e) => e.stopPropagation()}
        style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, borderTop: '1px solid rgba(17,17,17,.08)', paddingTop: 7, marginTop: 7, cursor: 'default' }}>
        {items.map(it => {
          const done = !!(it.c && it.c.done);
          if (done) {
            const when = it.key === 'post_self' && it.c.at ? t(`（${fmtMD(it.c.at)}）`, ` (${fmtMD(it.c.at)})`) : '';
            return (
              <span key={it.key} title={[it.c.by_name, fmtMD(it.c.at)].filter(Boolean).join(' · ')}
                style={{ display: 'inline-flex', gap: 5, alignItems: 'center', color: '#3F7D5A' }}>
                <CheckCircle2 size={14} />{it.label}{when}
              </span>
            );
          }
          if (it.can) {
            return (
              <button key={it.key} type="button" onClick={it.open}
                title={t('打开「写联系记录」，已选好联系目的和约会', 'Opens the contact form with the purpose and date chosen')}
                style={{ display: 'inline-flex', gap: 5, alignItems: 'center', color: '#9E2B25', background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12, cursor: 'pointer' }}>
                <Circle size={14} />{it.label}{it.hint || ''}
              </button>
            );
          }
          return (
            <span key={it.key} style={{ display: 'inline-flex', gap: 5, alignItems: 'center', color: '#6E685F' }}>
              <Circle size={14} />{it.label}
            </span>
          );
        })}
      </div>
    );
  };

  // 跟进记录 = 工单回访事件 + 教练打卡，按时间倒序合并.
  //
  // The two live in different systems and neither alone answers "这个客户被跟进
  // 得怎么样": the ticket events (kpi_v2_ticket_events type='followup') only
  // exist for clients who have a KPIv2 ticket, and the check-ins
  // (coach_daily_contacts) are the coach's own cadence — which already feeds
  // 下次联系 on the board. Merging is read-only on purpose: `followup` ticket
  // events carry SLA and 流程阶段 side effects, so a check-in must never be
  // written as one.
  const followupRows = useMemo(() => {
    const ticketRows = (followups || []).map(f => ({
      src: 'ticket',
      key: `t${f.id}`,
      at: f.created_at,
      title: f.body || f.ticket_title || t('回访', 'Follow-up'),
      meta: [f.ticket_no, f.actor_label || [f.actor_first_name, f.actor_last_name].filter(Boolean).join(' ')],
    }));
    const contactRows = (activity || []).filter(x => x.type === 'contact').map(x => {
      const k = CONTACT_KIND[x.kind] || CONTACT_KIND.other;
      // 5.0 records say what the contact was for, how, and whether the member
      // answered; legacy rows keep their kind label.
      const meta5 = x.purpose
        ? [purposeLabel(x.purpose, isZh), methodLabel(x.method, isZh), outcomeLabel(x.outcome, isZh)].filter(Boolean)
        : [isZh ? k.zh : k.en];
      return {
        src: 'contact',
        key: `c${x.id}`,
        at: x.contacted_at || x.at,
        title: x.text || t('联系打卡', 'Check-in'),
        meta: [...meta5, x.by],
        next_at: x.next_at || null,
      };
    });
    return [...ticketRows, ...contactRows]
      .filter(r => r.at)
      .sort((a, b) => new Date(b.at) - new Date(a.at));
  }, [followups, activity, isZh]);   // t() only reads isZh, which is a dep

  const renderRow = (x, i) => {
    const m = TL_META[x.type] || { Icon: StickyNote, color: '#6E685F', bg: 'rgba(17,17,17,.08)' };
    const RowIcon = m.Icon;
    let title = '';
    let sub = [];
    if (x.type === 'contact') {
      title = x.purpose
        ? `${purposeLabel(x.purpose, isZh)} · ${t('打卡', 'check-in')}`
        : t('电话联系 · 打卡', 'Contact check-in');
      if (x.outcome) sub.push(outcomeLabel(x.outcome, isZh));
      if (x.text) sub.push(`「${x.text}」`);
    } else if (x.type === 'match') {
      // 线下ID next to the name: it is the id staff quote to each other, so a
      // recommendation row is only identifiable with it. Comes from the
      // activity row, then an already-loaded peer profile, and finally the
      // internal id — the same fallback the candidate cards below use, so a
      // client without an offline id still shows something to quote.
      const oid = x.other_offline_id || peers[x.other_id]?.offline_id || x.other_id || null;
      title = (
        <>
          {t(`推荐:${x.other || ''}`, `Recommended ${x.other || ''}`)}
          {oid ? (
            <small style={{ color: APP.muted, fontWeight: 500, marginLeft: 6, fontFamily: APP.serif, fontStyle: 'italic' }}>#{oid}</small>
          ) : null}
        </>
      );
      if (x.pct) sub.push(`${Math.round(Number(x.pct))}%`);
      if (x.status) sub.push(matchStatusLabel(x.status, isZh));
    } else if (x.type === 'date') {
      // Same 名字 + #线下ID as the recommendation rows.
      const oid = x.other_offline_id || peers[x.other_id]?.offline_id || x.other_id || null;
      title = (
        <>
          {t(`约会:${x.other || '会员'}`, `Date with ${x.other || 'member'}`)}
          {oid ? (
            <small style={{ color: APP.muted, fontWeight: 500, marginLeft: 6, fontFamily: APP.serif, fontStyle: 'italic' }}>#{oid}</small>
          ) : null}
        </>
      );
      if (x.when) sub.push(x.when);
      if (x.location) sub.push(x.location);
      sub.push(x.hasFeedback ? t('已写反馈', 'feedback written') : t('待写反馈', 'feedback due'));
    } else {
      title = x.text || t('备注', 'Note');
    }
    if (x.by) sub.push(x.by);
    sub.push(fmtWhen(x.at, isZh));
    // Own entries get 编辑 / 删除. Ownership is decided on by_user_id from the
    // API, and the endpoints re-check it server-side — the buttons only hide
    // what the backend would refuse anyway.
    const mine = meId != null && Number(x.by_user_id) === Number(meId) && x.id != null;
    const canEdit = mine && ['contact', 'note', 'date'].includes(x.type);
    const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 11.5, fontWeight: 500 };
    return (
      <div key={i} onClick={() => openRecord(x.type, x, i)}
        style={{ display: 'flex', gap: 10, padding: '9px 0', borderBottom: '1px solid rgba(17,17,17,.08)', alignItems: 'flex-start', cursor: 'pointer' }}>
        <div style={{ width: 26, height: 26, borderRadius: 2, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: m.bg }}>
          <RowIcon size={14} color={m.color} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: x.type === 'date' && !x.hasFeedback ? '#93591A' : APP.ink, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{title}</span>
            {x.type === 'contact' && REVIEW_BADGE[x.review_status] && (
              <span style={{ fontSize: 10.5, fontWeight: 500, borderRadius: 2, padding: '1px 6px', color: REVIEW_BADGE[x.review_status][1], background: REVIEW_BADGE[x.review_status][2] }}>
                {REVIEW_BADGE[x.review_status][0]}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: APP.muted }}>{sub.filter(Boolean).join(' · ')}</div>
          {x.type === 'contact' && asArray(x.images).length > 0 && renderThumbs(asArray(x.images))}
          {x.type === 'contact' && x.next_at && (
            <div style={{ fontSize: 11.5, color: '#93591A', fontWeight: 500, marginTop: 2 }}>
              {t('下次联系', 'Next contact')}：{new Date(x.next_at).toLocaleString()}
            </div>
          )}
        </div>
        {mine && (
          <div style={{ display: 'flex', gap: 2, flex: '0 0 auto' }}>
            {canEdit && (
              <button type="button" onClick={(e) => { e.stopPropagation(); startEdit(x); }} style={{ ...iconBtn, color: APP.burgundy }}>
                {t('编辑', 'Edit')}
              </button>
            )}
            <button type="button" onClick={(e) => { e.stopPropagation(); removeItem(x); }} style={{ ...iconBtn, color: '#9E2B25' }}>
              {t('删除', 'Delete')}
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── action panels (replace the timeline area while `view` is set) ────────
  const panelBody = { minHeight: 240, maxHeight: '62vh', overflow: 'auto' };
  const input = { border: `1px solid ${APP.line}`, borderRadius: 2, padding: '7px 9px', fontSize: 13, fontFamily: 'inherit', minWidth: 0, background: APP.bg, color: APP.ink };
  const primaryBtn = (disabled) => ({ fontSize: 12.5, fontWeight: 400, letterSpacing: '.14em', padding: '10px 18px', borderRadius: 2, background: APP.burgundy, color: APP.paper, border: 'none', fontFamily: 'inherit', cursor: disabled ? 'wait' : 'pointer', opacity: disabled ? .7 : 1 });
  const pill = (bg) => ({ display: 'inline-flex', alignItems: 'center', fontSize: 11, fontWeight: 400, letterSpacing: '.12em', color: APP.paper, background: bg, borderRadius: 2, padding: '3px 8px', whiteSpace: 'nowrap' });
  const pctColor = (n) => (n >= 85 ? '#111111' : n >= 70 ? '#8A6D3B' : '#6E685F');
  const pickedList = Object.values(picked);

  // Search + 排序 + 筛选, shared by 推荐给他人 and 安排约会's 全部异性 tab.
  const candToolbar = () => {
    const sel = { ...input, padding: '6px 8px', fontSize: 12, cursor: 'pointer', background: '#FFFDF9' };
    const num = { ...input, padding: '6px 8px', fontSize: 12, width: 62 };
    return (
      <div style={{ marginBottom: 9 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={candSearch}
            onChange={(e) => {
              const v = e.target.value;
              setCandSearch(v);
              // Emptying the box puts the full pool back. Without this the grid
              // stayed stuck on the last search's handful of rows, which looked
              // like the picker only had a few candidates in it.
              if (!v.trim() && candSearch.trim()) loadCandidates('');
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') loadCandidates(candSearch.trim()); }}
            placeholder={t('搜索姓名 / 线下ID', 'Search name / offline ID')}
            style={{ ...input, flex: 1 }} />
          <button type="button" onClick={() => loadCandidates(candSearch.trim())}
            style={{ ...input, cursor: 'pointer', fontWeight: 400, letterSpacing: '.14em', color: APP.paper, background: APP.ink, borderColor: APP.ink }}>{t('搜索', 'Search')}</button>
          <button type="button" onClick={() => setShowFilters(v => !v)}
            style={{ ...input, cursor: 'pointer', fontWeight: 400, letterSpacing: '.12em', display: 'inline-flex', alignItems: 'center', gap: 5,
              color: filtersOn ? '#FFFDF9' : APP.burgundy, background: filtersOn ? APP.burgundy : '#FFFDF9', borderColor: filtersOn ? APP.burgundy : APP.line }}>
            <SlidersHorizontal size={13} />{t('筛选', 'Filter')}
          </button>
          <select value={candSort} onChange={(e) => setCandSort(e.target.value)} style={sel} title={t('排序', 'Sort')}>
            <option value="match">{t('按匹配度', 'By match %')}</option>
            <option value="ageAsc">{t('年龄从小到大', 'Age ↑')}</option>
            <option value="ageDesc">{t('年龄从大到小', 'Age ↓')}</option>
            <option value="newest">{t('最新加入', 'Newest')}</option>
          </select>
          <button type="button" onClick={() => setHideBottom(v => !v)}
            title={t('隐藏碰到会员或对方底线的候选人', 'Hide candidates who miss either side\'s bottom line')}
            style={{ ...input, cursor: 'pointer', fontWeight: 400, letterSpacing: '.12em', display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
              color: hideBottom ? APP.paper : APP.ink, background: hideBottom ? APP.ink : APP.bg, borderColor: hideBottom ? APP.ink : APP.line }}>
            <AlertTriangle size={13} strokeWidth={1.5} color={hideBottom ? APP.paper : WS.bad} />{hideBottom ? t('已隐藏碰底线', 'Hiding misses') : t('隐藏碰底线', 'Hide misses')}
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 14px', fontSize: 10.5, letterSpacing: '.18em', color: APP.muted, marginTop: 9 }}>
          <span>{t('点卡片 · 选人', 'CLICK A CARD · PICK')}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Eye size={12} strokeWidth={1.5} />{t('看档案 · 会员档案详情', 'PROFILE · FULL DETAILS')}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: WS.bad }} />{t('碰底线', 'BOTTOM LINE')}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: APP.gold }} />{t('不符合要求', 'PREFERENCE')}</span>
          <span>{t('详情 · 对比与操作', 'DETAILS · FIT & ACTIONS')}</span>
        </div>
        {showFilters && (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 8, padding: 9, border: `1px solid ${APP.line}`, borderRadius: 2, background: APP.alt }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: APP.muted }}>{t('年龄', 'Age')}</span>
            <input type="number" value={candFilters.ageMin} onChange={(e) => setCandFilters(f => ({ ...f, ageMin: e.target.value }))} placeholder={t('最小', 'min')} style={num} />
            <span style={{ color: APP.muted }}>–</span>
            <input type="number" value={candFilters.ageMax} onChange={(e) => setCandFilters(f => ({ ...f, ageMax: e.target.value }))} placeholder={t('最大', 'max')} style={num} />
            {/* 地区 split into three type-to-search boxes backed by datalists.
                Each list is drawn from the loaded pool and cascades, so the
                州 list only offers states inside the chosen country, etc. */}
            {[
              { key: 'country', zh: '国家', en: 'Country', opts: locationOptions.countries },
              { key: 'state', zh: '省 / 州', en: 'State', opts: locationOptions.states },
              { key: 'city', zh: '城市', en: 'City', opts: locationOptions.cities },
            ].map(loc => (
              <span key={loc.key} style={{ display: 'inline-flex', flex: '0 0 auto' }}>
                <input
                  list={`cand-loc-${loc.key}`}
                  value={candFilters[loc.key]}
                  onChange={(e) => setCandFilters(prev => {
                    const next = { ...prev, [loc.key]: e.target.value };
                    // Changing a broader level clears the narrower ones, or the
                    // leftover value would silently filter everything away.
                    if (loc.key === 'country') { next.state = ''; next.city = ''; }
                    if (loc.key === 'state') { next.city = ''; }
                    return next;
                  })}
                  placeholder={isZh ? loc.zh : loc.en}
                  style={{ ...input, padding: '6px 8px', fontSize: 12, width: 124 }}
                />
                <datalist id={`cand-loc-${loc.key}`}>
                  {loc.opts.map(v => {
                    const label = translateOptionLabel(v, isZh ? 'zh' : 'en');
                    return <option key={v} value={v}>{label !== v ? label : ''}</option>;
                  })}
                </datalist>
              </span>
            ))}
            {/* Each field is a type-to-search input backed by a datalist: the
                coach can either type freely or drop the list open and pick one
                of the values the profile form itself offers. */}
            {CAND_FILTER_FIELDS.map(f => {
              const opts = fieldOptions[f.fid] || [];
              return (
                <span key={f.key} style={{ display: 'inline-flex', flex: '0 0 auto' }}>
                  <input list={`cand-opt-${f.fid}`} value={candFilters[f.key]}
                    onChange={(e) => setCandFilters(prev => ({ ...prev, [f.key]: e.target.value }))}
                    placeholder={isZh ? f.zh : f.en}
                    style={{ ...input, padding: '6px 8px', fontSize: 12, width: 124 }} />
                  <datalist id={`cand-opt-${f.fid}`}>
                    {opts.map((o, i) => <option key={i} value={isZh ? (o.zh || o.en) : (o.en || o.zh)} />)}
                  </datalist>
                </span>
              );
            })}
            {filtersOn && (
              <button type="button" onClick={() => setCandFilters(EMPTY_FILTERS)}
                style={{ fontSize: 12, fontWeight: 500, padding: '6px 10px', borderRadius: 2, border: 'none', background: 'none', color: APP.burgundy, cursor: 'pointer' }}>
                {t('清空筛选', 'Clear')}
              </button>
            )}
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: APP.muted }}>
              {t(`共 ${visibleCandidates.length} 位`, `${visibleCandidates.length} shown`)}
            </span>
          </div>
        )}
      </div>
    );
  };

  // 测评报告 — grouped by what the questionnaire is FOR, because a coach opens
  // this either to read the 择偶定位 or to read the personality read-out, never
  // to browse a flat list. Pending / expired rows still show, greyed: knowing a
  // questionnaire was sent and never came back is itself the answer.
  const QUIZ_GROUPS = [
    { key: 'portrait', types: ['profile_preferences'], zh: '人生合伙人画像', en: 'Partner portrait' },
    { key: 'personality', types: ['love_language', 'attachment_style'], zh: '性格测评', en: 'Personality' },
    { key: 'other', types: null, zh: '其他问卷', en: 'Other' },
  ];
  // 付款记录 — 发票 / 分期收款 for this client. Read-only here: creating or
  // recording a payment stays in 账单 / 客户付款, this tab answers 「他付了多少、
  // 还欠多少、下次什么时候付」 without leaving the CRM modal.
  const payInvoices = (payData && payData.invoices) || [];
  const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  const renderPayRows = () => {
    const summary = payData?.paymentSummary || null;
    const live = payInvoices.filter(i => i.status !== 'cancelled');
    const paid = live.reduce((sum, i) => sum + Number(i.amount_paid || 0), 0);
    const due = live.filter(i => i.status !== 'paid' && i.status !== 'refunded')
      .reduce((sum, i) => sum + Math.max(0, Number(i.total || 0) - Number(i.amount_paid || 0)), 0);
    const nextAmt = Number(summary?.next_payment_amount || 0);
    const nextAt = summary?.next_payment_date ? parseCalDate(summary.next_payment_date) : null;
    const STATUS = {
      paid: { zh: '已付款', en: 'Paid', color: '#3F7D5A', bg: '#E6EEE8' },
      partial: { zh: '部分付款', en: 'Partial', color: '#93591A', bg: '#F3EADB' },
      pending: { zh: '待付款', en: 'Pending', color: '#93591A', bg: '#F3EADB' },
      overdue: { zh: '已逾期', en: 'Overdue', color: '#9E2B25', bg: '#F3E3E1' },
      draft: { zh: '草稿', en: 'Draft', color: APP.muted, bg: APP.alt },
      cancelled: { zh: '已取消', en: 'Cancelled', color: APP.muted, bg: APP.alt },
      refunded: { zh: '已退款', en: 'Refunded', color: '#3A3835', bg: '#EFEAE2' },
    };
    const cell = (label, value, color) => (
      <div style={{ flex: '1 1 120px', minWidth: 110, padding: '4px 16px 8px', borderLeft: `1px solid ${APP.line}` }}>
        <div style={{ fontSize: 11, color: APP.muted, fontWeight: 400, letterSpacing: '.2em' }}>{label}</div>
        <div style={{ fontFamily: APP.serif, fontSize: 26, fontWeight: 500, lineHeight: 1.1, color: color || APP.ink, marginTop: 6 }}>{value}</div>
      </div>
    );

    return (
      <div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {cell(t('已付款', 'Paid'), money(paid), '#3F7D5A')}
          {cell(t('待付余额', 'Outstanding'), money(due), due > 0 ? '#9E2B25' : APP.ink)}
          {cell(t('账单数', 'Invoices'), String(payInvoices.length))}
          {nextAmt || nextAt
            ? cell(t('下次付款', 'Next payment'),
                `${nextAmt ? money(nextAmt) : ''}${nextAt ? `${nextAmt ? ' · ' : ''}${nextAt.toLocaleDateString()}` : ''}`,
                '#93591A')
            : null}
        </div>

        {payInvoices.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {payInvoices.map(inv => {
              const st = STATUS[inv.status] || STATUS.draft;
              const title = inv.plan_name || inv.membership_plan_name
                || (inv.invoice_type ? String(inv.invoice_type).replace(/_/g, ' ') : t('账单', 'Invoice'));
              const dueDate = parseCalDate(inv.due_date);
              const meta = [
                inv.invoice_number,
                inv.paid_at ? `${t('付款', 'Paid')} ${fmtWhen(inv.paid_at, isZh)}` : (dueDate ? `${t('到期', 'Due')} ${dueDate.toLocaleDateString()}` : ''),
                inv.payment_method || inv.offline_payment_type || '',
              ].filter(Boolean);
              return (
                <div key={inv.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 2, background: '#FFFDF9', border: `1px solid ${APP.line}` }}>
                  <CreditCard size={15} color={APP.burgundy} style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: APP.ink, overflowWrap: 'anywhere' }}>{title}</div>
                    <div style={{ fontSize: 11.5, color: APP.muted, marginTop: 1, overflowWrap: 'anywhere' }}>{meta.join(' · ')}</div>
                  </div>
                  <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: APP.ink }}>{money(inv.total)}</div>
                    {Number(inv.amount_paid || 0) > 0 && Number(inv.amount_paid) < Number(inv.total) && (
                      <div style={{ fontSize: 11, color: APP.muted, marginTop: 1 }}>
                        {t('已付', 'Paid')} {money(inv.amount_paid)}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: 10.5, fontWeight: 500, borderRadius: 2, padding: '2px 6px', flex: '0 0 auto', color: st.color, background: st.bg }}>
                    {isZh ? st.zh : st.en}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ padding: '1.8rem 1rem', textAlign: 'center' }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: APP.ink, marginBottom: 4 }}>
              {t('该客户还没有付款记录', 'No payment records yet')}
            </div>
            <div style={{ fontSize: 12, color: APP.muted }}>
              {t('在「账单」里为该客户开出账单后，付款情况会显示在这里。', 'Create an invoice for this client in 账单 and payments will show up here.')}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderQuizRows = () => {
    const known = QUIZ_GROUPS.flatMap(g => g.types || []);
    const groupOf = (r) => {
      const ty = String(r.questionnaire_type || '');
      const g = QUIZ_GROUPS.find(x => x.types && x.types.includes(ty));
      return g ? g.key : (known.includes(ty) ? 'other' : 'other');
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {QUIZ_GROUPS.map(g => {
          const rows = quizRows.filter(r => groupOf(r) === g.key);
          if (!rows.length) return null;
          return (
            <div key={g.key}>
              <div style={{ fontSize: 12, fontWeight: 500, color: APP.muted, letterSpacing: '.03em', marginBottom: 6 }}>
                {isZh ? g.zh : g.en}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {rows.map(r => {
                  const done = r.status === 'completed';
                  const when = r.submitted_at || r.created_at;
                  const title = (isZh ? r.questionnaire_title_zh : r.questionnaire_title)
                    || r.questionnaire_title_zh || r.questionnaire_title || `#${r.questionnaire_id}`;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      disabled={!done}
                      onClick={() => done && setQuizOpen(r.id)}
                      title={done ? t('查看结果', 'View result') : t('客户还没有提交', 'Not submitted yet')}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', width: '100%',
                        padding: '9px 11px', borderRadius: 2, background: done ? '#FFFDF9' : APP.alt,
                        border: `1px solid ${APP.line}`, cursor: done ? 'pointer' : 'default',
                        opacity: done ? 1 : 0.72, fontFamily: 'inherit',
                      }}
                    >
                      <ClipboardList size={15} color={done ? APP.burgundy : '#A39E95'} style={{ flexShrink: 0 }} />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: APP.ink, overflowWrap: 'anywhere' }}>{title}</span>
                        <span style={{ display: 'block', fontSize: 11.5, color: APP.muted, marginTop: 1 }}>
                          {done
                            ? `${t('已提交', 'Submitted')} · ${fmtWhen(when, isZh)}`
                            : `${t('已发送', 'Sent')} · ${fmtWhen(r.created_at, isZh)}`}
                          {r.client_confirmed_at ? ` · ${t('客户已确认', 'Client confirmed')}` : ''}
                        </span>
                      </span>
                      <span style={{
                        fontSize: 10.5, fontWeight: 500, borderRadius: 2, padding: '2px 6px', flex: '0 0 auto',
                        color: done ? '#3F7D5A' : '#6E685F', background: done ? '#E6EEE8' : '#EFEAE2',
                      }}>
                        {done ? t('已完成', 'Done') : t('待提交', 'Pending')}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // The candidate grid, shared by 推荐给他人 (multi-select + the four actions)
  // and 安排约会's 全部异性 tab (single-select: the card IS the partner picker).

  // 底线提醒 in the Luxe editorial language: a letter-spaced kicker per
  // direction (会员的要求 / 对方的要求), then one hairline row per miss —
  //   年龄            30–37 岁   ≠   69 岁
  //   底线 / 要求       要求            实际
  // No filled badges: the 底线 / 要求 tone lives only in the small caps
  // under the item and in the 实际 value.
  const smallCaps = (size = 10.5, extra = {}) => ({ fontSize: size, fontWeight: 400, letterSpacing: '.2em', color: APP.muted, ...extra });
  const renderHitTable = (bh, { big = false, memberName, theirName } = {}) => {
    const blocks = [
      ['member', bh && bh.member, memberName ? t(`${memberName} 的要求`, `${memberName} wants`) : t('会员的要求', 'Client wants'),
        theirName || t('对方', 'Them')],
      ['partner', bh && bh.partner, theirName ? t(`${theirName} 的要求`, `${theirName} wants`) : t('对方的要求', 'They want'),
        memberName || t('会员', 'Client')],
    ].filter(([, hits]) => hits && hits.length);
    if (!blocks.length) return null;
    const cols = big ? '108px minmax(0,1fr) 22px minmax(0,1fr)' : '46px minmax(0,1fr) 16px minmax(0,1fr)';
    const val = big ? 14 : 12.5;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: big ? 16 : 10 }}>
        {blocks.map(([side, hits, title, haveTitle]) => (
          <div key={side} style={{ borderTop: `1px solid ${APP.ink}` }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: big ? '8px 0 6px' : '6px 0 4px' }}>
              <span style={smallCaps(big ? 11.5 : 10.5, { color: APP.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}>{title}</span>
              <span style={{ flex: 1 }} />
              <span style={{ fontFamily: APP.serif, fontStyle: 'italic', fontSize: big ? 13 : 11.5, color: APP.muted, whiteSpace: 'nowrap' }}>
                {t(`${hits.length} 项不符`, `${hits.length} off`)}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: cols, alignItems: 'baseline', paddingBottom: 3 }}>
              <span />
              <span style={smallCaps(big ? 10.5 : 9.5)}>{t('要求', 'ASKS')}</span>
              <span />
              <span style={smallCaps(big ? 10.5 : 9.5, { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' })}>{big ? haveTitle : t('实际', 'IS')}</span>
            </div>
            {hits.map(h => {
              const red = h.level === 'bottom';
              const tone = red ? WS.bad : APP.gold;
              return (
                <div key={h.key} style={{ display: 'grid', gridTemplateColumns: cols, alignItems: 'baseline',
                  borderTop: `1px solid ${APP.line}`, padding: big ? '8px 0' : '6px 0' }}>
                  <div>
                    <div style={{ fontSize: big ? 13 : 12, color: APP.ink, letterSpacing: '.08em' }}>{h.label}</div>
                    <div style={smallCaps(big ? 10 : 9, { color: tone, marginTop: 1 })}>{red ? t('底线', 'LIMIT') : t('要求', 'PREF')}</div>
                  </div>
                  <div style={{ fontFamily: APP.serif, fontStyle: 'italic', fontSize: val, color: APP.muted, overflowWrap: 'anywhere', lineHeight: 1.35 }}>{h.want}</div>
                  <div style={{ fontFamily: APP.serif, fontSize: val, color: APP.gold, textAlign: 'center' }}>≠</div>
                  <div style={{ fontFamily: APP.serif, fontSize: val, fontWeight: 500, color: red ? WS.bad : APP.ink, overflowWrap: 'anywhere', lineHeight: 1.35 }}>{h.have}</div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  // Ivory chip laid over a photo — the Luxe tag look (no saturated fills).
  const photoChip = (color) => ({ position: 'absolute', display: 'inline-flex', alignItems: 'center', fontSize: 10.5, fontWeight: 400, letterSpacing: '.18em',
    color, background: 'rgba(255,253,249,.94)', borderRadius: 2, padding: '4px 8px', whiteSpace: 'nowrap', boxShadow: 'none' });
  // Everything the picker needs to know about one candidate, shared by the
  // grid tile and the 详情 quick view.
  const candInfo = (c, mode) => {
    const linked = mode === 'recommend' && !!c.existing_match_id;
    const st = MATCH_STATUS.find(x => x[0] === String(c.existing_match_status || ''));
    const age = ageFromDob(c.date_of_birth || c.birth_date);
    return {
      on: mode === 'schedule' ? String(form.partnerId) === String(c.id) : !!picked[c.id],
      pct: Math.round(Number(c.matchPercentage) || 0),
      nm: nameOfProfile(c) || `#${c.id}`,
      // Already on a line with this client (any matchmaker's — createMatch
      // refuses the duplicate pair either way). Still listed so a search finds
      // the person; it just can't be recommended a second time.
      linked,
      // 「与 陈医生 已匹配过」 — naming the client tells the coach why this
      // member can't be picked again.
      linkedLabel: t(`与 ${name} 已匹配过`, `Already matched with ${name}`),
      linkedStatus: st ? (isZh ? st[1] : st[2]) : '',
      bh: bottomHits[c.id] || { member: [], partner: [], bottom: false },
      age,
      where: c.location_city || c.location_state || '',
    };
  };
  const pickCand = (c, mode) => (mode === 'schedule' ? setForm(f => ({ ...f, partnerId: c.id })) : togglePick(c));

  // The full action row, shown in the 详情 quick view.
  const candActions = (c, mode) => {
    const { on, linked, linkedLabel } = candInfo(c, mode);
    const busy = candBusy === c.id;
    const call = candCall[c.id];
    const act = (label, Icon, onClick, tone) => (
      <button type="button" disabled={busy}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
          flex: '1 1 auto', fontSize: 12, fontWeight: 400, letterSpacing: '.12em', padding: '8px 10px', borderRadius: 2,
          border: `1px solid ${tone ? tone : APP.line}`, fontFamily: 'inherit',
          background: tone || 'transparent', color: tone ? APP.paper : APP.ink,
          cursor: busy ? 'wait' : 'pointer', whiteSpace: 'nowrap',
        }}>
        <Icon size={13} strokeWidth={1.5} />{label}
      </button>
    );
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {mode === 'schedule' ? (
          <>
            {act(on ? t('已选中', 'Selected') : t('选这位', 'Pick'), Check, () => pickCand(c, mode), on ? APP.ink : undefined)}
            {act(t('档案', 'Profile'), Eye, () => setDetailId(c.id))}
            {act(t('相亲卡', 'Card'), CreditCard, () => openCard(c))}
            {act(t('速记卡对照', 'Compare cards'), ClipboardList, () => setQcCompare(c))}
          </>
        ) : (
          <>
            {!linked && act(on ? t('已选 · 取消', 'Selected · undo') : t('选这位', 'Pick'), Check, () => pickCand(c, mode), on ? APP.ink : undefined)}
            {linked
              ? act(t('已匹配过', 'Matched'), Link2, () => toast.info(linkedLabel))
              : act(t('签线', 'Sign'), Link2, () => signLine(c))}
            {act(t('约见', 'Meet'), CalendarHeart, () => meetCandidate(c))}
            {act(t('档案', 'Profile'), Eye, () => setDetailId(c.id))}
            {act(t('相亲卡', 'Card'), CreditCard, () => openCard(c))}
            {act(t('速记卡对照', 'Compare cards'), ClipboardList, () => setQcCompare(c))}
            {act(call ? t('复制邀请', 'Copy invite') : t('打电话', 'Call'), PhoneCall,
              () => (call ? copyText(shareBlock(call), t('邀请内容已复制', 'Invitation copied'), t('复制邀请内容', 'Copy the invitation')) : callCandidate(c)))}
          </>
        )}
      </div>
    );
  };

  // 简约网格 — an index page of portraits: a square photo, the name with the
  // match % beside it, age · city, and one dot for the fit (red = 碰底线,
  // gold = 不符合要求). Clicking the tile picks; 详情 opens the quick view with
  // the 底线对比 and every action, 看档案 the full profile.
  // The misses spelled out on the tile itself, one line each:
  //   年龄 底线  20–27 岁 ✕ 69 岁
  // grouped by whose requirement it is. Red = 碰底线, gold = 不符合要求.
  const renderTileHits = (bh) => {
    const groups = [
      ['member', bh.member, t(`${name || '会员'}的要求`, `${name || 'Client'} wants`)],
      ['partner', bh.partner, t('对方的要求', 'They want')],
    ].filter(([, hits]) => hits && hits.length);
    if (!groups.length) return null;
    return (
      <div style={{ marginTop: 7, display: 'flex', flexDirection: 'column', gap: 5 }}>
        {groups.map(([side, hits, title]) => (
          <div key={side} style={{ minWidth: 0 }}>
            <div style={smallCaps(9, { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 2 })}>{title}</div>
            {hits.map(h => {
              const red = h.level === 'bottom';
              const tone = red ? WS.bad : APP.gold;
              return (
                <div key={h.key} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 4, rowGap: 0,
                  fontSize: 11.5, lineHeight: 1.4, padding: '2px 0', borderTop: `1px solid ${APP.line}` }}>
                  <span style={{ color: APP.ink, letterSpacing: '.06em' }}>{h.label}</span>
                  <span style={smallCaps(8.5, { color: tone, letterSpacing: '.14em' })}>{red ? t('底线', 'LIMIT') : t('要求', 'PREF')}</span>
                  <span style={{ fontFamily: APP.serif, fontStyle: 'italic', color: APP.muted, overflowWrap: 'anywhere' }}>{h.want}</span>
                  <span style={{ color: tone }}>✕</span>
                  <span style={{ fontFamily: APP.serif, fontWeight: 500, color: red ? WS.bad : APP.ink, overflowWrap: 'anywhere' }}>{h.have}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const renderCandidateCards = (list, mode = 'recommend') => {
    const totalPages = Math.max(1, Math.ceil(list.length / CAND_PAGE_SIZE));
    const safePage = Math.min(Math.max(1, candPage), totalPages);
    const pageStart = (safePage - 1) * CAND_PAGE_SIZE;
    const pageRows = list.slice(pageStart, pageStart + CAND_PAGE_SIZE);
    const pagerBtn = (disabled) => ({
      ...input, padding: '6px 12px', fontSize: 12, fontWeight: 400, letterSpacing: '.12em',
      color: disabled ? '#A39E95' : APP.ink, background: 'transparent',
      cursor: disabled ? 'not-allowed' : 'pointer',
    });
    const link = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit',
      display: 'inline-flex', alignItems: 'center', gap: 3, ...smallCaps(9.5, { color: APP.ink }) };
    return (
    <>
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${candCols}, minmax(0, 1fr))`, gap: '22px 14px', padding: '4px 0 6px' }}>
      {pageRows.map(c => {
        const { on, pct, nm, linked, linkedLabel, bh, age, where } = candInfo(c, mode);
        const misses = bh.member.length + bh.partner.length;
        const dot = bh.bottom ? WS.bad : misses ? APP.gold : null;
        return (
          <div key={c.id} onClick={() => { if (!linked) pickCand(c, mode); }}
            title={linked ? t(`${linkedLabel}，不能重复推荐`, `${linkedLabel} — can't be recommended again`) : undefined}
            style={{ cursor: linked ? 'default' : 'pointer', minWidth: 0 }}>
            <div style={{ position: 'relative', width: '100%', paddingTop: '100%', background: photoOf(c) ? APP.alt : avColor(c.id),
              outline: on ? `2px solid ${APP.ink}` : 'none', outlineOffset: 3, opacity: linked ? .55 : 1 }}>
              {photoOf(c)
                ? <img src={photoOf(c)} alt="" loading="lazy"
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                : <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: APP.paper, fontFamily: APP.serif, fontSize: 40, fontWeight: 500 }}>{(nm[0] || '?').toUpperCase()}</span>}
              {linked && <span title={linkedLabel} style={{ ...photoChip(WS.warn), left: 6, top: 6, fontSize: 9.5, padding: '3px 6px' }}>{t('已匹配过', 'MATCHED')}</span>}
              {!linked && (
                <span style={{
                  position: 'absolute', top: 6, right: 6, width: 20, height: 20, borderRadius: 2,
                  border: `1px solid ${on ? APP.ink : 'rgba(255,253,249,.95)'}`, background: on ? APP.ink : 'rgba(17,17,17,.18)',
                  color: APP.paper, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {on ? <Check size={12} strokeWidth={3} /> : null}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 9, minWidth: 0 }}>
              <span style={{ flex: 1, minWidth: 0, fontFamily: APP.serif, fontSize: 16, fontWeight: 500, lineHeight: 1.2, color: APP.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nm}</span>
              {pct > 0 && <span style={{ flex: '0 0 auto', fontFamily: APP.serif, fontStyle: 'italic', fontSize: 14, color: pctColor(pct) }}>{pct}%</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, minWidth: 0 }}>
              <span style={{ flex: 1, minWidth: 0, ...smallCaps(10), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {[age ? t(`${age} 岁`, `${age}`) : null, where || null].filter(Boolean).join(' · ') || `№ ${c.offline_id || c.id}`}
              </span>
              {dot && (
                <span title={bh.bottom ? t(`碰底线 · ${misses} 项不符`, `Bottom line · ${misses} off`) : t(`${misses} 项不符合要求`, `${misses} preference(s) off`)}
                  style={{ flex: '0 0 auto', width: 7, height: 7, borderRadius: '50%', background: dot }} />
              )}
            </div>
            {renderTileHits(bh)}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 7, paddingTop: 6, borderTop: `1px solid ${APP.line}` }}>
              <button type="button" onClick={(e) => { e.stopPropagation(); setQuickView({ c, mode }); }} style={link}>
                {t('详情', 'DETAILS')}<ChevronRight size={11} strokeWidth={1.5} />
              </button>
              <button type="button" onClick={(e) => { e.stopPropagation(); setDetailId(c.id); }} style={link} title={t('查看会员档案详情', 'View profile details')}>
                <Eye size={11} strokeWidth={1.5} />{t('看档案', 'PROFILE')}
              </button>
            </div>
          </div>
        );
      })}
    </div>
    {list.length > CAND_PAGE_SIZE && (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap', padding: '16px 0 2px' }}>
        <button type="button" disabled={safePage <= 1} onClick={() => setCandPage(safePage - 1)} style={pagerBtn(safePage <= 1)}>
          {t('上一页', 'Prev')}
        </button>
        <span style={{ ...smallCaps(10.5) }}>
          {t(`第 ${safePage} / ${totalPages} 页 · 共 ${list.length} 位`, `PAGE ${safePage} / ${totalPages} · ${list.length} TOTAL`)}
        </span>
        <button type="button" disabled={safePage >= totalPages} onClick={() => setCandPage(safePage + 1)} style={pagerBtn(safePage >= totalPages)}>
          {t('下一页', 'Next')}
        </button>
      </div>
    )}
    </>
    );
  };

  // 详情 quick view of one candidate: portrait, the facts, the 底线对比 and
  // every action — what used to crowd the card.
  const renderQuickView = () => {
    if (!quickView) return null;
    const { mode } = quickView;
    // the row may have been reloaded (e.g. after 签线) — prefer the fresh one
    const c = (candidates || []).find(x => String(x.id) === String(quickView.c.id)) || quickView.c;
    const { pct, nm, linked, linkedLabel, linkedStatus, bh } = candInfo(c, mode);
    const meta = candidateMeta(c, isZh);
    const hasHits = bh.member.length || bh.partner.length;
    return dialog(nm, () => setQuickView(null), (
      <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '240px minmax(0,1fr)', gap: 20 }}>
        <div>
          <div style={{ position: 'relative', width: '100%', paddingTop: '125%', background: photoOf(c) ? APP.alt : avColor(c.id) }}>
            {photoOf(c)
              ? <img src={photoOf(c)} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              : <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: APP.paper, fontFamily: APP.serif, fontSize: 64 }}>{(nm[0] || '?').toUpperCase()}</span>}
          </div>
          {pct > 0 && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 10 }}>
              <span style={{ fontFamily: APP.serif, fontStyle: 'italic', fontSize: 32, color: APP.ink, lineHeight: 1 }}>{pct}%</span>
              <span style={smallCaps(10)}>{t('匹配度', 'MATCH')}</span>
            </div>
          )}
        </div>
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={smallCaps(10.5)}>
              № {c.offline_id || c.id}{' · '}{c.coach_name ? t(`负责教练 ${c.coach_name}`, `COACH ${c.coach_name}`) : t('未分配教练', 'NO COACH')}
            </div>
            {meta.facts && <div style={{ fontSize: 13, color: APP.ink, lineHeight: 1.6, marginTop: 6 }}>{meta.facts}</div>}
            {linked && (
              <div style={{ fontSize: 12, color: WS.warn, marginTop: 6 }}>
                {linkedLabel}{linkedStatus ? ` · ${linkedStatus}` : ''}
                {c.existing_match_by ? ` · ${c.existing_match_mine ? t('我推荐的', 'by me') : c.existing_match_by}` : ''}
                {c.existing_match_at ? ` · ${fmtWhen(c.existing_match_at, isZh)}` : ''}
              </div>
            )}
          </div>
          {meta.detail.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px 14px', borderTop: `1px solid ${APP.line}`, paddingTop: 10 }}>
              {meta.detail.map(([k, v]) => (
                <div key={k} style={{ minWidth: 0 }}>
                  <div style={smallCaps(9.5)}>{k}</div>
                  <div style={{ fontSize: 13, color: APP.ink, overflowWrap: 'anywhere' }}>{v}</div>
                </div>
              ))}
            </div>
          )}
          {hasHits
            ? renderHitTable(bh, { big: true, memberName: name, theirName: nm })
            : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, borderTop: `1px solid ${APP.ink}`, paddingTop: 10, ...smallCaps(11, { color: WS.ok }) }}>
                <Check size={13} strokeWidth={1.5} />{t('双方要求无冲突', 'NO CONFLICT')}
              </div>
            )}
          <div style={{ borderTop: `1px solid ${APP.line}`, paddingTop: 12 }}>{candActions(c, mode)}</div>
        </div>
      </div>
    ), 880);
  };

  // ── 时间线 as a Gantt ───────────────────────────────────────────────────
  // One lane per record kind, a real time axis underneath, every bar clickable.
  // Point-in-time records (打卡 / 推荐 / 备注) get a minimum-width bar so they
  // stay hittable on a months-long axis; 约会 uses its scheduled duration.
  const GANTT_LANES = [
    { kind: 'contact', label: t('打卡', 'Check-ins') },
    { kind: 'match', label: t('推荐', 'Recommendations') },
    { kind: 'date', label: t('约会', 'Dates') },
    { kind: 'note', label: t('备注', 'Notes') },
  ];

  const renderGantt = (rows) => {
    const evs = (rows || [])
      .map(x => ({ ...x, _t: x.at ? new Date(x.at).getTime() : 0 }))
      .filter(x => x._t);
    if (!evs.length) return null;

    const now = Date.now();
    const times = evs.map(x => x._t).concat(now);
    const pad = 2 * 86400000;
    const t0 = Math.min(...times) - pad;
    const t1 = Math.max(...times) + pad;
    const span = Math.max(1, t1 - t0);
    const days = span / 86400000;
    // wide enough that a busy month is still readable; the frame scrolls
    const innerW = Math.max(560, Math.min(2400, Math.round(days * (days > 240 ? 3 : days > 90 ? 8 : 26))));
    const x = (ms) => ((ms - t0) / span) * innerW;

    // ticks: months on long spans, weeks in between, days when it is short
    const ticks = [];
    const step = days > 150 ? 'month' : days > 35 ? 'week' : 'day';
    const cur = new Date(t0);
    cur.setHours(0, 0, 0, 0);
    if (step === 'month') cur.setDate(1);
    if (step === 'week') cur.setDate(cur.getDate() - cur.getDay());
    for (let i = 0; i < 400; i++) {
      const ms = cur.getTime();
      if (ms > t1) break;
      if (ms >= t0) {
        ticks.push({
          ms,
          label: step === 'month'
            ? (isZh ? `${cur.getMonth() + 1}月` : cur.toLocaleDateString('en-US', { month: 'short' }))
            : (isZh ? `${cur.getMonth() + 1}/${cur.getDate()}` : `${cur.getMonth() + 1}/${cur.getDate()}`),
        });
      }
      if (step === 'month') cur.setMonth(cur.getMonth() + 1);
      else if (step === 'week') cur.setDate(cur.getDate() + 7);
      else cur.setDate(cur.getDate() + 1);
    }

    const ROW_H = 22;         // one sub-row inside a lane
    const BAR_H = 16;
    const MIN_W = 14;

    // Two records on the same day land on the same pixel and the second one
    // disappears behind the first. Pack each lane into sub-rows instead: a bar
    // takes the first row whose last bar ends before this one starts.
    const lanes = GANTT_LANES
      .filter(l => evs.some(e => e.type === l.kind))
      .map(l => {
        const items = evs.filter(e => e.type === l.kind).sort((a, b) => a._t - b._t);
        const rowEnds = [];   // right edge (px) of the last bar placed in each row
        const placed = items.map(e => {
          const durMs = e.type === 'date' ? 90 * 60000 : 0;
          const left = x(e._t);
          const w = Math.max(MIN_W, (durMs / span) * innerW);
          let row = rowEnds.findIndex(end => left >= end + 3);
          if (row === -1) { row = rowEnds.length; rowEnds.push(0); }
          rowEnds[row] = left + w;
          return { e, left, w, row };
        });
        return { ...l, placed, rows: Math.max(1, rowEnds.length) };
      });
    const laneTop = [];
    let acc = 0;
    lanes.forEach(l => { laneTop.push(acc); acc += l.rows * ROW_H + 8; });
    const chartH = Math.max(ROW_H + 8, acc);

    return (
      <div style={{ border: `1px solid ${APP.line}`, borderRadius: 2, overflow: 'hidden', marginBottom: 12, background: '#FFFDF9' }}>
        <div style={{ display: 'flex' }}>
          {/* lane labels stay put while the chart scrolls */}
          <div style={{ width: 72, flex: '0 0 auto', borderRight: `1px solid ${APP.line}`, background: APP.alt }}>
            <div style={{ height: 22 }} />
            {lanes.map((l, li) => (
              <div key={l.kind} style={{ height: l.rows * ROW_H + 8, display: 'flex', alignItems: 'center', paddingLeft: 8, fontSize: 11.5, fontWeight: 500, color: APP.muted }}>
                {l.label}
                {l.placed.length > 1 && <span style={{ marginLeft: 4, color: '#6E685F', fontWeight: 500 }}>{l.placed.length}</span>}
              </div>
            ))}
          </div>
          <div style={{ overflowX: 'auto', flex: 1, minWidth: 0 }}>
            <div style={{ width: innerW, position: 'relative' }}>
              {/* axis */}
              <div style={{ height: 22, position: 'relative', borderBottom: `1px solid ${APP.line}`, background: APP.alt }}>
                {ticks.map((tk, i) => (
                  <span key={i} style={{ position: 'absolute', left: x(tk.ms), top: 4, fontSize: 10, color: '#6E685F', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>
                    {tk.label}
                  </span>
                ))}
              </div>
              <div style={{ position: 'relative', height: chartH }}>
                {ticks.map((tk, i) => (
                  <div key={i} style={{ position: 'absolute', left: x(tk.ms), top: 0, bottom: 0, width: 1, background: 'rgba(17,17,17,.06)' }} />
                ))}
                {/* 今天 */}
                <div style={{ position: 'absolute', left: x(now), top: 0, bottom: 0, width: 1.5, background: '#9E2B25', zIndex: 2 }}>
                  <span style={{ position: 'absolute', top: -1, left: -3, width: 7, height: 7, borderRadius: '50%', background: '#9E2B25' }} />
                </div>
                {lanes.map((l, li) => (
                  <div key={l.kind} style={{ position: 'absolute', left: 0, right: 0, top: laneTop[li], height: l.rows * ROW_H + 8, borderBottom: li === lanes.length - 1 ? 'none' : '1px solid rgba(17,17,17,.06)' }}>
                    {l.placed.map(({ e, left, w, row }, i) => {
                      const m = TL_META[e.type] || { color: '#6E685F', bg: 'rgba(17,17,17,.08)' };
                      const label = e.type === 'match' || e.type === 'date' ? (e.other || '') : (e.text || '');
                      const when = new Date(e._t);
                      return (
                        <button key={i} type="button" onClick={() => openRecord(e.type, e)}
                          title={`${when.toLocaleString()}${label ? ` · ${label}` : ''}`}
                          style={{
                            position: 'absolute', left, top: 4 + row * ROW_H, height: BAR_H, minWidth: w,
                            maxWidth: 170, padding: '0 6px', borderRadius: 2, border: 'none', cursor: 'pointer',
                            background: m.color, color: '#FFFDF9', fontSize: 10.5, fontWeight: 500,
                            display: 'inline-flex', alignItems: 'center', overflow: 'hidden', whiteSpace: 'nowrap',
                            boxShadow: record && record.data === e ? '0 0 0 2px rgba(0,0,0,.35)' : 'none',
                          }}>
                          {label ? String(label).slice(0, 12) : ''}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── one detail panel for every kind of record ──────────────────────────
  const renderRecordDetail = () => {
    // 名字 #线下ID for the other member of a recommendation / date.
    const withOid = (d) => {
      const oid = d && (d.other_offline_id || peers[d.other_id]?.offline_id || d.other_id);
      return d ? `${d.other || (isZh ? '会员' : 'Member')}${oid ? ` #${oid}` : ''}` : '';
    };
    if (!record) return null;
    const d = record.data || {};
    // 推荐 / 约见 are about a second person; show them properly rather than
    // just printing their name on a key/value row.
    const peerId = (record.kind === 'match' || record.kind === 'date')
      ? (d.other_id || d.otherId || d.partnerId || null) : null;
    const peer = peerId ? peers[peerId] : null;
    const peerPhoto = peer ? photoOf(peer) : null;
    const peerMeta = peer ? candidateMeta(peer, isZh) : null;
    const canStatus = record.kind === 'match' || record.kind === 'date';
    const opts = record.kind === 'match'
      ? MATCH_STATUS.map(([v, zh, en]) => [v, isZh ? zh : en])
      : Object.entries(DATE_STATUS_LABEL).map(([v, o]) => [v, isZh ? o.zh : o.en]);
    const rows = [];
    const push = (k, v) => { if (v) rows.push([k, v]); };
    push(t('时间', 'When'), d.at ? new Date(d.at).toLocaleString() : (d.created_at ? new Date(d.created_at).toLocaleString() : ''));
    if (record.kind === 'contact') {
      const kd = CONTACT_KIND[d.kind] || CONTACT_KIND.other;
      push(t('方式', 'Type'), isZh ? kd.zh : kd.en);
      push(t('内容', 'Notes'), d.text);
      push(t('下次联系', 'Next contact'), d.next_at ? new Date(d.next_at).toLocaleString() : '');
    } else if (record.kind === 'match') {
      push(t('对象', 'With'), withOid(d));
      push(t('匹配度', 'Match'), d.pct ? `${Math.round(Number(d.pct))}%` : '');
    } else if (record.kind === 'date') {
      push(t('对象', 'With'), withOid(d));
      push(t('时间', 'Scheduled'), d.when);
      push(t('地点', 'Location'), d.location);
      push(t('反馈', 'Feedback'), d.hasFeedback ? t('已写', 'written') : t('待写', 'due'));
    } else {
      push(t('内容', 'Note'), d.text);
    }
    push(t('记录人', 'By'), d.by);

    return (
      <div style={{ border: `1px solid ${APP.line}`, borderRadius: 2, padding: 12, marginBottom: 12, background: APP.alt }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: APP.ink }}>
            {{ contact: t('打卡详情', 'Check-in'), match: t('推荐详情', 'Recommendation'),
               date: t('约见详情', 'Meeting'), note: t('备注详情', 'Note'),
               followup: t('跟进详情', 'Follow-up') }[record.kind] || t('详情', 'Detail')}
          </span>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={() => setRecord(null)}
            style={{ display: 'inline-flex', background: 'none', border: 'none', cursor: 'pointer', color: APP.muted, padding: 2 }}>
            <X size={15} />
          </button>
        </div>
        {peerId && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '4px 0 10px', borderBottom: `1px solid ${APP.line}`, marginBottom: 8 }}>
            <div style={{ width: 104, height: 104, borderRadius: 2, overflow: 'hidden', flex: '0 0 auto', background: peerPhoto ? '#EFEAE2' : avColor(peerId), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {peerPhoto
                ? <img src={peerPhoto} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                : <span style={{ color: '#FFFDF9', fontSize: 34, fontWeight: 500 }}>{(String(d.other || '?')[0] || '?').toUpperCase()}</span>}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: APP.ink }}>
                {d.other || (peer ? nameOfProfile(peer) : `#${peerId}`)}
                {peer?.offline_id && <small style={{ color: APP.muted, fontWeight: 500, marginLeft: 6, fontFamily: APP.serif, fontStyle: 'italic' }}>#{peer.offline_id}</small>}
              </div>
              {peerMeta ? (
                <>
                  {peerMeta.facts && <div style={{ fontSize: 12, color: APP.ink, marginTop: 3 }}>{peerMeta.facts}</div>}
                  {peerMeta.detail.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 5px', marginTop: 4 }}>
                      {peerMeta.detail.slice(0, 6).map(([k, v]) => (
                        <span key={k} style={{ fontSize: 10.5, color: APP.muted, background: APP.alt, border: `1px solid ${APP.line}`, borderRadius: 2, padding: '2px 5px' }}>
                          <span style={{ opacity: .75 }}>{k}</span> {v}
                        </span>
                      ))}
                    </div>
                  )}
                  {peerMeta.bio && (
                    <div style={{ fontSize: 11.5, color: APP.muted, lineHeight: 1.5, marginTop: 4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      <span style={{ fontWeight: 500, color: APP.ink }}>{t('自我介绍', 'About')}：</span>{peerMeta.bio}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 11.5, color: '#6E685F', marginTop: 4 }}>{t('资料加载中…', 'Loading profile…')}</div>
              )}
            </div>
          </div>
        )}
        {rows.map(([k, v], i) => (
          <div key={i} style={{ display: 'flex', gap: 10, fontSize: 12, padding: '5px 0', borderTop: i ? `1px solid ${APP.line}` : 'none' }}>
            <span style={{ color: APP.muted, flex: '0 0 68px' }}>{k}</span>
            <span style={{ color: APP.ink, fontWeight: 500, wordBreak: 'break-word' }}>{v}</span>
          </div>
        ))}
        {record.kind === 'match' && (() => {
          // The same three actions the candidate cards offer, aimed at the
          // person on THIS recommendation. other_id comes from the activity row.
          const pid = d.other_id || d.otherId;
          if (!pid) return null;
          const target = { id: pid, first_name: d.other || '' };
          const busy = candBusy === pid;
          const btnStyle = (disabled) => ({
            display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500,
            padding: '6px 11px', borderRadius: 2, border: `1px solid ${APP.line}`, background: '#FFFDF9',
            color: APP.burgundy, cursor: disabled ? 'wait' : 'pointer',
          });
          // The three candidate-facing actions wait on an in-flight call for
          // this person; the two write-ups below don't touch them at all.
          const btn = (label, Icon, onClick) => (
            <button key={label} type="button" disabled={busy} onClick={onClick} style={btnStyle(busy)}>
              <Icon size={13} />{label}
            </button>
          );
          const writeBtn = (label, Icon, onClick) => (
            <button key={label} type="button" onClick={onClick} style={btnStyle(false)}>
              <Icon size={13} />{label}
            </button>
          );
          const call = candCall[pid];
          return (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10, paddingTop: 10, borderTop: `1px solid ${APP.line}` }}>
              {btn(t('约见', 'Meet'), CalendarHeart, () => openSchedule(pid))}
              {btn(t('相亲卡', 'Card'), CreditCard, () => openCard(target))}
              {btn(call ? t('复制邀请', 'Copy invite') : t('打电话', 'Call'), PhoneCall,
                () => (call
                  ? copyText(shareBlock(call, 'audio'), t('邀请内容已复制', 'Invitation copied'), t('复制邀请内容', 'Copy the invitation'))
                  : callCandidate(target)))}
              {/* 写约会反馈 / 写客户情况 — same two write-ups the bottom toolbar
                  offers, put here so a coach reading a 推荐详情 can record the
                  outcome without scrolling back out of the record. */}
              {writeBtn(t('写约会反馈', 'Date feedback'), PenLine, () => { setComposer(null); setEditing(null); openFeedback(); })}
              {writeBtn(t('写客户情况', 'Client note'), StickyNote, goNotes)}
            </div>
          );
        })()}
        {record.kind === 'match' && recReason && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${APP.line}` }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: APP.muted, marginBottom: 6 }}>{t('推荐理由（给会员看）', 'Why we recommend (member sees this)')}</div>
            {recReason.loading ? <div style={{ fontSize: 12, color: APP.muted }}>{t('加载中…', 'Loading…')}</div> : (
              <>
                <RecommendReasonEditor name={withOid(d)} isZh={isZh} draft={recReason.draft} disabled={recReasonSaving}
                  value={recReason} onChange={(v) => setRecReason(r => ({ ...r, ...v }))} />
                {(() => {
                  const dirty = !recReason.saved || recReason.text.trim() !== recReason.saved.text
                    || (!!recReason.confirmed && !!recReason.text.trim()) !== recReason.saved.confirmed;
                  return (
                    <button type="button" disabled={recReasonSaving || !dirty} onClick={saveRecReason}
                      style={{ marginTop: 6, fontSize: 12, fontWeight: 500, padding: '6px 12px', borderRadius: 2, border: 'none',
                        background: (recReasonSaving || !dirty) ? '#A39E95' : APP.burgundy, color: '#FFFDF9', cursor: recReasonSaving ? 'wait' : 'pointer' }}>
                      {recReasonSaving ? t('保存中…', 'Saving…') : t('保存推荐理由', 'Save')}
                    </button>
                  );
                })()}
              </>
            )}
          </div>
        )}
        {record.kind === 'match' && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${APP.line}` }}>
            {reviewLink ? (
              <div style={{ padding: 9, border: '1px solid rgba(17,17,17,.12)', borderRadius: 2, background: '#EFEAE2' }}>
                <div style={{ fontSize: 11.5, fontWeight: 500, color: '#111111', marginBottom: 6, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Link2 size={13} />
                  {t(`确认链接已生成（共 ${reviewLink.count} 位推荐）`, `Link ready (${reviewLink.count} recommendation(s))`)}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button type="button"
                    onClick={() => copyText(
                      t(`${inviteHost}，请确认我们为你推荐的人选：${reviewLink.url}`,
                        `${inviteHost}: please confirm the people we recommended: ${reviewLink.url}`),
                      t('已复制', 'Copied'), t('复制确认链接', 'Copy the confirmation link'))}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500, padding: '6px 11px', borderRadius: 2, border: `1px solid ${APP.line}`, background: '#FFFDF9', color: APP.burgundy, cursor: 'pointer' }}>
                    <Copy size={13} />{t('复制确认链接', 'Copy link')}
                  </button>
                  <a href={reviewLink.url} target="_blank" rel="noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500, padding: '6px 11px', borderRadius: 2, background: '#111111', color: '#FFFDF9', textDecoration: 'none' }}>
                    <ExternalLink size={13} />{t('预览客户看到的页面', 'Preview')}
                  </a>
                </div>
              </div>
            ) : (
              <button type="button" onClick={createReviewLink} disabled={reviewBusy}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500, padding: '7px 12px', borderRadius: 2, border: '1px solid rgba(17,17,17,.12)', background: '#EFEAE2', color: '#111111', cursor: reviewBusy ? 'wait' : 'pointer' }}>
                <Link2 size={13} />{reviewBusy ? t('生成中…', 'Creating…') : t('生成推荐确认链接', 'Create a confirmation link')}
              </button>
            )}
            <div style={{ fontSize: 11, color: APP.muted, marginTop: 6 }}>
              {t('客户打开后可以逐个选择「接受 / 不接受」，结果会回到这里。', 'The client accepts or declines each person; the result comes back here.')}
            </div>
          </div>
        )}
        {canStatus && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${APP.line}`, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: APP.muted }}>{t('状态', 'Status')}</span>
            <select value={recordStatus} onChange={(e) => setRecordStatus(e.target.value)}
              style={{ ...input, padding: '6px 8px', fontSize: 12, cursor: 'pointer', background: '#FFFDF9' }}>
              <option value="">{t('未设置', 'Not set')}</option>
              {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button type="button" disabled={recordSaving || !recordStatus || recordStatus === String(d.status || '')}
              onClick={saveRecordStatus}
              style={{ fontSize: 12, fontWeight: 500, padding: '7px 13px', borderRadius: 2, border: 'none',
                background: (recordSaving || !recordStatus || recordStatus === String(d.status || '')) ? '#A39E95' : APP.burgundy,
                color: '#FFFDF9', cursor: recordSaving ? 'wait' : 'pointer' }}>
              {recordSaving ? t('保存中…', 'Saving…') : t('更新状态', 'Update')}
            </button>
          </div>
        )}
      </div>
    );
  };

  function renderPanel() {
    if (view === 'recommend') {
      return (
        <>
          {candToolbar()}
          {/* 已选 bar — survives searching, so picks accumulate across queries */}
          {pickedList.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '7px 0 9px', borderBottom: `1px solid ${APP.line}`, marginBottom: 6 }}>
              {pickedList.map(r => (
                <button key={r.id} type="button" onClick={() => togglePick(r)} title={t('移除', 'Remove')}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontFamily: APP.serif, color: APP.ink,
                    background: 'transparent', border: `1px solid ${APP.ink}`, borderRadius: 2, padding: '3px 9px', cursor: 'pointer' }}>
                  {nameOfProfile(r) || `#${r.id}`} <X size={11} strokeWidth={1.5} />
                </button>
              ))}
            </div>
          )}
          {/* 推荐理由 per pick — drafted from both 速记卡, saved with 创建匹配;
              only the ones ticked 确认 show on the member's 确认链接. */}
          {pickedList.length > 0 && (
            <div style={{ padding: '8px 0 10px', borderBottom: `1px solid ${APP.line}`, marginBottom: 6 }}>
              <div style={{ fontSize: 10.5, fontWeight: 400, letterSpacing: '.2em', color: APP.muted, marginBottom: 8 }}>
                {t(`推荐理由（给会员看，已确认 ${pickedList.filter(r => { const v = reasonFor(r.id); return v.confirmed && v.text.trim(); }).length} / ${pickedList.length}）`,
                  `Why we recommend (member sees confirmed ones)`)}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10, maxHeight: 280, overflowY: 'auto' }}>
                {pickedList.map(r => (
                  <RecommendReasonEditor key={r.id} name={nameOfProfile(r) || `#${r.id}`} isZh={isZh} draft={reasonDraft(r.id)} disabled={saving}
                    value={reasonFor(r.id)} onChange={(v) => setReasons(prev => ({ ...prev, [r.id]: v }))} />
                ))}
              </div>
            </div>
          )}
          <div style={panelBody}>
            {candLoading ? <div style={{ color: '#6E685F', padding: '2rem', textAlign: 'center' }}>{t('加载中…', 'Loading…')}</div>
              : !visibleCandidates.length ? <div style={{ color: '#6E685F', padding: '2rem', textAlign: 'center' }}>{t('没有符合条件的候选人', 'No candidates match')}</div>
                : renderCandidateCards(visibleCandidates, 'recommend')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${APP.line}` }}>
            <span style={{ fontSize: 10.5, letterSpacing: '.2em', color: APP.muted }}>{t('已选', 'SELECTED')}<span style={{ fontFamily: APP.serif, fontSize: 18, letterSpacing: 0, color: APP.ink, marginLeft: 6 }}>{pickedList.length}</span></span>
            <div style={{ flex: 1 }} />
            <button type="button" disabled={saving} onClick={submitRecommend} style={primaryBtn(saving)}>
              {saving ? t('创建中…', 'Creating…') : t(`创建匹配 (${pickedList.length})`, `Create matches (${pickedList.length})`)}
            </button>
          </div>
        </>
      );
    }

    if (view === 'schedule') {
      return (
        <>
          {/* 已签线 or the whole opposite-sex pool — a date does not have to
              start from an existing line, so both are pickable here. */}
          <div style={{ display: 'inline-flex', gap: 2, padding: 2, background: '#EFEAE2', borderRadius: 2, marginBottom: 9 }}>
            {[['lines', t(`已签线 (${partners.length})`, `Signed lines (${partners.length})`)], ['pool', t('全部异性', 'All opposite sex')]].map(([k, label]) => (
              <button key={k} type="button"
                onClick={() => { setScheduleSource(k); if (k === 'pool' && !candidates.length) loadCandidates(candSearch.trim()); }}
                style={{
                  padding: '5px 13px', fontSize: 12.5, fontWeight: 500, border: 'none', cursor: 'pointer', borderRadius: 2,
                  fontFamily: 'inherit', background: scheduleSource === k ? '#FFFDF9' : 'transparent',
                  color: scheduleSource === k ? APP.ink : APP.muted,
                  boxShadow: scheduleSource === k ? '0 1px 3px rgba(0,0,0,.14)' : 'none',
                }}>{label}</button>
            ))}
          </div>
          {scheduleSource === 'pool' && candToolbar()}
          <div style={panelBody}>
            {scheduleSource === 'lines' ? (
              <>
                <div style={{ fontSize: 12, fontWeight: 500, color: APP.muted, marginBottom: 7 }}>{t('和谁约见（来自该客户的推荐记录）', 'With whom (from this client\'s recommendations)')}</div>
                {candLoading ? <div style={{ color: '#6E685F', padding: '1.5rem', textAlign: 'center' }}>{t('加载中…', 'Loading…')}</div>
                  : !partners.length ? (
                    <div style={{ color: '#6E685F', padding: '1.5rem', textAlign: 'center', fontSize: 12.5 }}>
                      {t('该客户还没有推荐记录，可切到「全部异性」直接选人。', 'No recommendations yet — switch to 全部异性 to pick anyone.')}
                    </div>
                  ) : partners.map(x => {
                    const on = String(form.partnerId) === String(x.id);
                    return (
                      <div key={x.id} onClick={() => setForm(f => ({ ...f, partnerId: x.id }))}
                        style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 4px', borderBottom: '1px solid rgba(17,17,17,.08)', cursor: 'pointer', background: on ? '#EFEAE2' : 'transparent' }}>
                        <span style={{ width: 16, height: 16, borderRadius: '50%', flex: '0 0 auto', border: `2px solid ${on ? APP.burgundy : 'rgba(17,17,17,.3)'}`, background: on ? APP.burgundy : '#FFFDF9' }} />
                        <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500 }}>
                          {x.name}<small style={{ color: APP.muted, fontWeight: 500, marginLeft: 6, fontFamily: APP.serif, fontStyle: 'italic' }}>#{x.offline_id || x.id}</small>
                        </div>
                        {x.pct ? <span style={pill(pctColor(Math.round(Number(x.pct))))}>{Math.round(Number(x.pct))}%</span> : null}
                        {/* 删除这条线 — removes the matches row, so the pair drops
                            out of 签线记录 and out of this list. */}
                        <button type="button" title={t('删除这条签线', 'Delete this line')}
                          onClick={(e) => { e.stopPropagation(); removeLine(x); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9E2B25', padding: '4px 2px', display: 'inline-flex', flex: '0 0 auto' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    );
                  })}
              </>
            ) : (
              candLoading ? <div style={{ color: '#6E685F', padding: '2rem', textAlign: 'center' }}>{t('加载中…', 'Loading…')}</div>
                : !visibleCandidates.length ? <div style={{ color: '#6E685F', padding: '2rem', textAlign: 'center' }}>{t('没有符合条件的候选人', 'No candidates match')}</div>
                  : renderCandidateCards(visibleCandidates, 'schedule')
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              <input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} style={{ ...input, minWidth: 140 }} />
              <TimeSelect value={form.time} onChange={(v) => setForm(f => ({ ...f, time: v }))} style={input} isZh={isZh} placeholder={t('选择时间', 'Time')} />
              <span style={ptHint}>{t('美西时间', 'PT')}</span>
              <input value={form.location} onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))}
                placeholder={t('地点（选填）', 'Location (optional)')} style={{ ...input, flex: 1 }} />
            </div>
            {/* 提醒时间 — on top of the automatic 24h / 1h reminders. Fires an
                in-app + push notification to the coach at the chosen moment. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 500, color: APP.muted }}>
                <AlarmClock size={13} />{t('提醒时间', 'Remind me')}
              </span>
              <input type="datetime-local" value={form.remindAt}
                onChange={(e) => setForm(f => ({ ...f, remindAt: e.target.value }))}
                style={{ border: `1px solid ${APP.line}`, borderRadius: 2, padding: '6px 8px', fontSize: 12.5, fontFamily: 'inherit' }} />
              <span style={ptHint}>{t('美西时间', 'PT')}</span>
              {/* presets are relative to the date being booked, so they only
                  light up once a date + time is on the form */}
              {[[24 * 60, t('提前1天', '1d before')], [120, t('提前2小时', '2h before')], [30, t('提前30分钟', '30m before')]].map(([mins, lbl]) => (
                <button key={mins} type="button" disabled={!form.date || !form.time}
                  onClick={() => {
                    // the date + time are Pacific, so the preset is computed on
                    // the Pacific clock too (not the browser's)
                    const at = laShiftInput(`${form.date}T${form.time}`, -mins);
                    if (at) setForm(f => ({ ...f, remindAt: at }));
                  }}
                  style={{ fontSize: 11.5, fontWeight: 500, padding: '5px 9px', borderRadius: 2, border: `1px solid ${APP.line}`,
                    background: '#FFFDF9', color: (!form.date || !form.time) ? '#A39E95' : APP.burgundy,
                    cursor: (!form.date || !form.time) ? 'not-allowed' : 'pointer' }}>
                  {lbl}
                </button>
              ))}
              {form.remindAt && (
                <button type="button" onClick={() => setForm(f => ({ ...f, remindAt: '' }))}
                  style={{ fontSize: 11.5, fontWeight: 500, padding: '5px 9px', borderRadius: 2, border: 'none', background: 'none', color: APP.muted, cursor: 'pointer' }}>
                  {t('不提醒', 'No reminder')}
                </button>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', marginTop: 12, paddingTop: 12, borderTop: `1px solid ${APP.line}` }}>
            <div style={{ flex: 1 }} />
            <button type="button" disabled={saving} onClick={submitSchedule} style={primaryBtn(saving)}>
              {saving ? t('创建中…', 'Creating…') : t('创建约会', 'Create date')}
            </button>
          </div>
        </>
      );
    }

    // view === 'feedback'
    return (
      <>
        <div style={panelBody}>
          {candLoading ? <div style={{ color: '#6E685F', padding: '2rem', textAlign: 'center' }}>{t('加载中…', 'Loading…')}</div>
            : !schedules.length ? (
              // Feedback always belongs to a specific date. With none on file
              // there is nothing to write against — say so and offer the step
              // that actually unblocks it, instead of a dead-end message.
              <div style={{ padding: '1.6rem 1rem', textAlign: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 5 }}>{t('该客户还没有约会记录', 'No dates for this client yet')}</div>
                <div style={{ fontSize: 12, color: APP.muted, marginBottom: 12 }}>
                  {t('约会反馈要挂在一场具体的约会上，先安排一场。', 'Feedback attaches to a specific date — schedule one first.')}
                </div>
                <button type="button" onClick={() => openSchedule()} style={{ ...primaryBtn(false), display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <CalendarHeart size={14} />{t('去安排约会', 'Schedule a date')}
                </button>
              </div>
            )
              : !fbTarget ? schedules.map(s => {
                const isC1 = Number(s.client1_profile_id) === Number(profileId);
                const other = isC1 ? nameOfProfile(s, 'client2_') : nameOfProfile(s, 'client1_');
                const done = !!s.feedback_at;
                return (
                  <div key={s.id} onClick={() => pickFbTarget(s)}
                    style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 4px', borderBottom: '1px solid rgba(17,17,17,.08)', cursor: 'pointer' }}>
                    <div style={{ width: 26, height: 26, borderRadius: 2, background: '#EFEAE2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <CalendarHeart size={14} color="#3A3835" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{other || t('对方', 'Partner')}</div>
                      <div style={{ fontSize: 11.5, color: APP.muted }}>
                        {[String(s.scheduled_date || '').slice(0, 10), String(s.scheduled_time || '').slice(0, 5), s.location].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                    <span style={pill(done ? '#6E685F' : '#8A6D3B')}>{done ? t('已写', 'Written') : t('待反馈', 'Feedback due')}</span>
                    {/* explicit affordance — the row itself is clickable, but
                        without a button nobody could tell where to write */}
                    <span style={{ fontSize: 11.5, fontWeight: 500, color: APP.burgundy, border: `1px solid ${APP.line}`, borderRadius: 2, padding: '5px 9px', whiteSpace: 'nowrap' }}>
                      {done ? t('修改反馈', 'Edit') : t('写反馈', 'Write')} <ChevronRight size={11} style={{ verticalAlign: '-1px' }} />
                    </span>
                  </div>
                );
              }) : (
                <>
                  <div style={{ fontSize: 12.5, fontWeight: 500, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CalendarHeart size={14} color="#3A3835" />
                    {String(fbTarget.scheduled_date || '').slice(0, 10)} {String(fbTarget.scheduled_time || '').slice(0, 5)}
                    <button type="button" onClick={() => setFbTarget(null)}
                      style={{ marginLeft: 10, background: 'none', border: 'none', color: APP.burgundy, cursor: 'pointer', fontSize: 12, fontWeight: 500 }}>{t('换一场', 'Change')}</button>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <select value={fbForm.status} onChange={(e) => setFbForm(f => ({ ...f, status: e.target.value }))} style={input}>
                      {[['completed', t('已完成', 'Completed')], ['meeting', t('见面了', 'Met')], ['chatting', t('聊天中', 'Chatting')],
                        ['in_relationship', t('交往中', 'Together')], ['no_show', t('未到场', 'No-show')], ['cancelled', t('已取消', 'Cancelled')]]
                        .map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <select value={fbForm.rating} onChange={(e) => setFbForm(f => ({ ...f, rating: e.target.value }))} style={input}>
                      <option value="">{t('评分（选填）', 'Rating (optional)')}</option>
                      {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{'★'.repeat(n)}</option>)}
                    </select>
                  </div>
                  <textarea value={fbForm.text} onChange={(e) => setFbForm(f => ({ ...f, text: e.target.value }))} rows={4} autoFocus
                    placeholder={t('这次约会怎么样？', 'How did it go?')}
                    style={{ width: '100%', border: `1px solid ${APP.line}`, borderRadius: 2, padding: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
                </>
              )}
        </div>
        {fbTarget && (
          <div style={{ display: 'flex', marginTop: 12, paddingTop: 12, borderTop: `1px solid ${APP.line}` }}>
            <div style={{ flex: 1 }} />
            <button type="button" disabled={saving} onClick={submitFeedback} style={primaryBtn(saving)}>
              {saving ? t('保存中…', 'Saving…') : t('保存反馈', 'Save feedback')}
            </button>
          </div>
        )}
      </>
    );
  }

  // ── 客户工作台 derived state + actions ───────────────────────────────────
  const teachers = useMemo(() => {
    const out = [];
    const add = (id, nm) => {
      const n = Number(id);
      if (!n || out.some(x => x.id === n)) return;
      out.push({ id: n, name: (nm && String(nm).trim()) || `#${n}` });
    };
    add(meId, [user?.first_name, user?.last_name].filter(Boolean).join(' ') || t('我', 'Me'));
    add(p.assigned_coach_id, coachName);
    add(p.matchmaker_id, [p.matchmaker_first_name, p.matchmaker_last_name].filter(Boolean).join(' '));
    activity.forEach(x => add(x.by_user_id, x.by));
    staffList.forEach(s => add(s.id, [s.first_name, s.last_name].filter(Boolean).join(' ')));
    return out;
  }, [meId, user, p.assigned_coach_id, coachName, p.matchmaker_id, p.matchmaker_first_name, p.matchmaker_last_name, activity, staffList, isZh]);

  const currentStep = asArray(ws && ws.steps).find(s => s.status === 'overdue') || asArray(ws && ws.steps).find(s => s.status === 'current') || null;
  const fbLight = fallbackLight(svc, isZh);
  const nextActions = ws && Array.isArray(ws.next_actions)
    ? ws.next_actions
    : fallbackNextActions(svc, Object.values(meetChecks), isZh);
  const reviewCount = ws && ws.reviews_pending && typeof ws.reviews_pending === 'object'
    ? Object.values(ws.reviews_pending).reduce((n, v) => n + (Number(v) || 0), 0)
    : (svc && svc.pending_interval_request ? 1 : 0) + activity.filter(x => x.type === 'contact' && x.review_status === 'submitted').length;

  const goNotes = () => {
    setComposer(null); setEditing(null); setView(null); setRecord(null);
    setTab('records'); setRecFilter('notes'); setNotesFocus(n => n + 1);
  };
  // 服务进度 → the place where an auto step gets its evidence.
  const goFromStep = (target) => {
    if (target === 'quick_card' || target === 'quiz') { setTab('profile'); return; }
    if (target === 'meets') { setTab('records'); setRecFilter('meet'); return; }
    if (target === 'recommend') { openRecommend(); return; }
    if (String(target).startsWith('contact:')) openComposer('contact', { purpose: String(target).slice(8) });
  };

  // One 下一步 card's button.
  const runNext = async (a) => {
    const args = (a && a.action_args) || {};
    const missing = Array.isArray(args.missing) ? args.missing : [];
    const partnerOnly = missing.length > 0 && !missing.includes('self') && missing.includes('partner');
    const contactOpts = () => ({
      draft: args.preset_text || '',
      purpose: args.purpose,
      scheduleId: args.dating_schedule_id != null ? args.dating_schedule_id : null,
      target: args.target_profile_id ? { id: args.target_profile_id, name: args.target_name || '' }
        : partnerOnly && args.partner_profile_id ? { id: args.partner_profile_id, name: args.partner_name || args.partner_label || '' } : null,
    });
    // 14 天无推荐 for a 主管 → open 新建整改单 prefilled.
    if (args.rectification && isSup) {
      setTab('review'); setPresetRect(x => ({ n: ((x && x.n) || 0) + 1, title: args.preset_title || '' }));
      return;
    }
    switch (a && a.action) {
      case 'contact': openComposer('contact', contactOpts()); return;
      case 'meeting_check': openComposer('contact', { ...contactOpts(), purpose: args.purpose || 'pre_meeting' }); return;
      case 'step': setTab('steps'); setStepFocus(args.step_key || args.key || String(a.key || '').replace(/^step:/, '')); return;
      case 'report': if (args.report_id || (svc && svc.report && svc.report.id)) setReportOpen(args.report_id || svc.report.id); return;
      case 'complaint': setComplaintsOpen(true); return;
      case 'interval': setTab(isSup ? 'review' : 'profile'); return;
      case 'review': setTab(isSup ? 'review' : 'next'); return;
      case 'recommend': openRecommend(); return;
      case 'schedule': openSchedule(args.partner_profile_id || null); return;
      case 'todo': {
        if (args.do === 'verify') { setTab(isSup ? 'review' : 'next'); return; }
        if (!args.rectification_id && !args.note_id) { setMemoFocus(n => n + 1); return; }
        // eslint-disable-next-line no-alert
        const v = window.prompt(args.rectification_id ? t('整改完成了。做了什么？', 'Done. What did you do?') : t('完成了。结果是？（选填）', 'Done. Result? (optional)'), '');
        if (v === null) return;
        if (args.rectification_id && !v.trim()) { toast.error(t('请写一句做了什么', 'Say what you did')); return; }
        setNextBusy(a.key);
        try {
          if (args.rectification_id) await workspaceAPI.updateRectification(args.rectification_id, { action: 'done', note: v.trim() || undefined });
          else await profilesAPI.markNoteDone(profileId, args.note_id, v.trim());
          toast.success(t('已完成', 'Done'));
          reloadWs(); reloadStatus(); onChanged && onChanged();
        } catch (e) {
          toast.error(e?.response?.data?.error || t('操作失败', 'Failed'));
        } finally { setNextBusy(null); }
        return;
      }
      default:
        if (args.purpose || args.preset_text) openComposer('contact', contactOpts());
    }
  };

  const renderThumbs = (imgs) => (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
      {imgs.map((u, i) => (
        <a key={i} href={resolvePhotoUrl(u) || u} target="_blank" rel="noreferrer">
          <img src={resolvePhotoUrl(u) || u} alt="" loading="lazy" style={{ width: 46, height: 60, objectFit: 'cover', borderRadius: 2, border: `1px solid ${APP.line}` }} />
        </a>
      ))}
    </div>
  );
  const REVIEW_BADGE = {
    submitted: [t('待审核', 'In review'), '#93591A', '#F3EADB'],
    approved: [t('已通过', 'Approved'), '#3F7D5A', '#E6EEE8'],
    rejected: [t('已退回', 'Returned'), '#9E2B25', '#F3E3E1'],
  };

  const renderMeetRow = (r) => {
                  const ds = DATE_STATUS_LABEL[r.status] || { zh: r.status, en: r.status, c: '#6E685F' };
                  // 第1次 with nothing after it is the 「没有第二次」 case the
                  // 今日待办 queue also nags about — flag it here in amber.
                  const onlyOnce = r.total === 1;
                  return (
                    <div key={r.id}
                      onClick={() => openRecord('date', { id: r.id, at: r.at, other: r.partner, other_id: r.partnerId, when: r.at, location: r.location, status: r.status, hasFeedback: r.done }, `meet-${r.id}`)}
                      style={{ display: 'flex', gap: 10, padding: '9px 0', borderBottom: '1px solid rgba(17,17,17,.08)', alignItems: 'flex-start', cursor: 'pointer' }}>
                      <div style={{ width: 26, height: 26, borderRadius: 2, background: '#EFEAE2', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                        <CalendarHeart size={14} color="#3A3835" />
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 13, fontWeight: 500 }}>{r.partner}</span>
                          <span style={{ fontSize: 10, fontWeight: 500, borderRadius: 2, padding: '1px 5px', color: onlyOnce ? '#93591A' : '#3A3835', background: onlyOnce ? '#F3EADB' : '#EFEAE2' }}>
                            {t(`第 ${r.nth} 次`, `Meeting ${r.nth}`)}
                          </span>
                          {onlyOnce && <span style={{ fontSize: 10.5, fontWeight: 500, color: '#93591A' }}>{t('还没有第二次', 'no second meeting yet')}</span>}
                        </div>
                        <div style={{ fontSize: 11.5, color: APP.muted, marginTop: 1 }}>
                          {[r.at, r.location, isZh ? ds.zh : ds.en, r.done ? t('已写反馈', 'feedback written') : t('待写反馈', 'feedback due')].filter(Boolean).join(' · ')}
                        </div>
                        {renderMeetChecks(r)}
                      </div>
                    </div>
                  );
  };

  // ── 客户工作台 layout ────────────────────────────────────────────────────
  const facts = [
    String(p.gender || '').toLowerCase() === 'male' ? t('男', 'M') : String(p.gender || '').toLowerCase() === 'female' ? t('女', 'F') : '',
    (() => { const a = ageOf(p.date_of_birth || p.birth_date) || (ws && ws.profile && ws.profile.age); return a ? t(`${a} 岁`, `${a}y`) : ''; })(),
    p.location_city || (ws && ws.profile && ws.profile.city) || '',
  ].filter(Boolean).join(' / ');
  const serviceDay = ws && ws.service_day != null ? ws.service_day : (svc && svc.service_day != null ? svc.service_day : null);
  const stageName = (ws && ws.stage_name) || (currentStep && currentStep.name) || stage.label;
  const lightKey = (ws && ws.light) || fbLight.light;
  const lightReasons = ws ? asArray(ws.light_reasons) : fbLight.reasons;
  const serviceClosed = !!(ws && ws.service_closed);
  const closedDay = serviceClosed && ws.closed_at ? (() => { const d = new Date(ws.closed_at); return isNaN(d) ? '' : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })() : '';
  const lt = serviceClosed ? { ...LIGHT.green, zh: '已结单', en: 'Closed' } : (LIGHT[lightKey] || null);

  const TABS = [
    { k: 'next', label: t('下一步', 'Next'), n: nextActions.filter(a => a.severity === 'red' || a.severity === 'amber').length, red: nextActions.some(a => a.severity === 'red') },
    { k: 'steps', label: t('服务进度', 'Progress'), n: 0 },
    { k: 'records', label: t('全部记录', 'All records'), n: 0 },
    { k: 'profile', label: t('资料·报告', 'Profile & reports'), n: 0 },
    ...(isSup ? [{ k: 'review', label: t('审核', 'Review'), n: reviewCount, red: reviewCount > 0 }] : []),
  ];
  const MORE = [
    { key: 'recommend', Icon: HeartHandshake, label: t('推荐给他人', 'Recommend'), run: () => { setComposer(null); setEditing(null); openRecommend(); } },
    { key: 'date', Icon: CalendarHeart, label: t('安排约会', 'Schedule a date'), run: () => { setComposer(null); setEditing(null); openSchedule(); } },
    { key: 'feedback', Icon: PenLine, label: t('写约会反馈', 'Date feedback'), run: () => { setComposer(null); setEditing(null); openFeedback(); } },
    { key: 'note', Icon: StickyNote, label: t('写客户情况', 'Client note'), run: goNotes },
    { key: 'complaint', Icon: AlertTriangle, label: t('投诉记录', 'Complaints'), run: () => setComplaintsOpen(true) },
    ...(svc && svc.report && svc.report.id ? [{ key: 'report', Icon: ClipboardList, label: t('月度服务报告', 'Monthly report'), run: () => setReportOpen(svc.report.id) }] : []),
    { key: 'login', Icon: KeyRound, label: t('生成登录', 'Client login'), run: () => setLoginOpen(true) },
  ];

  const REC_FILTERS = [
    ['all', t('全部', 'All')], ['contact', t('联系', 'Contacts')], ['notes', t('客户情况', 'Notes')],
    ['match', t('推荐', 'Recommendations')], ['meet', t('约见', 'Meetings')], ['complaint', t('投诉', 'Complaints')],
    ...(favs ? [['fav', t('心动', 'Hearts')]] : []),
  ];

  // Rows of 全部记录 that are not on the activity feed.
  const extraRow = (r) => (
    <div key={r.key} onClick={r.onClick}
      style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(17,17,17,.08)', alignItems: 'flex-start', cursor: r.onClick ? 'pointer' : 'default' }}>
      <div style={{ width: 26, height: 26, borderRadius: 2, flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', background: r.bg }}>
        <r.Icon size={14} color={r.color} />
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13.5, fontWeight: 500, color: APP.ink, overflowWrap: 'anywhere' }}>{r.title}</span>
          {r.badge}
        </div>
        {r.body && <div style={{ fontSize: 13, color: APP.ink, marginTop: 2, lineHeight: 1.55, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>{r.body}</div>}
        <div style={{ fontSize: 11.5, color: APP.muted, marginTop: 1 }}>{r.sub}</div>
        {r.images && r.images.length > 0 && renderThumbs(r.images)}
      </div>
    </div>
  );
  const ticketItems = followupRows.filter(r => r.src === 'ticket').map(r => ({
    at: r.at, key: r.key,
    node: extraRow({ key: r.key, Icon: AlarmClock, color: '#93591A', bg: '#F3EADB', title: r.title,
      badge: <span style={{ fontSize: 10, fontWeight: 500, borderRadius: 2, padding: '1px 5px', color: '#93591A', background: '#F3EADB' }}>{t('工单回访', 'Ticket')}</span>,
      sub: [...r.meta, fmtWhen(r.at, isZh)].filter(Boolean).join(' · '),
      onClick: () => openRecord('followup', { at: r.at, text: r.title, by: r.meta.filter(Boolean).join(' · ') }, r.key) }),
  }));
  const complaintItems = complaints.map(c => ({
    at: c.created_at, key: `cp${c.id}`,
    node: extraRow({ key: `cp${c.id}`, Icon: AlertTriangle, color: '#9E2B25', bg: '#F3E3E1',
      title: c.level === 'urgent' ? t('紧急投诉', 'Urgent complaint') : t('投诉', 'Complaint'),
      badge: <span style={{ fontSize: 10.5, fontWeight: 500, borderRadius: 2, padding: '1px 6px', color: c.status === 'resolved' ? '#3F7D5A' : '#9E2B25', background: c.status === 'resolved' ? '#E6EEE8' : '#F3E3E1' }}>
        {c.status === 'resolved' ? t('已解决', 'Resolved') : c.status === 'in_progress' ? t('处理中', 'In progress') : t('待处理', 'Open')}</span>,
      body: [c.note, c.result ? t(`处理结果：${c.result}`, `Result: ${c.result}`) : ''].filter(Boolean).join('\n'),
      sub: [c.created_by_name, fmtWhen(c.created_at, isZh)].filter(Boolean).join(' · '),
      images: asArray(c.photos), onClick: () => setComplaintsOpen(true) }),
  }));
  const quickItems = quickHist.map((h, i) => ({
    at: h.created_at, key: `qc${h.id || i}`,
    node: extraRow({ key: `qc${h.id || i}`, Icon: ClipboardList, color: '#8A6D3B', bg: '#EFEAE2', title: t('速记卡修改', 'Quick card changed'),
      body: h.summary || [asArray(h.changes).map(c => `${quickLabel(c.field)}：${c.from || '空'} → ${c.to || '空'}`).join('；'), h.reason ? `原因：${h.reason}` : ''].filter(Boolean).join('；'),
      sub: [h.changed_by_name, fmtWhen(h.created_at, isZh)].filter(Boolean).join(' · '), onClick: () => setTab('profile') }),
  }));
  const stepItems = asArray(ws && ws.steps).filter(s => s.status === 'done' && s.done_at).map(s => ({
    at: s.done_at, key: `st${s.key}`,
    node: extraRow({ key: `st${s.key}`, Icon: CheckCircle2, color: '#3F7D5A', bg: '#E6EEE8', title: t(`完成步骤：${s.name}`, `Step done: ${s.name}`),
      body: s.note || '', sub: [s.done_by_name, s.verified_by_name ? t(`核实：${s.verified_by_name}`, `verified by ${s.verified_by_name}`) : '', fmtWhen(s.done_at, isZh)].filter(Boolean).join(' · '),
      images: asArray(s.images), onClick: () => { setTab('steps'); setStepFocus(s.key); } }),
  }));
  const favItems = asArray(favs && favs.events).map((e, i) => ({
    at: e.at, key: `fv${i}`,
    node: extraRow({ key: `fv${i}-${e.action}-${e.target && e.target.id}`, Icon: Heart, color: e.action === 'remove' ? '#6E685F' : '#9E2B25', bg: e.action === 'remove' ? '#EFEAE2' : '#F3E3E1',
      title: e.action === 'remove' ? t(`取消心动：${favName(e.target)}`, `Un-hearted ${favName(e.target)}`) : t(`加入心动：${favName(e.target)}`, `Hearted ${favName(e.target)}`),
      sub: [t('会员在 App 操作', 'by the member'), favWhen(e.at)].filter(Boolean).join(' · '), onClick: () => setTab('profile') }),
  }));
  const actItems = (type) => activity.filter(x => !type || x.type === type).map((x, i) => {
    const key = `${x.type}-${x.id != null ? x.id : i}`;
    return { at: x.contacted_at || x.at, key, node: renderRow(x, key) };
  });
  const byTime = (list) => list.filter(x => x.at).sort((a, b) => new Date(b.at) - new Date(a.at));
  const recCount = {
    all: activity.length + ticketItems.length + complaintItems.length + quickItems.length + stepItems.length + favItems.length,
    contact: activity.filter(x => x.type === 'contact').length + ticketItems.length,
    notes: activity.filter(x => x.type === 'note').length,
    match: activity.filter(x => x.type === 'match').length,
    meet: meetRows.length,
    complaint: complaints.length,
    fav: favItems.length,
  };

  const emptyBox = (title, hint, action) => (
    <div style={{ padding: '1.8rem 1rem', textAlign: 'center' }}>
      <div style={{ fontFamily: APP.serif, fontSize: 20, fontWeight: 500, color: APP.ink, marginBottom: 6 }}>{title}</div>
      {hint && <div style={{ fontSize: 12.5, color: APP.muted }}>{hint}</div>}
      {action && <div style={{ marginTop: 12 }}>{action}</div>}
    </div>
  );

  // The list of the current 全部记录 filter (null for 客户情况, which pages itself in CrmNotesPanel).
  const recordItems = () => {
    if (recFilter === 'notes') return null;
    if (recFilter === 'meet') return meetRows.map(r => ({ key: `meet-${r.id}`, node: renderMeetRow(r) }));
    if (recFilter === 'complaint') return byTime(complaintItems);
    if (recFilter === 'fav') return byTime(favItems);
    if (recFilter === 'contact') return byTime([...actItems('contact'), ...ticketItems]);
    if (recFilter === 'match') return byTime(actItems('match'));
    return byTime([...actItems(null), ...ticketItems, ...complaintItems, ...quickItems, ...stepItems, ...favItems]);
  };
  // Rows of one page, each followed by its detail when it is the open one.
  const pagedRows = (pg) => pg.slice.map(it => (
    <React.Fragment key={it.key}>
      {it.node}
      {record && record.anchor === it.key && <div style={{ padding: '10px 0 2px' }}>{renderRecordDetail()}</div>}
    </React.Fragment>
  ));
  const pager = (pg) => (
    <RecPager page={pg.page} pages={pg.pages} total={pg.total} isZh={isZh}
      onPage={(n) => { setRecPage(n); setRecord(r => (r && r.anchor ? null : r)); }} />
  );

  const renderRecords = () => {
    const items = loading ? null : recordItems();
    const pg = items ? pageSlice(items, recPage) : null;
    const anchorOnPage = !!(record && record.anchor && pg && pg.slice.some(it => it.key === record.anchor));
    return (
    <div>
      <TabIntro isZh={isZh} explain={isZh ? TAB_EXPLAIN.records : ''}
        text={t('这个会员的所有记录都在这里，按时间从新到旧。用上面的按钮筛选。', 'Every record for this member, newest first. Filter with the chips.')}
        steps={[
          t('点一条记录看详情；自己写的可以「编辑」或「删除」。', 'Click a record for details; edit or delete your own.'),
          t('推荐记录里可以生成「推荐确认链接」发给会员，约见记录里能看到见面前后的三个勾。', 'Recommendations can send a confirmation link; meetings show the three checks.'),
          t('客户情况里写备注和待办；投诉里看处理进度。', 'Notes and to-dos live under 客户情况; complaints show their status.'),
        ]} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        {REC_FILTERS.map(([k, label]) => {
          const on = recFilter === k;
          return (
            <button key={k} type="button" onClick={() => { setRecFilter(k); setRecord(null); }}
              style={{ height: 32, padding: '0 14px', borderRadius: WS.radius, fontSize: 12.5, fontWeight: 400, letterSpacing: '.12em', cursor: 'pointer', fontFamily: 'inherit',
                border: `1px solid ${on ? APP.ink : WS.line}`, background: on ? APP.ink : 'transparent', color: on ? WS.paper : WS.sub }}>
              {label}{recCount[k] ? <span style={{ marginLeft: 6, color: on ? WS.goldDecor : WS.mute }}>{recCount[k]}</span> : null}
            </button>
          );
        })}
        <span style={{ flex: 1 }} />
        {(recFilter === 'all' || recFilter === 'match') && activity.length > 0 && (
          <button type="button" onClick={() => setShowGantt(v => !v)} style={wsBtnLink}>{showGantt ? t('收起时间轴', 'Hide chart') : t('看时间轴', 'Timeline chart')}</button>
        )}
      </div>
      <div style={{ ...wsCard, padding: '6px 16px 10px' }}>
        {showGantt && (recFilter === 'all' || recFilter === 'match') && <div style={{ paddingTop: 10 }}>{renderGantt(recFilter === 'match' ? activity.filter(x => x.type === 'match') : activity)}</div>}
        {record && !anchorOnPage && <div style={{ paddingTop: 10 }}>{renderRecordDetail()}</div>}
        {loading ? (
          <div style={{ color: '#6E685F', padding: '2rem', textAlign: 'center' }}>{t('加载中…', 'Loading…')}</div>
        ) : recFilter === 'notes' ? (
          <div style={{ paddingTop: 10 }}>
            <CrmNotesPanel profileId={profileId} profile={profile} meId={meId} isZh={isZh} focusKey={notesFocus}
              onChanged={() => { reloadStatus(); reloadWs(); onChanged && onChanged(); }} />
          </div>
        ) : recFilter === 'meet' ? (
          <>
            <div style={{ display: 'flex', gap: 8, padding: '10px 0', flexWrap: 'wrap' }}>
              <button type="button" onClick={() => openSchedule()} style={wsBtnGhost}><CalendarHeart size={14} />{t('安排约会', 'Schedule a date')}</button>
              <button type="button" onClick={openFeedback} style={wsBtnGhost}><PenLine size={14} />{t('写约会反馈', 'Date feedback')}</button>
            </div>
            {pg.total ? <>{pagedRows(pg)}{pager(pg)}</> : emptyBox(t('还没有约见记录', 'No meetings yet'), t('用「安排约会」约第一次见面。', 'Use 安排约会 to book the first one.'))}
          </>
        ) : recFilter === 'complaint' ? (
          <>
            <div style={{ display: 'flex', gap: 8, padding: '10px 0' }}>
              <button type="button" onClick={() => setComplaintsOpen(true)} style={wsBtnGhost}><AlertTriangle size={14} />{t('新建 / 处理投诉', 'Open complaints')}</button>
            </div>
            {pg.total ? <>{pagedRows(pg)}{pager(pg)}</> : emptyBox(t('没有投诉', 'No complaints'), t('会员不满意时，在这里记录并跟进到解决。', 'Log any complaint here and follow it to resolution.'))}
          </>
        ) : recFilter === 'fav' ? (
          pg.total ? <>{pagedRows(pg)}{pager(pg)}</> : emptyBox(t('还没有心动记录', 'No hearts yet'), t('会员在 App 里点「加入心动」后会出现在这里。', 'Hearts the member adds in the app show up here.'))
        ) : (() => {
          if (pg.total) return <>{pagedRows(pg)}{pager(pg)}</>;
          return recFilter === 'match'
            ? emptyBox(t('还没有推荐记录', 'No recommendations yet'), t('点右上角 ⋯ →「推荐给他人」创建第一条匹配。', 'Use ⋯ → 推荐给他人 to create the first match.'))
            : recFilter === 'contact'
              ? emptyBox(t('还没有联系记录', 'No contacts yet'), t('点右上角「＋ 写联系记录」。', 'Use + 写联系记录 at the top.'))
              : emptyBox(t('该客户还没有任何记录', 'No records yet'), t('联系、推荐、约会、备注都会自动出现在这里。', 'Contacts, matches, dates and notes all land here.'));
        })()}
      </div>
    </div>
    );
  };

  const renderProfileTab = () => (
    <div>
      <TabIntro isZh={isZh} explain={isZh ? TAB_EXPLAIN.profile : ''}
        text={t('会员的资料、速记卡、测评、报告和付款都在这里。', 'Profile, quick card, assessments, reports and payments.')}
        steps={[
          t('先把「匹配速记卡」填完整，别的老师 30 秒就能看懂 TA 适合谁。', 'Fill the quick card so any teacher gets the direction in 30 seconds.'),
          t('改速记卡里已有的内容要写原因，所有修改都有记录。', 'Changing a filled field needs a reason; every change is kept.'),
          t('联系频率要调整，点「每 N 小时联系」申请，主管确认后生效。', 'To change the contact frequency, click it and file a request.'),
        ]} />
      <Section title={t('服务状态 · 联系频率', 'Service status')}
        description={t('点「每 N 小时联系」可以申请调整频率（会员要求、记录原因、主管确认后生效）。', 'Click the frequency to request a change (needs a supervisor).')}>
        <ServiceStatusLine status={svc} profileId={profileId} isZh={isZh} onReload={() => { reloadStatus(); reloadWs(); }}
          onOpenTodos={() => setTab('next')} onOpenComplaints={() => setComplaintsOpen(true)} onOpenReport={(id) => setReportOpen(id)} />
        {!svc && <div style={{ fontSize: 13, color: WS.mute }}>{t('暂无服务状态', 'No service status')}</div>}
        {noPhotoId && (
          <div style={{ marginTop: 10, padding: '6px 10px', borderRadius: 2, fontSize: 12.5, fontWeight: 500, background: '#F3EADB', border: '1px solid rgba(147,89,26,.3)', color: '#93591A', display: 'inline-block' }}>
            ⚠ {t('该会员未提供身份证件', 'No photo ID provided')}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 24px', marginTop: 10 }}>
          {kv(t('阶段', 'Stage'), stage.label, stage.color)}
          {kv(t('负责教练', 'Coach'), coachName)}
          {kv(t('会员套餐', 'Plan'), p.plan_name)}
          {kv(t('会员到期', 'Expires'), expiry)}
          {kv(t('本周联系', 'This week'), contactedThisWeek ? t('已联系', 'Done') : t('未联系', 'Not yet'), contactedThisWeek ? '#3F7D5A' : '#9E2B25')}
        </div>
      </Section>
      <Section title={t('匹配速记卡', 'Quick card')} description={t('17 项，画像后 24 小时内完成。', '17 fields, within 24h of the portrait.')}>
        <QuickCardPanel profileId={profileId} isZh={isZh}
          hints={{ member_no: p.offline_id ? String(p.offline_id) : '', age: ageOf(p.date_of_birth) ? String(ageOf(p.date_of_birth)) : '', city: p.location_city || '' }}
          onChanged={() => { reloadWs(); loadQuickHist(); }} />
      </Section>
      <Section title={t('档案资料 · 择偶要求', 'Profile & preferences')}>
        {profileFacts.length > 0 && (
          <>
            <button type="button" onClick={() => setShowProfilePanel(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', padding: '4px 0', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: WS.sub, fontFamily: 'inherit' }}>
              {showProfilePanel ? <ChevronUp size={14} /> : <ChevronDown size={14} />}{t('档案资料', 'Profile')} <span style={{ color: WS.mute }}>{profileFacts.length}</span>
            </button>
            {showProfilePanel && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0 24px' }}>{profileFacts.map(([k, v], i) => stackedKv(k, v, i))}</div>}
          </>
        )}
        <button type="button" onClick={() => setShowPrefPanel(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', padding: '4px 0', marginTop: 4, cursor: 'pointer', fontSize: 13, fontWeight: 500, color: WS.sub, fontFamily: 'inherit' }}>
          {showPrefPanel ? <ChevronUp size={14} /> : <ChevronDown size={14} />}{t('择偶要求', 'Partner preferences')} <span style={{ color: WS.mute }}>{prefFacts.length}</span>
        </button>
        {showPrefPanel && (prefFacts.length
          ? <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0 24px' }}>{prefFacts.map(([k, v], i) => stackedKv(k, v, i))}</div>
          : <div style={{ fontSize: 12.5, color: WS.mute, padding: '8px 0' }}>{t('还没有填择偶要求', 'No preferences on file')}</div>)}
      </Section>
      {favs && (
        <Section title={t(`TA 的心动（收藏）· ${favs.counts && favs.counts.current != null ? favs.counts.current : asArray(favs.current).length}`, `Hearts · ${asArray(favs.current).length}`)}
          description={t('会员在 App 里「加入心动」的人。互相心动、还没推荐的，优先考虑推荐。', 'People the member hearted in the app. Mutual hearts not yet recommended come first.')}>
          <FavoritesPanel data={favs} isZh={isZh} onView={viewFav} busyId={candBusy} />
        </Section>
      )}
      <Section title={t('测评报告', 'Assessments')} description={t('人生合伙人画像、爱的五种语言等问卷结果。', 'Portrait and personality questionnaires.')}>
        {quizRows.length ? renderQuizRows() : emptyBox(t('该客户还没有测评记录', 'No assessments yet'), t('在「＋ 写联系记录」里点「发送问卷」，会员提交后结果会出现在这里。', 'Send one from 写联系记录 → 发送问卷.'))}
      </Section>
      <Section title={t('月度服务报告', 'Monthly service reports')} description={t('每 30 天一份，主管审核后发给会员。', 'One every 30 days, reviewed before it goes out.')}>
        {reports.length ? reports.map(r => {
          const rk = reportStatusKey(r);
          const rs = REPORT_STATUS[rk] || REPORT_STATUS.draft;
          const mon = monthNo(r);
          return (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: `1px solid ${WS.line}`, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, color: APP.ink, flex: 1, minWidth: 0 }}>
                {mon ? t(`${mon} 月报告`, `Month ${mon} report`) : t(`第 ${r.period_index} 期报告`, `Report ${r.period_index}`)}
                <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 500, color: rs.color, background: rs.bg, borderRadius: 2, padding: '2px 8px' }}>{isZh ? rs.zh : rs.en}</span>
              </span>
              <button type="button" onClick={() => setReportOpen(r.id)} style={wsBtnGhost}>{['draft', 'returned'].includes(r.status) ? t('打开编辑', 'Edit') : t('查看', 'View')}</button>
            </div>
          );
        }) : emptyBox(t('还没有服务报告', 'No reports yet'), t('服务满 30 天前 3 天，系统会自动生成草稿。', 'A draft appears 3 days before each 30-day period ends.'))}
      </Section>
      <Section title={t('付款记录', 'Payments')}>{renderPayRows()}</Section>
    </div>
  );

  const actionPanelTitle = view === 'recommend' ? t(`为 ${name} 选择候选人`, `Pick candidates for ${name}`)
    : view === 'schedule' ? t(`为 ${name} 安排约会`, `Schedule a date for ${name}`)
      : t(`写 ${name} 的约会反馈`, `Write feedback for ${name}`);

  const dialog = (title, onX, body, width = 640) => (
    <div onClick={onX} style={{ position: 'fixed', inset: 0, background: 'rgba(20,18,16,.38)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}
        style={{ background: APP.bg, borderRadius: WS.radius, border: `1px solid ${APP.line}`, width: `min(${width}px, 100%)`, maxHeight: '92vh', overflow: 'auto', boxShadow: 'none', padding: '20px 22px', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 12, borderBottom: `1px solid ${APP.line}` }}>
          <span style={{ fontFamily: APP.serif, fontSize: 24, fontWeight: 500, lineHeight: 1.2, color: APP.ink, flex: 1 }}>{title}</span>
          <button type="button" onClick={onX} aria-label={t('关闭', 'Close')} style={{ display: 'inline-flex', background: 'none', border: 'none', cursor: 'pointer', color: APP.muted, padding: 2 }}><X size={18} /></button>
        </div>
        {body}
      </div>
    </div>
  );

  // 速记卡对照 — this client's card next to the candidate's, field by field,
  // with the 底线提醒 on top. 离婚原因 is left out on both sides (the backend
  // never sends the candidate's).
  const renderQcCompare = () => {
    const c = qcCompare;
    if (!c) return null;
    const theirName = nameOfProfile(c) || `#${c.id}`;
    const bh = bottomHits[c.id] || { member: [], partner: [] };
    const mine = ownQuick || {};
    const theirs = c.quick_card || {};
    const rows = QUICK_FIELDS.filter(([k]) => k !== 'divorce_reason');
    const missing = c.quick_card === undefined
      ? t('对方速记卡要等后端更新后才会显示。', 'The candidate\'s card shows once the backend is updated.')
      : !c.quick_card ? t(`${theirName} 还没有速记卡。`, `${theirName} has no quick card yet.`) : '';
    const cell = { fontSize: 12.5, lineHeight: 1.55, padding: '7px 8px', borderBottom: `1px solid ${APP.line}`, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' };
    const hitRows = [...bh.member.map(h => ['member', h]), ...bh.partner.map(h => ['partner', h])];
    return dialog(t(`速记卡对照：${name} × ${theirName}`, `Quick cards: ${name} × ${theirName}`), () => setQcCompare(null), (
      <div>
        <div style={{ marginBottom: 12 }}>
          {hitRows.length ? renderHitTable(bh, { big: true, memberName: name, theirName }) : (
            <div style={{ fontSize: 12.5, padding: '5px 8px', borderRadius: 2, background: '#E6EEE8', color: '#3F7D5A' }}>
              {t('双方的底线和择偶要求（年龄、身高、学历、月薪、婚姻、子女、房产、宗教）都没有冲突。', 'No conflict with either side\'s bottom lines or preferences.')}
            </div>
          )}
          <div style={{ fontSize: 11, color: APP.muted, marginTop: 4 }}>
            {t('红色 = 画像问卷里的「底线」；橙色 = 档案里的择偶要求。资料没填的项不比对。', 'Red = portrait bottom line; amber = profile preference. Blank facts are not compared.')}
          </div>
        </div>
        {missing && <div style={{ fontSize: 12.5, color: '#93591A', marginBottom: 8 }}>{missing}</div>}
        {!ownQuick && <div style={{ fontSize: 12.5, color: '#93591A', marginBottom: 8 }}>{t(`看不到 ${name} 的速记卡。`, `${name}'s quick card is not available.`)}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '88px minmax(0,1fr) minmax(0,1fr)', borderTop: `1px solid ${APP.line}` }}>
          <div style={{ ...cell, color: APP.muted, fontWeight: 500 }} />
          <div style={{ ...cell, fontWeight: 600 }}>{name}</div>
          <div style={{ ...cell, fontWeight: 600 }}>{theirName}</div>
          {rows.map(([k, label]) => {
            const a = String(mine[k] || '').trim();
            const b = String(theirs[k] || '').trim();
            const key = k === 'dealbreakers' || k === 'best_match_type' || k === 'criteria';
            return (
              <React.Fragment key={k}>
                <div style={{ ...cell, color: APP.muted, fontWeight: key ? 600 : 500 }}>{label}</div>
                <div style={{ ...cell, color: a ? APP.ink : '#A39E95', background: key ? APP.alt : 'transparent' }}>{a || '—'}</div>
                <div style={{ ...cell, color: b ? APP.ink : '#A39E95', background: key ? APP.alt : 'transparent' }}>{b || '—'}</div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    ), 860);
  };

  const mobile = vw <= 768;
  dialogOpenRef.current = !!(composer || editing || detailId || qcCompare || quickView);
  return (
    <div style={{ position: 'fixed', top: demo ? 34 : 0, bottom: 0, left: mobile ? 0 : frameLeft, right: 0, zIndex: demo ? 1250 : 1200, background: APP.paper, color: APP.ink, display: hiddenByTour ? 'none' : 'flex', flexDirection: 'column' }}>
      {/* top bar — one line */}
      <div data-tour="ws-top" style={{ minHeight: 64, background: APP.paper, borderBottom: `1px solid ${APP.ink}`, display: 'flex', alignItems: 'center', gap: 12, padding: mobile ? '8px 12px' : '0 20px', flexWrap: mobile ? 'wrap' : 'nowrap', boxSizing: 'border-box' }}>
        <button type="button" onClick={onClose}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', color: APP.ink, fontSize: 12.5, fontWeight: 400, letterSpacing: '.22em', padding: 0, fontFamily: 'inherit', flex: '0 0 auto' }}>
          <ArrowLeft size={16} strokeWidth={1.4} />{mobile ? '' : t('返回列表', 'Back')}
        </button>
        <Avatar photo={p.profile_photo || p.photo || (ws && ws.profile && ws.profile.photo)} initials={initials} color={avColor(profileId)} size={40} />
        <div style={{ minWidth: 0, flex: '1 1 auto', display: 'flex', alignItems: 'baseline', gap: 10, overflow: 'hidden' }}>
          <span style={{ fontFamily: APP.serif, fontSize: 26, fontWeight: 500, lineHeight: 1.1, whiteSpace: 'nowrap' }}>{name}</span>
          <span style={{ fontFamily: APP.serif, fontStyle: 'italic', fontSize: 15, color: APP.muted, whiteSpace: 'nowrap' }}>#{p.offline_id || (ws && ws.profile && ws.profile.offline_id) || profileId}</span>
          {!demo && <UrgentFlagTag profileId={profileId} isZh={isZh} />}
          {!demo && <RecStatusTag profileId={profileId} isZh={isZh} style={{ alignSelf: 'center' }} />}
          {facts && <span style={{ fontSize: 13, color: WS.sub, whiteSpace: 'nowrap' }}>{facts}</span>}
          {serviceClosed && !mobile ? (
            <span style={{ fontSize: 12, color: WS.ok, fontWeight: 400, letterSpacing: '.14em', whiteSpace: 'nowrap' }}>
              「{t(`已结单${closedDay ? ` · ${closedDay}` : ''}`, `Closed${closedDay ? ` · ${closedDay}` : ''}`)}」
            </span>
          ) : (serviceDay != null || stageName) && !mobile && (
            <span data-tour="ws-stage" style={{ fontSize: 12, color: APP.gold, letterSpacing: '.14em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              「{[serviceDay != null ? t(`签约第 ${serviceDay} 天`, `Day ${serviceDay}`) : '', stageName ? t(`当前：${stageName}`, `Now: ${stageName}`) : ''].filter(Boolean).join(' · ')}」
            </span>
          )}
        </div>
        {lt && (
          <span data-tour="ws-light" title={serviceClosed ? t('一对一满 3 个月，服务已完成', 'Service complete') : lightReasons.join('\n')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '5px 11px', borderRadius: WS.radius, background: lt.bg, color: lt.fg, fontSize: 12, fontWeight: 400, letterSpacing: '.14em', whiteSpace: 'nowrap', flex: '0 0 auto', cursor: 'help' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: lt.dot }} />{isZh ? lt.zh : lt.en}
          </span>
        )}
        {!demo && <UrgentFlagButton profileId={profileId} isZh={isZh} compact={mobile} onChanged={() => onChanged && onChanged()} />}
        <button type="button" data-tour="ws-log" onClick={() => openComposer('contact')} style={{ ...wsBtnPrimary(false), flex: '0 0 auto' }}>
          <Plus size={15} />{t('写联系记录', 'Log contact')}
        </button>
        {!demo && <TourEntryButton compact={mobile} />}
        <div ref={moreRef} style={{ position: 'relative', flex: '0 0 auto' }}>
          <button type="button" onClick={() => setMoreOpen(v => !v)} aria-label={t('更多', 'More')} title={t('更多操作', 'More actions')}
            style={{ ...wsBtnGhost, padding: '0 10px' }}>
            <MoreHorizontal size={17} />
          </button>
          {moreOpen && (
            <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 6, zIndex: 30, width: 200, background: APP.bg, border: `1px solid ${APP.ink}`, borderRadius: WS.radius, boxShadow: 'none', padding: 6 }}>
              {MORE.map(m => (
                <button key={m.key} type="button" onClick={() => { setMoreOpen(false); m.run(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '9px 10px', border: 'none', background: 'none', borderRadius: 2, cursor: 'pointer', fontSize: 13.5, color: APP.ink, textAlign: 'left', fontFamily: 'inherit' }}>
                  <m.Icon size={15} strokeWidth={1.5} color={APP.gold} />{m.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* tabs */}
      {!view && (
        <div data-tour="ws-tabs" style={{ background: APP.paper, borderBottom: `1px solid ${WS.line}`, display: 'flex', gap: mobile ? 14 : 26, padding: mobile ? '0 12px' : '0 20px', overflowX: 'auto' }}>
          {TABS.map((x, i) => {
            const on = tab === x.k;
            return (
              <button key={x.k} type="button" data-tour={`ws-tab-${x.k}`} onClick={() => { setTab(x.k); setRecord(null); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13.5, fontWeight: on ? 500 : 400, letterSpacing: '.14em', padding: '14px 0 12px', color: on ? APP.ink : WS.sub, borderBottom: `2px solid ${on ? APP.ink : 'transparent'}`, whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
                <span style={{ fontFamily: APP.serif, color: on ? APP.gold : WS.mute, marginRight: 6 }}>{'①②③④⑤'[i]}</span>{x.label}
                {x.n ? <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 400, letterSpacing: 0, color: WS.paper, background: x.red ? WS.bad : WS.gold, borderRadius: WS.radius, padding: '1px 7px' }}>{x.n}</span> : null}
              </button>
            );
          })}
        </div>
      )}

      {/* content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {/* 选择候选人 uses the full width (no max-width, no extra bottom padding) so more cards fit per row. */}
        <div style={view === 'recommend'
          ? { margin: '0 auto', padding: mobile ? '16px 12px' : '24px 24px', boxSizing: 'border-box' }
          : { maxWidth: view ? 1240 : 1000, margin: '0 auto', padding: mobile ? '16px 12px 40px' : '24px 24px 48px', boxSizing: 'border-box' }}>
          {view ? (
            <div style={{ ...wsCard, padding: '14px 16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${APP.line}`, paddingBottom: 9, marginBottom: 10 }}>
                <button type="button" onClick={() => setView(null)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 400, letterSpacing: '.18em', color: APP.ink, padding: 0, fontFamily: 'inherit' }}>
                  <ChevronLeft size={15} strokeWidth={1.4} />{t('返回', 'Back')}
                </button>
                <span style={{ fontFamily: APP.serif, fontSize: 22, fontWeight: 500, lineHeight: 1.2 }}>{actionPanelTitle}</span>
              </div>
              {renderPanel()}
            </div>
          ) : tab === 'next' ? (
            <NextStepTab isZh={isZh} actions={nextActions}
              nudge={favs ? <FavoritesNudge data={favs} isZh={isZh} onRecommend={recommendFav} /> : null} loading={loading && !svc && !ws} svc={svc} fallback={wsMissing} onRun={runNext} busyKey={nextBusy}
              closed={serviceClosed} closedAt={ws ? ws.closed_at : null}
              memo={<MemoList profileId={profileId} meId={meId} isZh={isZh} focusKey={memoFocus} initialText={demo && demoView && demoView.memo ? DEMO_MEMO_TEXT : ''}
                onChanged={() => { reloadStatus(); reloadWs(); onChanged && onChanged(); }} onOpenAll={goNotes} />} />
          ) : tab === 'steps' ? (
            (() => { const sw = ws || wsRo; return (
            <StepsTab profileId={profileId} steps={sw && Array.isArray(sw.steps) ? sw.steps : null} round={Number(sw && sw.round) || 1}
              readOnly={!ws && !!wsRo}
              viewRound={wsRound} onRound={(n) => setWsRound(n === (Number(sw && sw.round) || 1) ? null : n)}
              rounds={sw ? asArray(sw.rounds) : []} serviceClosed={serviceClosed || !!(wsRo && wsRo.service_closed)} closedAt={sw ? sw.closed_at : null}
              roundMatches={ws ? asArray(ws.round_matches) : []}
              exclusiveSince={sw ? sw.exclusive_since : null} closeDueAt={sw ? sw.close_due_at : null}
              closeDaysLeft={sw && sw.close_days_left != null ? Number(sw.close_days_left) : null}
              isZh={isZh} meId={meId} isSup={isSup} teachers={teachers} focusKey={stepFocus} memberName={name} TimeInput={TimeSelect}
              onChanged={() => { reloadWs(); onChanged && onChanged(); }} onGo={goFromStep} />
            ); })()
          ) : tab === 'records' ? (
            renderRecords()
          ) : tab === 'profile' ? (
            renderProfileTab()
          ) : tab === 'review' && isSup ? (
            <ReviewTab profileId={profileId} isZh={isZh} svc={svc} contacts={activity.filter(x => x.type === 'contact')}
              teachers={teachers} defaultCoachId={p.assigned_coach_id} rectFallback={ws ? asArray(ws.rectifications) : null} presetRect={presetRect}
              onOpenReport={(id) => setReportOpen(id)}
              onChanged={() => { reloadStatus(); reloadWs(); load(); onChanged && onChanged(); }} />
          ) : null}
        </div>
      </div>

      {/* 写联系记录 — a small focused dialog over the workspace */}
      {composer && dialog(composer === 'contact' ? t('写联系记录', 'Log a contact') : t('客户情况', 'Client note'), () => { setComposer(null); setDraft(''); }, (
        <div>
                {composer === 'contact' ? renderContactHead() : (
                  <div style={{ fontSize: 12, fontWeight: 500, color: APP.muted, marginBottom: 6 }}>{t('客户情况', 'Client note')}</div>
                )}
                <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={composer === 'contact' ? 4 : 3} autoFocus
                  aria-label={composer === 'contact' ? t('沟通内容', 'What was discussed') : t('客户情况', 'Client note')}
                  style={{ width: '100%', border: `1px solid ${APP.line}`, borderRadius: 2, padding: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
                {composer === 'contact' && renderContactTail()}
                {/* 问卷 — generate a client link and copy the invitation. */}
                {composer === 'contact' && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 8, marginTop: 9, position: 'relative' }}>
                    {qLink ? (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', width: '100%', boxSizing: 'border-box', padding: 8, border: '1px solid rgba(17,17,17,.12)', borderRadius: 2, background: '#EFEAE2' }}>
                        <span style={{ fontSize: 11.5, fontWeight: 500, color: '#111111', display: 'inline-flex', alignItems: 'center', gap: 5, width: '100%' }}>
                          <ClipboardList size={13} />{qLink.title}
                        </span>
                        <div style={{ width: '100%' }}>{shareBox(qLink.url, 'questionnaire', '#111111')}</div>
                        <button type="button" onClick={() => { setQLink(null); setQOpen(true); }}
                          style={{ fontSize: 12, fontWeight: 500, padding: '6px 9px', borderRadius: 2, border: 'none', background: 'none', color: APP.muted, cursor: 'pointer' }}>
                          {t('换一份', 'Another')}
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={openQuestionnaires} disabled={qBusy}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500, padding: '7px 12px', borderRadius: 2, border: '1px solid rgba(17,17,17,.12)', background: '#EFEAE2', color: '#111111', cursor: qBusy ? 'wait' : 'pointer' }}>
                        <ClipboardList size={13} />{qBusy ? t('生成中…', 'Creating…') : t('发送问卷', 'Questionnaire')}
                        <ChevronDown size={12} />
                      </button>
                    )}

                    {/* the picker: which questionnaire to generate for this client */}
                    {qOpen && (
                      <div style={{ position: 'relative', marginTop: -2, zIndex: 20, width: 'min(340px, 100%)', maxHeight: 240, overflow: 'auto', background: '#FFFDF9', border: `1px solid ${APP.line}`, borderRadius: 2, boxShadow: 'none' }}>
                        {qList === null ? (
                          <div style={{ padding: 14, fontSize: 12, color: APP.muted, textAlign: 'center' }}>{t('加载中…', 'Loading…')}</div>
                        ) : !qList.length ? (
                          <div style={{ padding: 14, fontSize: 12, color: APP.muted, textAlign: 'center' }}>{t('还没有可用的问卷', 'No questionnaires available')}</div>
                        ) : qList.map(q => (
                          <button key={q.id} type="button" onClick={() => generateQuestionnaire(q)} disabled={qBusy}
                            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 11px', border: 'none', borderBottom: '1px solid rgba(17,17,17,.08)', background: '#FFFDF9', cursor: qBusy ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                            <span style={{ display: 'block', fontSize: 12.5, fontWeight: 500, color: APP.ink }}>{(isZh && q.title_zh) ? q.title_zh : q.title}</span>
                            {(q.description || q.description_zh) && (
                              <span style={{ display: 'block', fontSize: 11, color: APP.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {(isZh && q.description_zh) ? q.description_zh : q.description}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {composer === 'contact' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 9, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: APP.muted }}>{t('下次联系', 'Next contact')}</span>
                    {needsNextContact(cMethod) && <span style={reqTag}>{t('必填', 'Required')}</span>}
                    <input type="datetime-local" value={nextAt} onChange={(e) => setNextAt(e.target.value)}
                      style={{ border: `1px solid ${APP.line}`, borderRadius: 2, padding: '6px 8px', fontSize: 12.5, fontFamily: 'inherit' }} />
                    <span style={ptHint}>{t('美西时间', 'PT')}</span>
                    {/* one-tap presets: this member's interval first (48 h by default) */}
                    {[...new Set([contactInterval, 72, 168])].map((h) => [h, h % 24 === 0 && h >= 72 ? t(`${h / 24}天`, `${h / 24}d`) : t(`${h}小时`, `${h}h`)]).map(([h, lbl]) => (
                      <button key={h} type="button"
                        onClick={() => setNextAt(toLocalInput(new Date(Date.now() + h * 3600000)))}
                        style={{ fontSize: 11.5, fontWeight: 500, padding: '5px 9px', borderRadius: 2, border: `1px solid ${APP.line}`, background: '#FFFDF9', color: APP.burgundy, cursor: 'pointer' }}>
                        +{lbl}
                      </button>
                    ))}
                    {nextAt && !needsNextContact(cMethod) && (
                      <button type="button" onClick={() => setNextAt('')}
                        style={{ fontSize: 11.5, fontWeight: 500, padding: '5px 9px', borderRadius: 2, border: 'none', background: 'none', color: APP.muted, cursor: 'pointer' }}>
                        {t('不设置', 'Clear')}
                      </button>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button type="button" disabled={saving} onClick={submitComposer}
                    style={{ fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 2, background: APP.burgundy, color: '#FFFDF9', border: 'none', cursor: saving ? 'wait' : 'pointer' }}>
                    {saving ? t('保存中…', 'Saving…') : t('保存', 'Save')}
                  </button>
                  <button type="button" onClick={() => { setComposer(null); setDraft(''); }}
                    style={{ fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 2, background: '#FFFDF9', color: APP.muted, border: `1px solid ${APP.line}`, cursor: 'pointer' }}>
                    {t('取消', 'Cancel')}
                  </button>
                </div>
        </div>
      ))}
      {editing && dialog(editing.type === 'contact' ? t('编辑联系记录', 'Edit contact') : editing.type === 'note' ? t('编辑客户情况', 'Edit note') : t('编辑约会', 'Edit date'), () => setEditing(null), (
        <div>
                {editing.type === 'date' ? (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input type="date" value={editing.sdate} onChange={(e) => setEditing(s => ({ ...s, sdate: e.target.value }))}
                      style={{ border: `1px solid ${APP.line}`, borderRadius: 2, padding: '7px 9px', fontSize: 13, fontFamily: 'inherit' }} />
                    <TimeSelect value={editing.stime} onChange={(v) => setEditing(s => ({ ...s, stime: v }))} isZh={isZh} placeholder={t('选择时间', 'Time')}
                      style={{ border: `1px solid ${APP.line}`, borderRadius: 2, padding: '7px 9px', fontSize: 13, fontFamily: 'inherit' }} />
                    <span style={ptHint}>{t('美西时间', 'PT')}</span>
                    <input value={editing.location} onChange={(e) => setEditing(s => ({ ...s, location: e.target.value }))}
                      placeholder={t('地点', 'Location')}
                      style={{ flex: 1, minWidth: 120, border: `1px solid ${APP.line}`, borderRadius: 2, padding: '7px 9px', fontSize: 13, fontFamily: 'inherit' }} />
                  </div>
                ) : (
                  <>
                    <textarea value={editing.text} onChange={(e) => setEditing(s => ({ ...s, text: e.target.value }))} rows={3} autoFocus
                      style={{ width: '100%', border: `1px solid ${APP.line}`, borderRadius: 2, padding: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
                    {editing.type === 'contact' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 9, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: APP.muted }}>{t('下次联系', 'Next contact')}</span>
                        <input type="datetime-local" value={editing.next_at} onChange={(e) => setEditing(s => ({ ...s, next_at: e.target.value }))}
                          style={{ border: `1px solid ${APP.line}`, borderRadius: 2, padding: '6px 8px', fontSize: 12.5, fontFamily: 'inherit' }} />
                        <span style={ptHint}>{t('美西时间', 'PT')}</span>
                      </div>
                    )}
                  </>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button type="button" disabled={saving} onClick={saveEdit}
                    style={{ fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 2, background: APP.burgundy, color: '#FFFDF9', border: 'none', cursor: saving ? 'wait' : 'pointer' }}>
                    {saving ? t('保存中…', 'Saving…') : t('保存', 'Save')}
                  </button>
                  <button type="button" onClick={() => setEditing(null)}
                    style={{ fontSize: 12, fontWeight: 500, padding: '7px 14px', borderRadius: 2, background: '#FFFDF9', color: APP.muted, border: `1px solid ${APP.line}`, cursor: 'pointer' }}>
                    {t('取消', 'Cancel')}
                  </button>
                </div>
        </div>
      ))}
      {loginOpen && (
        <GenerateLoginModal profile={profile || { id: profileId }} isZh={isZh} onClose={() => setLoginOpen(false)} />
      )}

      {/* 相亲卡 generator for a candidate — its own overlay, above this modal.
          stopPropagation so a click inside it never closes the CRM behind. */}
      {/* 月度服务报告 editor (portalled to body) */}
      {reportOpen && (
        <ServiceReportEditor
          reportId={reportOpen}
          isZh={isZh}
          memberName={name}
          onClose={() => setReportOpen(null)}
          onChanged={() => { reloadStatus(); onChanged && onChanged(); }}
          onOpenRecords={(k) => {
            setView(null); setRecord(null); setComposer(null); setTab('records');
            setRecFilter({ tasks: 'contact', matches: 'match', meets: 'meet', notes: 'notes' }[k] || 'all');
          }}
        />
      )}
      {renderQuickView()}
      {renderQcCompare()}
      {detailId && (
        <div onClick={(e) => e.stopPropagation()}>
          {/* Contact info stays admin-only, as on /profiles. */}
          <RichProfileDetailModal profileId={detailId} onClose={() => setDetailId(null)} contactAdminOnly={true} />
        </div>
      )}
      {complaintsOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <ComplaintsModal
            profileId={profileId}
            profileName={name}
            isZh={isZh}
            profile={profile}
            onClose={() => { setComplaintsOpen(false); reloadStatus(); }}
            onChanged={() => { reloadStatus(); onChanged && onChanged(); }}
            onUpdated={() => { reloadStatus(); onChanged && onChanged(); }}
          />
        </div>
      )}
      {/* 测评结果 viewer — the same modal the KPIv2 ticket timeline opens. */}
      {quizOpen && (
        <div onClick={(e) => e.stopPropagation()}>
          <QuestionnaireResultsModal
            submissionId={quizOpen}
            profileId={profileId}
            isZh={isZh}
            onClose={() => setQuizOpen(null)}
          />
        </div>
      )}
      {cardProfile && (() => {
        // 身高/婚姻/子女/职业/族裔/月收入/房产 on the card all come from the
        // customFields prop — passing [] left every one of those rows blank.
        // /profiles/:id already returns them, so shape them the same way the
        // 档案详情「生成卡片」button does.
        const cfArray = (cardProfile.customFields || []).map(cf => ({
          field_name: cf.field_name || cf.name || '',
          field_label: cf.field_label || cf.label || '',
          field_label_zh: cf.field_label_zh || cf.label_zh || '',
          field_type: cf.field_type || '',
          field_options: cf.field_options || null,
          field_value: cf.field_value !== undefined
            ? cf.field_value
            : ((cardProfile.custom_values && cardProfile.custom_values[cf.custom_field_id || cf.id]) || ''),
        })).filter(f => f.field_value !== '' && f.field_value !== null && f.field_value !== undefined);
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <MatchmakingCard
              profile={cardProfile}
              customFields={cfArray}
              preferences={cfArray.filter(f => f.field_name.startsWith('mate_'))}
              language={isZh ? 'zh' : 'en'}
              onClose={() => setCardProfile(null)}
            />
          </div>
        );
      })()}
    </div>
  );
}
